import React, { useState, useEffect, useCallback, useRef } from 'react';
import io from 'socket.io-client';
import '../styles/DashboardTheme.css';
import { useNavigate } from 'react-router-dom';
import { useLanguage, SUPPORTED_LANGUAGES } from '../contexts/LanguageContext';
import SubscriptionModal from '../components/SubscriptionModal';
import PlanRenewalModal from '../components/PlanRenewalModal';
import BuyBranchesModal from '../components/BuyBranchesModal';
import { useAuth } from '../contexts/AuthContext';
import BranchesTab from '../components/BranchesTab';
import ActivityLogTab from '../components/ActivityLogTab';
import RedeemSettingsTab from '../components/RedeemSettingsTab';
import { useBranch } from '../contexts/BranchContext';
import { useCurrency } from '../contexts/CurrencyContext';
import * as XLSX from 'xlsx';
import { usePlanFeatures } from '../hooks/usePlanFeatures';
import { syncStoreWithServer, getAllFromStore, getQueue, removeFromQueue } from '../utils/localDb';
import { getLocalizedText } from '../utils/localizationHelper';
import { getHighAccuracyPosition, isGeolocationAvailable, formatAccuracy } from '../utils/geolocation';
// import UpgradePrompt from '../components/UpgradePrompt';

/*spaces*/
function AdminPage() {
  const { t, language } = useLanguage();
  const { token, logout, currentUser: authUser, isLoading } = useAuth();
  const { branches } = useBranch();
  const [selectedBranch, setSelectedBranchState] = useState(() => {
    const saved = localStorage.getItem('selectedBranch_admin');
    return saved ? (saved === 'null' ? null : parseInt(saved, 10)) : null;
  });
  const setSelectedBranch = (val) => {
    setSelectedBranchState(val);
    localStorage.setItem('selectedBranch_admin', val === null ? 'null' : val.toString());
  };
  // Auto-select the only branch when there's exactly one
  useEffect(() => {
    if (branches && branches.length === 1 && !selectedBranch) {
      setSelectedBranch(branches[0].id);
    }
  }, [branches]); // eslint-disable-line react-hooks/exhaustive-deps
  const { currency, refreshCurrency, getCurrencySymbol, formatAmount, currencyMeta } = useCurrency();
  const navigate = useNavigate();

  const tabPermissions = React.useMemo(() => ({
    'menu': 'menu',
    'tables': 'tables',
    'groups': 'tables',
    'staff': 'staff',
    'users': 'users',
    'roles': 'roles',
    'branches': 'branches',
    'settings': 'settings'
  }), []);

  const hasTabPermission = useCallback((tabName) => {
    if (!authUser) return false;
    const roleStr = (authUser.role || '').toLowerCase();
    // System-level admins get full access to everything
    if (roleStr === 'admin' || roleStr === 'owner' || roleStr === 'superadmin') return true;

    // Company creator / super admin: if no permissions object is set and not a restricted role
    if (!authUser.permissions && roleStr !== 'customer' && roleStr !== 'management') return true;

    let perms = authUser.permissions;
    if (typeof perms === 'string') {
      try { perms = JSON.parse(perms); } catch (e) { perms = {}; }
    }
    if (!perms) return false;

    // 'admin' permission only grants access to the menu tab (Admin Dashboard = menu management)
    // Other tabs require their specific permissions
    if (perms.admin === true && tabName === 'menu') return true;

    const reqPerm = tabPermissions[tabName];
    return !!perms[reqPerm];
  }, [authUser, tabPermissions]);

  // ── State ───────────────────────────────────────
  const [activeTab, setActiveTab] = useState('menu');
  const [companyProfile, setCompanyProfile] = useState({
    name: '',
    logo_url: '',
    banner_url: '',
    twitter_url: '',
    youtube_url: '',
    instagram_url: '',
    qr_codes: [],
    upi_id: ''
  });

  // Plan Features Hook - Must come after companyProfile state is defined
  const planFeatures = usePlanFeatures(companyProfile);

  const [menuItems, setMenuItems] = useState([]);
  const [tables, setTables] = useState([]);
  const [tableGroups, setTableGroups] = useState([]);
  const [categories, setCategories] = useState([]);
  const [orders, setOrders] = useState([]);
  const [selectedGroupFilter, setSelectedGroupFilter] = useState('all');
  const [tablePerspective, setTablePerspective] = useState('tables'); // 'tables' or 'waiting'
  const [isSubscriptionOpen, setIsSubscriptionOpen] = useState(false);
  const [isRenewalModalOpen, setIsRenewalModalOpen] = useState(false);
  const [isBuyBranchesOpen, setIsBuyBranchesOpen] = useState(false);
  const [renewalModalOptions, setRenewalModalOptions] = useState({});

  // Smart upgrade opener: paid Starter users see proration modal; others see standard SubscriptionModal
  const openUpgradeModal = useCallback((options = {}) => {
    setConfirmModal({
      show: true,
      title: 'Upgrade Required',
      message: 'This feature is not available in the Starter plan. You need to upgrade the plan to access this feature. Do you want to upgrade the plan?',
      confirmText: 'Upgrade Plan',
      confirmClass: 'btn btn-primary flex-1 py-2 bg-indigo-600 hover:bg-indigo-700 text-white font-bold rounded-lg',
      cancelClass: 'btn btn-secondary flex-1 py-2 bg-gray-100 hover:bg-gray-200 text-gray-700 font-bold rounded-lg',
      onConfirm: () => {
        setConfirmModal({ show: false, message: '', onConfirm: () => {} });
        setRenewalModalOptions(options);
        setIsRenewalModalOpen(true);
      }
    });
  }, []);
  const [isProfileLoading, setIsProfileLoading] = useState(false);
  const [isCompanyLocating, setIsCompanyLocating] = useState(false);
  const [staffList, setStaffList] = useState([]);
  // const [leavesList, setLeavesList] = useState([]);
  // const [isStaffLoading, setIsStaffLoading] = useState(false);
  const [visiblePins, setVisiblePins] = useState({});
  const [rolesList, setRolesList] = useState([]);
  const [usersList, setUsersList] = useState([]);
  const [isRolesLoading, setIsRolesLoading] = useState(false);
  const [isUsersLoading, setIsUsersLoading] = useState(false);
  const [isRoleModalOpen, setIsRoleModalOpen] = useState(false);
  const [isUserModalOpen, setIsUserModalOpen] = useState(false);
  const [currentRole, setCurrentRole] = useState({ name: '', permissions: {} });
  const [selectedMenuItemIds, setSelectedMenuItemIds] = useState([]);
  const [isUploading, setIsUploading] = useState(false);
  const [currentUser, setCurrentUser] = useState({ full_name: '', email: '', phone: '', password: '', role_id: '' });

  const isRoleInBranch = (role, branchId) => {
    if (!branchId) return true; // all branches
    
    const roleName = (role.name || '').toLowerCase();
    if (roleName === 'admin' || roleName === 'owner' || roleName === 'superadmin') {
      return true; // system admin roles match all branches
    }
    
    let allowed = role.allowed_branches;
    if (typeof allowed === 'string') {
      try { allowed = JSON.parse(allowed); } catch (e) { allowed = []; }
    }
    
    // If no branches are explicitly selected or allowed is not an array,
    // treat the role as global (accessible from all branches)
    if (!allowed || !Array.isArray(allowed) || allowed.length === 0) {
      return true;
    }
    
    return allowed.some(id => String(id) === String(branchId));
  };

  const isUserInBranch = (user, branchId) => {
    if (!branchId) return true; // all branches
    
    const userRoleStr = (user.role || '').toLowerCase();
    if (userRoleStr === 'admin' || userRoleStr === 'owner' || userRoleStr === 'superadmin') {
      return true; // system admin users match all branches
    }
    
    const userRole = rolesList.find(r => r.id === user.role_id || String(r.id) === String(user.role_id));
    if (!userRole) {
      // If user has a default system role or no custom role is found,
      // treat them as global (visible)
      return true;
    }
    
    return isRoleInBranch(userRole, branchId);
  };

  // Branch Management State
  // const [branchesList, setBranchesList] = useState([]);
  // const [isBranchesLoading, setIsBranchesLoading] = useState(false);
  // const [isBranchModalOpen, setIsBranchModalOpen] = useState(false);
  // const [currentBranch, setCurrentBranch] = useState({
  //   name: '',
  //   address: '',
  //   phone: '',
  //   manager_name: '',
  //   is_active: true
  // });

  const AVAILABLE_PERMISSIONS = React.useMemo(() => [
    { id: 'admin', label: t('adminDashboard') },
    { id: 'kitchen', label: t('kitchenDisplay') },
    { id: 'tables', label: t('tables') },
    { id: 'staff', label: t('staff') },
    { id: 'branches', label: t('branches') },
    { id: 'settings', label: t('settings') },
    { id: 'users', label: t('users') },
    { id: 'roles', label: t('roles') },
    { id: 'analytics', label: t('analytics') },
    { id: 'customer', label: t('customerPage') },
    { id: 'ingredients', label: t('ingredients') }
  ], [t]);

  // Modals
  const [isMenuModalOpen, setIsMenuModalOpen] = useState(false);
  const [isSubmittingMenu, setIsSubmittingMenu] = useState(false);
  const [isTableModalOpen, setIsTableModalOpen] = useState(false);
  const [isGroupModalOpen, setIsGroupModalOpen] = useState(false);
  const [isQRModalOpen, setIsQRModalOpen] = useState(false);
  const [isStaffQRModalOpen, setIsStaffQRModalOpen] = useState(false);
  const [isWorkingHoursModalOpen, setIsWorkingHoursModalOpen] = useState(false);
  const [isSavingWorkingHours, setIsSavingWorkingHours] = useState(false);
  const [workingHoursSettings, setWorkingHoursSettings] = useState({
    salary_calculation_type: 'daily',
    deduct_absent_from_monthly: true,
    holidays: [],
    late_minutes: 15,
    half_day_minutes: 240,
    full_day_hours: 8,
    lates_for_half_day: 3,
    schedule: {
      Mon: { enabled: true, start: '09:00', end: '18:00' },
      Tue: { enabled: true, start: '09:00', end: '18:00' },
      Wed: { enabled: true, start: '09:00', end: '18:00' },
      Thu: { enabled: true, start: '09:00', end: '18:00' },
      Fri: { enabled: true, start: '09:00', end: '18:00' },
      Sat: { enabled: false, start: '09:00', end: '14:00' },
      Sun: { enabled: false, start: '09:00', end: '14:00' },
    }
  });
  const [isProfileModalOpen, setIsProfileModalOpen] = useState(false);
  const [isStaffModalOpen, setIsStaffModalOpen] = useState(false);
  const [showOrdersModal, setShowOrdersModal] = useState(false);
  const [selectedTableForQR, setSelectedTableForQR] = useState(null);
  const [selectedTableNum, setSelectedTableNum] = useState(null);
  const [selectedTableOrders, setSelectedTableOrders] = useState([]);
  const [confirmModal, setConfirmModal] = useState({
    show: false,
    message: '',
    onConfirm: () => { },
  });

  // Category inline
  const [showNewCategoryInput, setShowNewCategoryInput] = useState(false);
  const [newCategoryName, setNewCategoryName] = useState('');
  const [isSelfServiceNewCategory, setIsSelfServiceNewCategory] = useState(false);
  const [isManageCategoriesModalOpen, setIsManageCategoriesModalOpen] = useState(false);

  // Form data
  const [currentMenuItem, setCurrentMenuItem] = useState({});
  const [currentTable, setCurrentTable] = useState({ group_id: null });
  const [currentGroup, setCurrentGroup] = useState({ name: '' });
  const [menuImagePreview, setMenuImagePreview] = useState(null);
  const [menuImageFile, setMenuImageFile] = useState(null);

  // Payment Security
  const [isPaymentSettingsUnlocked, setIsPaymentSettingsUnlocked] = useState(false);
  const [showUnlockModal, setShowUnlockModal] = useState(false);
  const [unlockPassword, setUnlockPassword] = useState('');
  const [isUnlocking, setIsUnlocking] = useState(false);
  const [showUpiId, setShowUpiId] = useState(false);
  const [showQrCodes, setShowQrCodes] = useState(false);

  // ── Global Refresh Function ─────────────────────

  // Loading & Filters
  const [isMenuLoading, setIsMenuLoading] = useState(false);
  const [isTablesLoading, setIsTablesLoading] = useState(false);
  const [isTableSubmitting, setIsTableSubmitting] = useState(false);

  const [searchTerm, setSearchTerm] = useState('');
  const [selectedCategory, setSelectedCategory] = useState('');

  // Toast
  const [toast, setToast] = useState({ show: false, message: '', type: 'success' });


  // const [attendanceLogs, setAttendanceLogs] = useState([]);
  const [attendanceDateFilter /*, setAttendanceDateFilter */] = useState('thisMonth');
  const [customStart /*, setCustomStart */] = useState('');
  const [customEnd /*, setCustomEnd */] = useState('');
  const [showNewRoleInput, setShowNewRoleInput] = useState(false);
  const [editingStaff, setEditingStaff] = useState(null);
  const [staffData, setStaffData] = useState({ name: '', role: '', phone: '', pin: '', branch_id: '', salary: 0, daily_rate: 0 });
  const [showLanguageDropdown, setShowLanguageDropdown] = useState(false);
  const [showBranchDropdown, setShowBranchDropdown] = useState(false);
  const [isWaitingQRModalOpen, setIsWaitingQRModalOpen] = useState(false); // New state
  const [isDeliveryQRModalOpen, setIsDeliveryQRModalOpen] = useState(false);
  const [qrBranchId, setQrBranchId] = useState(null);

  // Category inline edit state
  const [editingCategory, setEditingCategory] = useState(null);
  const [editingCategoryName, setEditingCategoryName] = useState('');

  // Refs
  const qrRefs = useRef({});
  const BASE_URL = (() => {
    const origin = window.location.origin;
    const pathname = window.location.pathname; // e.g. "/RedSorm/admin.html"
    const lastSlashIdx = pathname.lastIndexOf('/');
    const dir = lastSlashIdx > 0 ? pathname.substring(0, lastSlashIdx) : '';
    return origin + dir;
  })();
  // Use environment variable for API URL with fallback
  const API_URL = window.location.hostname === 'localhost' || window.location.hostname.endsWith('.localhost') || window.location.hostname === '127.0.0.1'
    ? `http://${window.location.hostname}:5000`
    : (process.env.REACT_APP_API_URL || 'https://dineflowbackend.onrender.com');

  const loadLeaves = useCallback(async () => {
    try {
      const res = await fetch(`${API_URL}/api/staff/leaves`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      // const json = await res.json();
      await res.json();
      // if (json.success) setLeavesList(json.data);
    } catch (err) {
      console.error(err);
    }
  }, [API_URL, token]);

  const loadAttendanceLogs = useCallback(async () => {
    try {
      let url = `${API_URL}/api/staff/attendance`;

      const now = new Date();
      let start = new Date();
      let end = new Date();

      if (attendanceDateFilter === 'today') {
        // start and end are already now
      } else if (attendanceDateFilter === 'yesterday') {
        start.setDate(now.getDate() - 1);
        end.setDate(now.getDate() - 1);
      } else if (attendanceDateFilter === 'last7') {
        start.setDate(now.getDate() - 7);
      } else if (attendanceDateFilter === 'thisMonth') {
        start = new Date(now.getFullYear(), now.getMonth(), 1);
      } else if (attendanceDateFilter === 'custom' && customStart && customEnd) {
        start = new Date(customStart);
        end = new Date(customEnd);
      } else if (attendanceDateFilter === 'custom') {
        // If incomplete custom range, don't fetch or fetch default (e.g. today)
        return;
      }

      const formatDate = (d) => {
        const year = d.getFullYear();
        const month = String(d.getMonth() + 1).padStart(2, '0');
        const day = String(d.getDate()).padStart(2, '0');
        return `${year}-${month}-${day}`;
      };
      url += `?startDate=${formatDate(start)}&endDate=${formatDate(end)}`;

      if (selectedBranch) {
        url += `&branch_id=${selectedBranch}`;
      }

      const res = await fetch(url, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      const json = await res.json();
      if (json.success) {
        // console.log('Attendance loaded:', json.data);
        // setAttendanceLogs(json.data || []);
      } else {
        console.warn('Attendance load failed:', json.message);
      }
    } catch (err) {
      console.error(err);
    }
  }, [API_URL, token, attendanceDateFilter, customStart, customEnd, selectedBranch]);

  // Redirect if not authenticated
  useEffect(() => {
    if (!isLoading && !authUser) {
      logout();
    }
  }, [isLoading, authUser, logout]);

  // Click outside to close dropdowns
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (!event.target.closest('.language-dropdown-container') && !event.target.closest('.branch-dropdown-container')) {
        setShowLanguageDropdown(false);
        setShowBranchDropdown(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  // Refs


  // Set initial active tab based on permissions - only run once on mount
  const hasInitializedTab = useRef(false);

  useEffect(() => {
    if (authUser && !hasInitializedTab.current) {
      hasInitializedTab.current = true;
      const allowed = ['menu', 'tables', 'groups', 'staff', 'users', 'roles', 'branches', 'settings'].filter(hasTabPermission);
      if (allowed.length > 0) {
        setActiveTab(allowed[0]);
      }
    }
  }, [authUser, hasTabPermission]);

  // Redirect away from tables/groups if company 36
  useEffect(() => {
    if (companyProfile && companyProfile.id === 36) {
      if (activeTab === 'tables' || activeTab === 'groups') {
        setActiveTab('qr_codes');
      }
    }
  }, [companyProfile, activeTab]);

  // ── Helpers ─────────────────────────────────────
  const toastTimeoutRef = useRef(null);

  const showToast = useCallback((msg, type = 'success') => {
    if (toastTimeoutRef.current) {
      clearTimeout(toastTimeoutRef.current);
    }
    setToast({ show: true, message: msg, type });
    const duration = type === 'error' ? 10000 : 3000;
    toastTimeoutRef.current = setTimeout(() => {
      setToast({ show: false, message: '', type: 'success' });
      toastTimeoutRef.current = null;
    }, duration);
  }, []);

  const openConfirm = useCallback((message, onYes) => {
    setConfirmModal({ show: true, message, onConfirm: onYes });
  }, []);

  const getTableCounts = useCallback((tableNumber, branchId) => {
    const isSameDay = (d1, d2) => (
      d1.getFullYear() === d2.getFullYear() &&
      d1.getMonth() === d2.getMonth() &&
      d1.getDate() === d2.getDate()
    );

    const today = new Date();

    const tableOrders = orders.filter(o => {
      const matchTable = String(o.table_number) === String(tableNumber);
      const oBranch = o.branch_id ? String(o.branch_id) : '';
      const tBranch = branchId ? String(branchId) : '';
      const matchBranch = !o.branch_id || !branchId || oBranch === tBranch;
      if (!matchTable || o.is_waiting || !matchBranch) return false;

      const ts = o.created_at || o.updated_at || o.session_checkout_at;
      const d = ts ? new Date(ts) : new Date(0);
      return isSameDay(d, today);
    });

    return {
      pending: tableOrders.filter(o => o.order_status === 'pending').length,
      preparing: tableOrders.filter(o => o.order_status === 'preparing').length,
      ready: tableOrders.filter(o => o.order_status === 'ready').length,
      delivered: tableOrders.filter(o => o.order_status === 'delivered').length,
    };
  }, [orders]);

  const toggleMenuItemAvailability = async (id, currentStatus) => {
    try {
      const res = await fetch(`${API_URL}/api/menu/${id}/availability`, {
        method: 'PATCH',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify({ is_available: !currentStatus })
      });
      const json = await res.json();
      if (json.success) {
        setMenuItems(prev => prev.map(item => item.id === id ? { ...item, is_available: !currentStatus } : item));
        showToast(t('itemUpdated'));
      } else {
        showToast(json.message || t('actionFailed'), 'error');
      }
    } catch (err) {
      showToast(t('actionFailed'), 'error');
    }
  };

  const toggleTableAvailability = async (id, currentIsAvailable) => {
    try {
      const newStatus = !currentIsAvailable;
      const res = await fetch(`${API_URL}/api/tables/${id}/availability`, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify({ is_available: newStatus })
      });
      if (res.status === 401 || res.status === 403) {
        showToast(t('sessionExpired'), 'error');
        return;
      }
      const json = await res.json();
      if (json.success) {
        setTables(prev => prev.map(t => t.id === id ? { ...t, is_available: newStatus } : t));
        showToast('Table status updated!', 'success');
      } else {
        showToast(json.message || 'Failed to update table status', 'error');
      }
    } catch (err) {
      showToast('Network error', 'error');
    }
  };

  const handleRefresh = () => {
    if (!token) return;
    showToast(t('refreshing') || 'Refreshing...', 'info');

    switch (activeTab) {
      case 'menu':
        loadMenu();
        loadCategories();
        break;
      case 'tables':
        loadTables();
        loadOrders();
        loadTableGroups();
        break;
      case 'groups':
        loadTableGroups();
        break;
      case 'staff':
        loadStaff();
        loadLeaves();
        loadAttendanceLogs();
        break;
      case 'users':
        loadUsers();
        loadRoles();
        break;
      case 'roles':
        loadRoles();
        break;
      case 'branches':
        // Trigger refresh in BranchesTab by forcing a re-render
        // We'll need to pass a refresh trigger
        window.dispatchEvent(new CustomEvent('refreshBranches'));
        break;
      case 'activity_log':
        window.dispatchEvent(new CustomEvent('refreshActivityLog'));
        break;
      case 'settings':
        loadCompanyProfile();
        break;
      case 'orders':
        loadOrders();
        break;
      case 'orders_today':
        loadOrders();
        break;
      default:
        break;
    }

    // Show success toast after a short delay
    setTimeout(() => {
      showToast(t('refreshed') || 'Refreshed!', 'success');
    }, 500);
  };




  // ── Load QRCode lib ─────────────────────────────
  useEffect(() => {
    const script = document.createElement('script');
    script.src = 'https://cdn.jsdelivr.net/npm/qrcodejs@1.0.0/qrcode.min.js';
    script.async = true;
    document.body.appendChild(script);
    return () => {
      if (document.body.contains(script)) document.body.removeChild(script);
    };
  }, []);

  // ── Load data ───────────────────────────────────
  useEffect(() => {
    if (token) {
      loadMenu();
      loadTables();
      loadCategories();
      loadTableGroups();
      loadCompanyProfile();
      // Pre-warm cache for offline support
      loadStaff();
      loadRoles();
      loadUsers();
      loadOrders();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [token]); // Only token - callbacks are stable via useCallback

  // ── Reload data when branch changes ─────────────
  useEffect(() => {
    if (token && selectedBranch !== undefined) {
      loadMenu();
      loadTables();
      loadTableGroups();
      loadOrders();
      loadStaff();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedBranch, token]); // Callbacks are stable via useCallback

  useEffect(() => {
    if (activeTab === 'tables' && token) loadOrders();
    if (activeTab === 'settings' && token) loadCompanyProfile();
    if (activeTab === 'staff' && token) {
      loadStaff();
      loadLeaves();
      loadAttendanceLogs();
    }
    if (activeTab === 'roles' && token) {
      loadRoles();
    }
    if (activeTab === 'users' && token) {
      loadUsers();
      loadRoles(); // Need roles for dropdown
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeTab, token]); // Functions are stable via useCallback

  // ── Listen for branch updates from BranchesTab ─────────────
  useEffect(() => {
    const handleBranchRefresh = () => {
      // Force a re-render by triggering data reload
      if (token) {
        loadMenu();
        loadTables();
        loadTableGroups();
        loadStaff();
      }
    };

    const handleOpenRenewalModal = (e) => {
      setRenewalModalOptions(e.detail || {});
      setIsRenewalModalOpen(true);
    };

    const handleOpenBuyBranchesModal = () => {
      setIsBuyBranchesOpen(true);
    };

    const handleOpenUpgrade = (e) => openUpgradeModal(e.detail || {});
    window.addEventListener('refreshBranches', handleBranchRefresh);
    window.addEventListener('openRenewalModal', handleOpenRenewalModal);
    window.addEventListener('openBuyBranchesModal', handleOpenBuyBranchesModal);
    window.addEventListener('openUpgradeModal', handleOpenUpgrade);

    return () => {
      window.removeEventListener('refreshBranches', handleBranchRefresh);
      window.removeEventListener('openRenewalModal', handleOpenRenewalModal);
      window.removeEventListener('openBuyBranchesModal', handleOpenBuyBranchesModal);
      window.removeEventListener('openUpgradeModal', handleOpenUpgrade);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [token]);

  // ── Real-time Socket listener for orders ─────────
  useEffect(() => {
    if (!token) return;

    const socket = io(API_URL, {
      transports: ['websocket', 'polling'],
      reconnection: true,
      reconnectionAttempts: 10,
      reconnectionDelay: 3000,
      reconnectionDelayMax: 15000,
      timeout: 10000,
      auth: { token }
    });

    socket.on('new-order', (order) => {
      setOrders(prev => {
        // Avoid duplicates
        if (prev.some(o => o.id === order.id)) return prev;
        return [order, ...prev];
      });
    });

    const handleOrderUpdate = (updatedOrder) => {
      setOrders(prev =>
        prev.map(o => o.id === updatedOrder.id ? { ...o, ...updatedOrder } : o)
      );
    };

    socket.on('order-updated', handleOrderUpdate);
    socket.on('order-status-updated', handleOrderUpdate);

    return () => {
      socket.disconnect();
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [token, API_URL]);


  // ── API Calls ───────────────────────────────────
  const loadMenu = useCallback(async () => {
    setIsMenuLoading(true);
    try {
      let url = `${API_URL}/api/menu`;
      if (selectedBranch) url += `?branch_id=${selectedBranch}`;

      console.log('Loading menu from:', url);
      const res = await fetch(url, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      if (res.status === 401 || res.status === 403) {
        logout();
        return;
      }
      const json = await res.json();
      console.log('Menu API response:', json);
      if (json.success) {
        console.log('Raw menu items from API:', json.data?.length || 0);
        // Filter out placeholder items and 'add-new' categories
        const validItems = (json.data || []).filter(item =>
          item.name !== '[Category Placeholder]' &&
          item.category !== 'add-new'
        );
        console.log('Valid menu items after filtering:', validItems.length);

        // Sort items alphabetically by name
        const sortedItems = validItems.sort((a, b) => (a.name || '').localeCompare(b.name || ''));
        setMenuItems(sortedItems);
        // Sync to local IndexedDB
        syncStoreWithServer('menu', sortedItems).catch(err => console.error('[IndexedDB] Sync menu failed:', err));
      }
    } catch (err) {
      console.error('Menu load error:', err);
      // OFFLINE FALLBACK
      try {
        const cachedMenu = await getAllFromStore('menu');
        if (cachedMenu && cachedMenu.length > 0) {
          setMenuItems(cachedMenu);
          showToast(t('loadedMenuOffline') || 'Loaded menu from offline cache', 'info');
        } else {
          showToast(t('failedLoadMenu') || 'Failed to load menu', 'error');
        }
      } catch (dbErr) {
        showToast(t('failedLoadMenu') || 'Failed to load menu', 'error');
      }
    } finally {
      setIsMenuLoading(false);
    }
  }, [API_URL, token, logout, selectedBranch, showToast, t]);

  const loadTables = useCallback(async () => {
    setIsTablesLoading(true);
    try {
      let url = `${API_URL}/api/tables`;
      if (selectedBranch) url += `?branch_id=${selectedBranch}`;

      const res = await fetch(url, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      if (res.status === 401 || res.status === 403) {
        logout();
        return;
      }
      const json = await res.json();
      if (res.ok) {
        if (json.success) {
          setTables(json.data || []);
          // Sync to local IndexedDB
          syncStoreWithServer('tables', json.data || []).catch(err => console.error('[IndexedDB] Sync tables failed:', err));
        } else {
          showToast(json.message || t('serverError'), 'error');
        }
      } else {
        showToast(`${t('serverError')}: ${res.status}`, 'error');
      }
    } catch (err) {
      console.error('Tables load error:', err);
      // OFFLINE FALLBACK
      try {
        const cachedTables = await getAllFromStore('tables');
        if (cachedTables && cachedTables.length > 0) {
          setTables(cachedTables);
          showToast(t('loadedTablesOffline') || 'Loaded tables from offline cache', 'info');
        } else {
          showToast(t('networkError'), 'error');
        }
      } catch (dbErr) {
        showToast(t('networkError'), 'error');
      }
    } finally {
      setIsTablesLoading(false);
    }
  }, [API_URL, token, logout, selectedBranch, showToast, t]);

  const loadCategories = useCallback(async () => {
    try {
      const res = await fetch(`${API_URL}/api/categories`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      if (res.status === 401 || res.status === 403) {
        logout();
        return;
      }
      const json = await res.json();
      if (json.success && Array.isArray(json.data)) {
        const validCats = json.data.filter(c => c && (c.name || c).trim() !== '');
        setCategories(validCats);
        // Sync to local IndexedDB
        syncStoreWithServer('categories', validCats).catch(err => console.error('[IndexedDB] Sync categories failed:', err));
      }
    } catch (err) {
      console.error('Categories load error:', err);
      // OFFLINE FALLBACK
      try {
        const cachedCats = await getAllFromStore('categories');
        if (cachedCats && cachedCats.length > 0) {
          setCategories(cachedCats);
        } else {
          setCategories([
            { id: 1, name: 'Appetizer', is_self_service: false },
            { id: 2, name: 'Main Course', is_self_service: false },
            { id: 3, name: 'Dessert', is_self_service: false },
            { id: 4, name: 'Beverage', is_self_service: false }
          ]);
        }
      } catch (dbErr) {
        setCategories([
          { id: 1, name: 'Appetizer', is_self_service: false },
          { id: 2, name: 'Main Course', is_self_service: false },
          { id: 3, name: 'Dessert', is_self_service: false },
          { id: 4, name: 'Beverage', is_self_service: false }
        ]);
      }
    }
  }, [API_URL, token, logout]);

  const loadOrders = useCallback(async () => {
    try {
      let url = `${API_URL}/api/orders`;
      if (selectedBranch) url += `?branch_id=${selectedBranch}`;

      const res = await fetch(url, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      if (res.status === 401 || res.status === 403) {
        logout();
        return;
      }
      const json = await res.json();
      if (json.success) {
        const fetchedOrders = json.data || [];

        // Always merge any pending offline-queued orders so they remain visible
        // until they are successfully synced to the server
        try {
          const queuedOrders = await getQueue();
          if (queuedOrders && queuedOrders.length > 0) {
            queuedOrders.forEach(qOrder => {
              const alreadySynced = fetchedOrders.some(
                o => String(o.id) === String(qOrder.id) || String(o.id) === String(qOrder.order_id)
              );
              if (!alreadySynced) {
                fetchedOrders.push({
                  ...qOrder,
                  order_status: qOrder.order_status || 'pending',
                  items: (qOrder.items || []).map(item => ({
                    ...item,
                    item_name: item.item_name || item.name || '',
                    item_status: item.item_status || 'pending'
                  }))
                });
              }
            });
          }
        } catch (qErr) {
          console.warn('[Admin] Could not merge offline queue with server orders:', qErr);
        }

        const sortedOrders = fetchedOrders.sort((a, b) => {
          if (a.is_waiting !== b.is_waiting) return a.is_waiting ? 1 : -1;
          return new Date(a.created_at) - new Date(b.created_at);
        });
        setOrders(sortedOrders);
        // Cache only true server orders (exclude offline-queued ones)
        syncStoreWithServer('orders', (json.data || []).filter(o => !o.is_offline)).catch(err => console.error('[IndexedDB] Sync orders failed:', err));
      }
    } catch (err) {
      console.error('Admin load orders error:', err);
      // OFFLINE FALLBACK - Load both cached online orders and queued offline orders
      try {
        const cachedOrders = await getAllFromStore('orders');
        const queuedOrders = await getAllFromStore('orders_queue');

        // Merge them, prioritizing queued orders
        const mergedOrders = [...queuedOrders.map(qOrder => ({
          ...qOrder,
          order_status: qOrder.order_status || 'pending',
          items: (qOrder.items || []).map(item => ({
            ...item,
            item_name: item.item_name || item.name || '',
            item_status: item.item_status || 'pending'
          }))
        }))];
        cachedOrders.forEach(co => {
          if (!mergedOrders.some(qo => qo.id === co.id || qo.order_id === co.id)) {
            mergedOrders.push(co);
          }
        });

        const filteredOrders = selectedBranch
          ? mergedOrders.filter(o => String(o.branch_id) === String(selectedBranch) || o.branch_id === null || o.branch_id === undefined)
          : mergedOrders;

        const sortedOrders = filteredOrders.sort((a, b) => {
          if (a.is_waiting !== b.is_waiting) {
            return a.is_waiting ? 1 : -1;
          }
          return new Date(a.created_at) - new Date(b.created_at);
        });

        setOrders(sortedOrders);
      } catch (dbErr) {
        console.error('[IndexedDB] Load orders failed offline in Admin:', dbErr);
        showToast(t('serverError'), 'error');
      }
    }
  }, [API_URL, token, logout, selectedBranch, showToast, t]);

  const loadTableGroups = useCallback(async () => {
    try {
      let url = `${API_URL}/api/table-groups`;
      if (selectedBranch) url += `?branch_id=${selectedBranch}`;

      const res = await fetch(url, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      if (res.status === 401 || res.status === 403) {
        logout();
        return;
      }
      const json = await res.json();
      if (json.success) {
        const uniqueGroups = json.data.filter((g, index, self) => index === self.findIndex(g2 => g2.id === g.id));
        setTableGroups(uniqueGroups || []);
        localStorage.setItem('redsorm_table_groups', JSON.stringify(uniqueGroups || []));
      } else {
        showToast(json.message || t('serverError'), 'error');
      }
    } catch (err) {
      console.error('Table groups load error:', err);
      const cached = localStorage.getItem('redsorm_table_groups');
      if (cached) {
        const parsed = JSON.parse(cached);
        const filtered = selectedBranch
          ? parsed.filter(g => String(g.branch_id) === String(selectedBranch))
          : parsed;
        setTableGroups(filtered);
      } else {
        showToast(t('serverError'), 'error');
        setTableGroups([]);
      }
    }
  }, [API_URL, token, logout, selectedBranch, showToast, t]);

  const loadCompanyProfile = useCallback(async () => {
    setIsProfileLoading(true);
    try {
      const res = await fetch(`${API_URL}/api/company/profile`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      if (res.status === 401 || res.status === 403) {
        logout();
        return;
      }
      const json = await res.json();
      if (json.success) {
        // Ensure qr_codes is parsed
        if (typeof json.data.qr_codes === 'string') {
          try { json.data.qr_codes = JSON.parse(json.data.qr_codes); } catch (e) { json.data.qr_codes = []; }
        }
        setCompanyProfile(json.data);
        // Sync working hours settings from attendance_settings
        if (json.data.attendance_settings) {
          let parsedSettings = json.data.attendance_settings;
          if (typeof parsedSettings === 'string') {
            try { parsedSettings = JSON.parse(parsedSettings); } catch(e) { parsedSettings = null; }
          }
          if (parsedSettings) {
            setWorkingHoursSettings(prev => ({
              ...prev,
              ...parsedSettings,
              schedule: { ...prev.schedule, ...(parsedSettings.schedule || {}) }
            }));
          }
        }
        localStorage.setItem('redsorm_company_profile', JSON.stringify(json.data));
      }
    } catch (err) {
      console.error('Company profile load error:', err);
      const cached = localStorage.getItem('redsorm_company_profile');
      if (cached) {
        setCompanyProfile(JSON.parse(cached));
      } else {
        showToast(t('serverError'), 'error');
      }
    } finally {
      setIsProfileLoading(false);
    }
  }, [API_URL, token, logout, showToast, t]);

  const loadRoles = useCallback(async () => {
    setIsRolesLoading(true);
    try {
      const res = await fetch(`${API_URL}/api/roles`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      const json = await res.json();
      if (json.success) {
        setRolesList(json.data);
        localStorage.setItem('redsorm_roles', JSON.stringify(json.data || []));
      }
    } catch (err) {
      console.error('Roles load error:', err);
      const cached = localStorage.getItem('redsorm_roles');
      if (cached) {
        setRolesList(JSON.parse(cached));
      } else {
        showToast(t('serverError'), 'error');
      }
    } finally {
      setIsRolesLoading(false);
    }
  }, [API_URL, token, showToast, t]);

  const saveRole = async (e) => {
    e.preventDefault();
    try {
      const method = currentRole.id ? 'PUT' : 'POST';
      const url = currentRole.id ? `${API_URL}/api/roles/${currentRole.id}` : `${API_URL}/api/roles`;

      const res = await fetch(url, {
        method,
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify(currentRole)
      });

      const json = await res.json();
      if (json.success) {
        showToast(t('roleSaved'));
        setIsRoleModalOpen(false);
        loadRoles();
      } else {
        showToast(json.message || t('actionFailed'), 'error');
      }
    } catch (err) {
      showToast(t('actionFailed'), 'error');
    }
  };

  const deleteRole = async (id) => {
    openConfirm('Are you sure you want to delete this role?', async () => {
      try {
        const res = await fetch(`${API_URL}/api/roles/${id}`, {
          method: 'DELETE',
          headers: { 'Authorization': `Bearer ${token}` }
        });
        const json = await res.json();
        if (json.success) {
          showToast(t('roleDeleted'));
          loadRoles();
        } else {
          showToast(json.message || t('actionFailed'), 'error');
        }
      } catch (err) {
        showToast(t('actionFailed'), 'error');
      } finally {
        setConfirmModal({ show: false });
      }
    });
  };

  const loadUsers = useCallback(async () => {
    setIsUsersLoading(true);
    try {
      const res = await fetch(`${API_URL}/api/users`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      const json = await res.json();
      if (json.success) {
        setUsersList(json.data);
        localStorage.setItem('redsorm_users', JSON.stringify(json.data || []));
      }
    } catch (err) {
      console.error('Users load error:', err);
      const cached = localStorage.getItem('redsorm_users');
      if (cached) {
        setUsersList(JSON.parse(cached));
      } else {
        showToast(t('serverError'), 'error');
      }
    } finally {
      setIsUsersLoading(false);
    }
  }, [API_URL, token, showToast, t]);

  const saveUser = async (e) => {
    e.preventDefault();
    try {
      const method = currentUser.id ? 'PUT' : 'POST';
      const url = currentUser.id ? `${API_URL}/api/users/${currentUser.id}` : `${API_URL}/api/users`;

      const res = await fetch(url, {
        method,
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify(currentUser)
      });

      const json = await res.json();
      if (json.success) {
        showToast(t('userSaved'));
        setIsUserModalOpen(false);
        loadUsers();
      } else {
        showToast(json.message || t('actionFailed'), 'error');
      }
    } catch (err) {
      showToast(t('actionFailed'), 'error');
    }
  };

  const deleteUser = async (id) => {
    openConfirm('Are you sure you want to delete this user?', async () => {
      try {
        const res = await fetch(`${API_URL}/api/users/${id}`, {
          method: 'DELETE',
          headers: { 'Authorization': `Bearer ${token}` }
        });
        const json = await res.json();
        if (json.success) {
          showToast(t('userDeleted'));
          loadUsers();
        } else {
          showToast(json.message || t('actionFailed'), 'error');
        }
      } catch (err) {
        showToast(t('actionFailed'), 'error');
      } finally {
        setConfirmModal({ show: false });
      }
    });
  };

  const handleFileUpload = async (file) => {
    // --- DIRECT CLOUDINARY UPLOAD (browser → Cloudinary CDN) ---
    // This bypasses the Render backend entirely, saving all image bandwidth on Render.
    // The api_key is safe to expose publicly. The api_secret stays only on the backend.
    const CLOUD_NAME = 'zanmii9x';
    const UPLOAD_PRESET = 'redsorm_unsigned';

    const cloudinaryFormData = new FormData();
    cloudinaryFormData.append('file', file);
    cloudinaryFormData.append('upload_preset', UPLOAD_PRESET);
    cloudinaryFormData.append('folder', 'dineflow_menu_items');

    try {
      const res = await fetch(
        `https://api.cloudinary.com/v1_1/${CLOUD_NAME}/image/upload`,
        { method: 'POST', body: cloudinaryFormData }
      );
      const json = await res.json();
      if (json.secure_url) {
        return json.secure_url;
      }
      // If Cloudinary direct upload fails, fall back to backend route
      console.warn('[UPLOAD] Cloudinary direct upload failed, falling back to backend:', json.error?.message);
    } catch (err) {
      console.warn('[UPLOAD] Cloudinary direct upload error, falling back to backend:', err.message);
    }

    // --- FALLBACK: route through backend (original behaviour) ---
    const formData = new FormData();
    formData.append('file', file);
    try {
      const res = await fetch(`${API_URL}/api/upload`, {
        method: 'POST',
        body: formData,
      });
      const json = await res.json();
      if (json.success) {
        return json.url;
      } else {
        showToast(json.message || t('uploadFailed'), 'error');
        return null;
      }
    } catch (err) {
      showToast(t('uploadFailed'), 'error');
      return null;
    }
  };

  const handleDetectCompanyLocation = async () => {
    if (!isGeolocationAvailable()) {
      showToast('Geolocation is not supported by your browser', 'error');
      return;
    }

    setIsCompanyLocating(true);
    showToast('Getting an accurate GPS fix…', 'info');

    let position;
    try {
      // Wait for a precise fix: a single quick reading is often hundreds of metres
      // off, which would make every radius check fail for real customers.
      position = await getHighAccuracyPosition();
    } catch (err) {
      console.error('Geolocation error:', err);
      showToast(
        err && (err.code === 'PERMISSION_DENIED' || err.code === 'UNSUPPORTED')
          ? 'Location permission is required. Please allow location access.'
          : 'Unable to detect an accurate location. Please turn on GPS, move near a window and try again.',
        'warning'
      );
      setIsCompanyLocating(false);
      return;
    }

    const lat = position.latitude;
    const lng = position.longitude;

    try {
      const res = await fetch(`https://nominatim.openstreetmap.org/reverse?format=json&lat=${lat}&lon=${lng}`);
      const json = await res.json();
      if (json && json.address) {
        const cityName = json.address.city || json.address.town || json.address.village || json.address.county || '';
        const stateName = json.address.state || '';
        const postcode = json.address.postcode || '';
        const addressText = json.display_name || '';

        setCompanyProfile(prev => ({
          ...prev,
          address: prev.address || addressText,
          city: prev.city || cityName,
          state: prev.state || stateName,
          pincode: prev.pincode || postcode,
          qr_latitude: lat,
          qr_longitude: lng
        }));
        showToast(`Location saved (±${formatAccuracy(position.accuracy)}). Remember to Save Settings.`, 'success');
      } else {
        setCompanyProfile(prev => ({
          ...prev,
          qr_latitude: lat,
          qr_longitude: lng
        }));
        showToast(`Coordinates found (±${formatAccuracy(position.accuracy)}), but address details were not available`, 'warning');
      }
    } catch (err) {
      console.error('Reverse geocoding failed:', err);
      setCompanyProfile(prev => ({
        ...prev,
        qr_latitude: lat,
        qr_longitude: lng
      }));
      showToast(`Coordinates saved (±${formatAccuracy(position.accuracy)}), but address lookup failed`, 'warning');
    } finally {
      setIsCompanyLocating(false);
    }
  };

  const handleProfileSubmit = async (e) => {
    e.preventDefault();
    const f = new FormData(e.target);
    const payload = {
      name: f.get('name'),
      logo_url: companyProfile.logo_url, // Use state value
      banner_url: companyProfile.banner_url, // Use state value
      twitter_url: f.get('twitter_url'),
      youtube_url: f.get('youtube_url'),
      instagram_url: f.get('instagram_url'),
      sgst: f.get('sgst'),
      cgst: f.get('cgst'),
      qr_codes: companyProfile.qr_codes || [], // Use state for array
      upi_id: f.get('upi_id'),
      festival_name: f.get('festival_name'),
      festival_discount: f.get('festival_discount') || 0,
      default_currency: f.get('default_currency') || 'INR',
      address: f.get('address'),
      city: f.get('city'),
      state: f.get('state'),
      pincode: f.get('pincode'),
      phone: f.get('phone'),
      collect_customer_info: companyProfile.collect_customer_info !== false,
      no_tables_mode: !!companyProfile.no_tables_mode,
      auto_deduct_inventory: !!companyProfile.auto_deduct_inventory,
      master_qr_location_restriction_enabled: !!companyProfile.master_qr_location_restriction_enabled,
      master_qr_radius_meters: companyProfile.master_qr_radius_meters || 200,
      attendance_location_restriction_enabled: !!companyProfile.attendance_location_restriction_enabled,
      attendance_radius_meters: companyProfile.attendance_radius_meters || 150,
      qr_latitude: companyProfile.qr_latitude || null,
      qr_longitude: companyProfile.qr_longitude || null
    };

    try {
      const res = await fetch(`${API_URL}/api/company/profile`, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify(payload),
      });

      if (res.status === 401 || res.status === 403) {
        logout();
        return;
      }

      const json = await res.json();
      if (json.success) {
        // Ensure qr_codes is parsed
        if (typeof json.data.qr_codes === 'string') {
          try { json.data.qr_codes = JSON.parse(json.data.qr_codes); } catch (e) { json.data.qr_codes = []; }
        }
        setCompanyProfile(json.data);
        // Refresh currency context to update all pages
        await refreshCurrency();
        showToast(t('profileUpdated'), 'success');
      } else {
        showToast(json.message || t('actionFailed'), 'error');
      }
    } catch (err) {
      showToast(t('actionFailed'), 'error');
    }
  };


  const togglePin = (id) => {
    setVisiblePins(prev => ({ ...prev, [id]: !prev[id] }));
  };

  const handleShowStaffQR = () => {
    setIsStaffQRModalOpen(true);
  };

  const printStaffPortalQR = () => {
    const windowUrl = 'about:blank';
    const uniqueName = new Date();
    const windowName = 'Print' + uniqueName.getTime();
    const printWindow = window.open(windowUrl, windowName, 'width=600,height=600');

    printWindow.document.write(`
      <html>
        <head>
          <title>Staff Portal QR</title>
          <style>
            body { display: flex; flex-direction: column; align-items: center; justify-content: center; height: 100vh; margin: 0; font-family: sans-serif; text-align: center; }
            img { width: 350px; height: 350px; margin-bottom: 25px; border: 1px solid #eee; padding: 10px; border-radius: 15px; }
            h1 { color: #059669; font-size: 28px; margin-bottom: 10px; }
            p { color: #666; font-size: 18px; max-width: 80%; }
            .footer { margin-top: 30px; font-size: 12px; color: #999; }
          </style>
        </head>
        <body>
          <h1>Staff Portal Access</h1>
          <img src="https://api.qrserver.com/v1/create-qr-code/?size=350x350&data=${encodeURIComponent(`${BASE_URL}/staff`)}" />
          <p>Scan this QR code to access the Staff Attendance, Orders, and Kitchen management portal.</p>
          <div class="footer">Generated by RedSorm Digital Restaurant System</div>
          <script>
            setTimeout(() => {
              window.print();
              window.close();
            }, 500);
          </script>
        </body>
      </html>
    `);
    printWindow.document.close();
  };

  const loadStaff = useCallback(async () => {
    try {
      // Fetch staff filtered by branch
      const branchQuery = selectedBranch ? `?branch_id=${selectedBranch}` : '';
      const res = await fetch(`${API_URL}/api/staff${branchQuery}`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      const json = await res.json();
      if (json.success) {
        setStaffList(json.data);
        localStorage.setItem('redsorm_staff', JSON.stringify(json.data || []));
      }
    } catch (err) {
      console.error('Staff load error:', err);
      const cached = localStorage.getItem('redsorm_staff');
      if (cached) {
        const parsed = JSON.parse(cached);
        const filtered = selectedBranch
          ? parsed.filter(s => String(s.branch_id) === String(selectedBranch))
          : parsed;
        setStaffList(filtered);
      }
    }
  }, [API_URL, token, selectedBranch]);




  const handleGlobalRefresh = useCallback(async () => {
    if (!token) return;
    showToast(t('refreshing') || 'Refreshing all data...', 'info');
    try {
      if (typeof loadMenu === 'function') await loadMenu();
      if (typeof loadTables === 'function') await loadTables();
      if (typeof loadCategories === 'function') await loadCategories();
      if (typeof loadOrders === 'function') await loadOrders();
      if (typeof loadTableGroups === 'function') await loadTableGroups();
      if (typeof loadCompanyProfile === 'function') await loadCompanyProfile();
      if (typeof loadRoles === 'function') await loadRoles();
      if (typeof loadUsers === 'function') await loadUsers();
      if (typeof loadStaff === 'function') await loadStaff();
      if (typeof loadLeaves === 'function') await loadLeaves();
      if (typeof loadAttendanceLogs === 'function') await loadAttendanceLogs();

      showToast(t('refreshed') || 'All data refreshed!', 'success');
    } catch (err) {
      showToast(t('refreshFailed') || 'Refresh failed', 'error');
    }
  }, [token, t, loadMenu, loadTables, loadCategories, loadOrders, loadTableGroups, loadCompanyProfile, loadRoles, loadUsers, loadStaff, loadLeaves, loadAttendanceLogs, showToast]);

  // Auto-load staff when branch changes or component mounts
  React.useEffect(() => {
    if (token) {
      loadStaff();
    }
  }, [token, selectedBranch, loadStaff]);

  // Offline Synchronization Manager & Heartbeat Sync Hook
  const syncOfflineOrders = useCallback(async () => {
    try {
      const queue = await getQueue();
      if (!queue || queue.length === 0) return;

      console.log(`[Offline Sync Admin] Found ${queue.length} offline orders to sync...`);
      showToast(`Syncing ${queue.length} offline order(s)...`, 'info');

      let syncedCount = 0;

      // POST each queued order individually to /api/orders (the correct endpoint)
      for (const order of queue) {
        try {
          const apiOrderData = {
            table_number: order.is_offline_delivery ? 0 : (order.table_number === 'Delivery' ? 0 : order.table_number),
            is_waiting: order.is_offline_delivery ? false : order.is_waiting,
            items: (order.items || []).map(item => ({
              id: item.menu_item_id || item.id,
              quantity: item.quantity,
              price_inr: item.price_inr,
              price_usd: item.price_usd,
              name: item.item_name || item.name,
              discount_percentage: item.discount_percentage || 0
            })),
            currency: order.currency,
            payment_method: order.payment_method,
            customer_id: order.customer_id,
            customer_name: order.customer_name,
            customer_phone: order.customer_phone || null,
            branch_id: order.branch_id,
            session_id: order.session_id,
            notes: order.notes,
            order_status: order.order_status,
            is_offline_delivery: order.is_offline_delivery ? true : false,
            delivery_address: order.delivery_address || null,
            delivery_city: order.delivery_city || null,
            delivery_state: order.delivery_state || null,
            delivery_pincode: order.delivery_pincode || null,
            delivery_lat: order.delivery_lat || null,
            delivery_lng: order.delivery_lng || null
          };

          const res = await fetch(`${API_URL}/api/orders`, {
            method: 'POST',
            headers: {
              'Content-Type': 'application/json',
              'Authorization': `Bearer ${token}`
            },
            body: JSON.stringify(apiOrderData)
          });

          const json = await res.json();
          if (json.success) {
            console.log(`[Offline Sync Admin] Synchronized order ${order.id} successfully!`);
            await removeFromQueue(order.id);
            syncedCount++;
          } else {
            console.error(`[Offline Sync Admin] Failed to sync order ${order.id}:`, json.message);
          }
        } catch (orderErr) {
          console.error(`[Offline Sync Admin] Network error syncing order ${order.id}:`, orderErr);
        }
      }

      if (syncedCount > 0) {
        showToast(`${syncedCount} offline order(s) synchronized successfully!`, 'success');
        // Notify KDS in same browser to refresh from server
        try {
          const syncChannel = new BroadcastChannel('redsorm_sync_channel');
          syncChannel.postMessage({ type: 'ORDERS_SYNCED' });
          setTimeout(() => { try { syncChannel.close(); } catch (e) {} }, 1000);
          console.log('[Offline Sync Admin] Broadcasted ORDERS_SYNCED to KDS.');
        } catch (bcErr) {
          console.warn('[Offline Sync Admin] BroadcastChannel error on sync complete:', bcErr);
        }
        if (typeof loadTables === 'function') loadTables();
        if (typeof loadOrders === 'function') loadOrders();
      }
    } catch (err) {
      console.error('[Offline Sync Admin] Connection error during sync:', err);
    }
  }, [API_URL, token, loadTables, loadOrders, showToast]);

  useEffect(() => {
    if (!token) return;

    const handleOnline = () => {
      console.log('[Connection] Network online. Syncing offline orders and reloading...');
      syncOfflineOrders();
      if (typeof loadOrders === 'function') loadOrders();
    };

    window.addEventListener('online', handleOnline);

    const intervalId = setInterval(() => {
      if (navigator.onLine) {
        syncOfflineOrders();
      }
    }, 60000);

    if (navigator.onLine) {
      syncOfflineOrders();
    }

    return () => {
      window.removeEventListener('online', handleOnline);
      clearInterval(intervalId);
    };
  }, [token, syncOfflineOrders, loadOrders]);

  // Broadcast Channel Listener for POS-KDS sync in Admin Page
  useEffect(() => {
    try {
      const channel = new BroadcastChannel('redsorm_sync_channel');
      channel.onmessage = (event) => {
        const { type, data } = event.data || {};
        if (type === 'NEW_OFFLINE_ORDER' && data) {
          // Filter by branch
          if (selectedBranch && data.branch_id && String(data.branch_id) !== String(selectedBranch)) {
            return;
          }
          console.log('[Admin Page] Received broadcasted offline order:', data);
          setOrders(prev => {
            if (prev.some(o => String(o.id) === String(data.id) || (o.order_id && data.order_id && String(o.order_id) === String(data.order_id)))) {
              return prev;
            }
            return [data, ...prev];
          });
          showToast(`New offline order placed for Table ${data.table_number}!`, 'warning');
        }
      };
      return () => channel.close();
    } catch (err) {
      console.warn('Broadcast Channel disabled:', err);
    }
  }, [showToast, selectedBranch]);


  const deleteStaff = async (id) => {
    openConfirm('Are you sure you want to delete this staff member?', async () => {
      try {
        const res = await fetch(`${API_URL}/api/staff/${id}`, {
          method: 'DELETE',
          headers: { 'Authorization': `Bearer ${token}` }
        });
        const json = await res.json();
        if (json.success) {
          showToast(t('userDeleted'), 'success');
          loadStaff();
        } else {
          showToast(json.message || t('actionFailed'), 'error');
        }
      } catch (err) {
        console.error(err);
        showToast(t('networkError'), 'error');
      } finally {
        setConfirmModal({ show: false });
      }
    });
  };

  const handleStaffSubmit = async (e) => {
    e.preventDefault();
    const f = new FormData(e.target);

    // Role Logic
    let role = f.get('role');
    const customRoleName = f.get('roleValue'); // Hidden input for custom role name

    // Fix: If showNewRoleInput is true, 'role' might be missing from form data
    if ((!role || role === 'custom' || role === 'add-new') && customRoleName) {
      role = customRoleName.trim();
    }

    const payload = {
      name: f.get('name'),
      role: role,
      pin: f.get('pin'),
      email: f.get('email'),
      phone: f.get('phone'),
      salary: parseFloat(f.get('salary')) || 0,
      daily_rate: parseFloat(f.get('daily_rate')) || 0,
      branch_id: f.get('branch_id') ? parseInt(f.get('branch_id'), 10) : null // null = no branch, number = specific branch
    };
    // Add or Edit staff endpoint
    try {
      let url = `${API_URL}/api/staff`;
      let method = 'POST';

      if (editingStaff) {
        url = `${API_URL}/api/staff/${editingStaff.id}`;
        method = 'PUT';
      }

      const res = await fetch(url, {
        method: method,
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify(payload),
      });
      const json = await res.json();
      if (json.success) {
        showToast(editingStaff ? t('staffUpdated') || 'Staff Updated' : t('staffAdded') || 'Staff Added', 'success');

        // If a new role was created, we should refresh the roles list too
        if (showNewRoleInput && customRoleName) {
          try {
            // Check if role already exists in rolesList to avoid duplicates
            const roleExists = rolesList.some(r => r.name.toLowerCase() === customRoleName.trim().toLowerCase());
            if (!roleExists) {
              await fetch(`${API_URL}/api/roles`, {
                method: 'POST',
                headers: {
                  'Content-Type': 'application/json',
                  'Authorization': `Bearer ${token}`
                },
                body: JSON.stringify({
                  name: customRoleName.trim(),
                  permissions: [], // Default empty permissions
                  allowed_branches: branches.length === 1 ? [branches[0].id] : []
                })
              });
              loadRoles();
            }
          } catch (e) {
            console.error('Failed to auto-create role:', e);
          }
        }

        loadStaff();
        e.target.reset();
        setEditingStaff(null);
        setShowNewRoleInput(false);
        setIsStaffModalOpen(false);
      } else {
        showToast(json.message, 'error');
      }
    } catch (err) {
      showToast(t('actionFailed'), 'error');
    }
  };

  // const handleLeaveAction = async (id, status) => {
  //   try {
  //     const res = await fetch(`${API_URL}/api/staff/leaves/${id}/status`, {
  //       method: 'PUT',
  //       headers: {
  //         'Content-Type': 'application/json',
  //         'Authorization': `Bearer ${token}`
  //       },
  //       body: JSON.stringify({ status }),
  //     });
  //     const json = await res.json();
  //     if (json.success) {
  //       showToast(t('actionFailed').replace('failed', status)); // A bit hacky but works for Approve/Reject
  //       loadLeaves();
  //     }
  //   } catch (err) {
  //     showToast(t('actionFailed'), 'error');
  //   }
  // };

  // ── Add Category ────────────────────────────
  const addNewCategory = async () => {
    const name = newCategoryName.trim();
    if (!name) {
      showToast(t('enterName') || 'Please enter a category name', 'error');
      return;
    }
    if (categories.some(c => c.name === name)) {
      showToast(t('categoryExists') || 'Category already exists', 'error');
      return;
    }
    try {
      const res = await fetch(`${API_URL}/api/categories`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify({ name, is_self_service: isSelfServiceNewCategory }),
      });
      if (res.status === 401 || res.status === 403) {
        logout();
        return;
      }
      const json = await res.json();
      if (json.success) {
        setCategories(prev => [...prev, json.data]);
        setNewCategoryName('');
        setIsSelfServiceNewCategory(false);
        setShowNewCategoryInput(false);
        // Auto-select the new category if in menu modal
        const categorySelect = document.querySelector('select[name="category"]');
        if (categorySelect) {
          categorySelect.value = name;
          setCurrentMenuItem(prev => ({ ...prev, category: name }));
        }
        showToast(t('categoryAdded') || 'Category added successfully!', 'success');
        // Reload categories to ensure consistency
        loadCategories();
      } else {
        showToast(json.message || t('serverError'), 'error');
      }
    } catch (err) {
      console.error('Category creation error:', err);
      showToast(t('serverError') || 'Failed to create category', 'error');
    }
  };

  const toggleCategorySelfService = async (category) => {
    try {
      const res = await fetch(`${API_URL}/api/categories/${category.id}`, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify({ is_self_service: !category.is_self_service }),
      });
      const json = await res.json();
      if (json.success) {
        setCategories(prev => prev.map(c => c.id === category.id ? { ...c, is_self_service: !c.is_self_service } : c));
        showToast('Updated successfully');
      }
    } catch (err) {
      showToast('Update failed', 'error');
    }
  };

  const toggleAllCategoriesSelfService = async (enable) => {
    try {
      const res = await fetch(`${API_URL}/api/categories/self-service/all`, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify({ is_self_service: enable }),
      });
      const json = await res.json();
      if (json.success) {
        // Update all local
        setCategories(json.data);
        showToast(enable ? 'Enabled for all' : 'Disabled for all');
      }
    } catch (err) {
      showToast('Bulk update failed', 'error');
    }
  };


  // ── Group Submit ───────────────────────────
  const handleGroupSubmit = async (e) => {
    e.preventDefault();
    const name = e.target.name.value.trim();
    if (!name) return showToast('Enter group name', 'error');

    const payload = {
      name,
      branch_id: selectedBranch || null // null = all branches, number = specific branch
    };

    try {
      const url = currentGroup.id ? `${API_URL}/api/table-groups/${currentGroup.id}` : `${API_URL}/api/table-groups`;
      const res = await fetch(url, {
        method: currentGroup.id ? 'PUT' : 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify(payload),
      });
      if (res.status === 401 || res.status === 403) {
        logout();
        return;
      }
      const json = await res.json();
      if (json.success) {
        closeGroupModal();
        loadTableGroups();
        loadTables();
        showToast(currentGroup.id ? t('groupUpdated') : t('groupAdded'), 'success');
      } else {
        showToast(json.message || t('actionFailed'), 'error');
      }
    } catch {
      showToast(t('actionFailed'), 'error');
    }
  };

  // ── Delete Group ───────────────────────────
  const deleteGroup = (id) => {
    openConfirm('Delete group? Tables in this group will be set to Non AC.', async () => {
      try {
        const res = await fetch(`${API_URL}/api/table-groups/${id}`, {
          method: 'DELETE',
          headers: { 'Authorization': `Bearer ${token}` }
        });
        if (res.status === 401 || res.status === 403) {
          logout();
          return;
        }
        const json = await res.json();
        if (json.success) {
          loadTableGroups();
          loadTables();
          showToast(t('itemDeleted'), 'success');
        } else {
          showToast(json.message || t('actionFailed'), 'error');
        }
      } catch {
        showToast(t('actionFailed'), 'error');
      }
      setConfirmModal({ show: false });
    });
  };

  // ── Handlers ───────────────────────────────────
  const handleImageChange = (e) => {
    const file = e.target.files[0];
    if (!file) return;
    setMenuImageFile(file);
    const reader = new FileReader();
    reader.onloadend = () => setMenuImagePreview(reader.result);
    reader.readAsDataURL(file);
  };

  const isTableSubmittingRef = useRef(false);
  const isMenuSubmittingRef = useRef(false);

  // ── Submit Handlers ─────────────────────────────
  const handleMenuSubmit = async (e) => {
    e.preventDefault();
    if (isMenuSubmittingRef.current || isSubmittingMenu) return;
    isMenuSubmittingRef.current = true;
    setIsSubmittingMenu(true);
    const f = new FormData(e.target);
    const id = f.get('id');
    const category = f.get('category');
    let image_url = currentMenuItem.image_url || null;
    if (menuImageFile) {
      const uploadedUrl = await handleFileUpload(menuImageFile);
      if (uploadedUrl) {
        image_url = uploadedUrl;
      }
    }

    const price = parseFloat(f.get('price'));
    const currency_code = f.get('currency_code');

    const isVegVal = f.get('is_veg');
    const is_veg = (isVegVal === '0' || isVegVal === 'false' || isVegVal === false) ? 0 : 1;

    const basePayload = {
      name: f.get('name'),
      description: f.get('description'),
      ingredients: f.get('ingredients'),
      is_veg,
      price,
      currency_code,
      price_inr: currency_code === 'INR' ? price : (id ? (currentMenuItem.price_inr || 0) : price),
      price_usd: currency_code === 'USD' ? price : (id ? (currentMenuItem.price_usd || 0) : price),
      category,
      is_available: f.get('is_available') === 'on',
      image_url,
      nutritional_info: f.get('nutritional_info'),
      vitamins: f.get('vitamins'),
      is_special: f.get('is_special') === 'on',
      special_duration: f.get('special_duration') || null,
      discount_percentage: parseFloat(f.get('discount_percentage')) || 0,
    };

    try {
      // If editing, just update the single item
      if (id) {
        const payload = { ...basePayload, branch_id: currentMenuItem.branch_id };
        const url = `${API_URL}/api/menu/${id}`;
        const res = await fetch(url, {
          method: 'PUT',
          headers: {
            'Content-Type': 'application/json',
            'Authorization': `Bearer ${token}`
          },
          body: JSON.stringify(payload),
        });

        if (res.status === 401 || res.status === 403) {
          showToast(t('sessionExpired'), 'error');
          return;
        }
        const json = await res.json();
        if (json.success) {
          // If the currency selected for this item is different from global currency,
          // update the company's default currency automatically as requested.
          if (currency_code && currency_code !== currency) {
            try {
              await fetch(`${API_URL}/api/company/profile`, {
                method: 'PUT',
                headers: {
                  'Content-Type': 'application/json',
                  'Authorization': `Bearer ${token}`
                },
                body: JSON.stringify({ ...companyProfile, default_currency: currency_code }),
              });
              await refreshCurrency();
            } catch (e) { console.error('Failed to sync global currency', e); }
          }

          closeMenuModal();
          loadMenu();
          loadCategories();
          showToast(t('itemUpdated'), 'success');
        } else {
          showToast(json.message || t('actionFailed'), 'error');
        }
      } else {
        // Adding new item
        // If "All Branches" selected (selectedBranch is null/falsy), create ONE global item with branch_id = null
        // If specific branch selected, create item for that branch only
        const payload = { ...basePayload, branch_id: selectedBranch || null };
        const res = await fetch(`${API_URL}/api/menu`, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            'Authorization': `Bearer ${token}`
          },
          body: JSON.stringify(payload),
        });

        if (res.status === 401 || res.status === 403) {
          showToast(t('sessionExpired'), 'error');
          return;
        }
        const json = await res.json();
        if (json.success) {
          // If the currency selected for this item is different from global currency,
          // update the company's default currency automatically as requested.
          if (currency_code && currency_code !== currency) {
            try {
              await fetch(`${API_URL}/api/company/profile`, {
                method: 'PUT',
                headers: {
                  'Content-Type': 'application/json',
                  'Authorization': `Bearer ${token}`
                },
                body: JSON.stringify({ ...companyProfile, default_currency: currency_code }),
              });
              await refreshCurrency();
            } catch (e) { console.error('Failed to sync global currency', e); }
          }

          closeMenuModal();
          loadMenu();
          loadCategories();
          const branchLabel = selectedBranch
            ? (branches.find(b => b.id === selectedBranch)?.name || 'Selected Branch')
            : (branches.length === 0 ? t('noBranch') || 'No Branch' : t('all') || 'All');
          showToast(`${t('itemAdded')} (${branchLabel})`, 'success');
        } else {
          showToast(json.message || t('actionFailed'), 'error');
        }
      }
    } catch (error) {
      console.error('Menu submit error:', error);
      showToast(t('actionFailed'), 'error');
    } finally {
      setIsSubmittingMenu(false);
      setTimeout(() => { isMenuSubmittingRef.current = false; }, 500);
    }
  };

  const handleTableSubmit = async (e) => {
    e.preventDefault();
    if (isTableSubmittingRef.current) return;

    const f = new FormData(e.target);
    const id = f.get('id');
    const num = parseInt(f.get('table_number'), 10);
    const groupIdValue = f.get('group_id');
    const groupId = groupIdValue && groupIdValue !== '' ? parseInt(groupIdValue, 10) : null;
    const isActive = f.get('is_active') === 'on';

    const basePayload = {
      table_number: num,
      table_name: f.get('table_name') || null,
      group_id: groupId,
      is_active: isActive,
      is_available: true
    };

    isTableSubmittingRef.current = true;
    setIsTableSubmitting(true);

    try {
      // If editing, just update the single table
      if (id) {
        const payload = { ...basePayload, branch_id: currentTable.branch_id };
        const url = `${API_URL}/api/tables/${id}`;
        const res = await fetch(url, {
          method: 'PUT',
          headers: {
            'Content-Type': 'application/json',
            'Authorization': `Bearer ${token}`
          },
          body: JSON.stringify(payload),
        });

        if (res.status === 401 || res.status === 403) {
          showToast(t('sessionExpired'), 'error');
          return;
        }

        const json = await res.json();
        if (res.ok && json.success) {
          closeTableModal();
          loadTables();
          showToast(t('itemUpdated'), 'success');
        } else {
          showToast(json.message || 'Failed', 'error');
        }
      } else {
        // Adding new table
        // If "All Branches" selected (selectedBranch is null/falsy), create ONE global table with branch_id = null
        // If specific branch selected, create table for that branch only
        const payload = { ...basePayload, branch_id: selectedBranch || null };
        const res = await fetch(`${API_URL}/api/tables`, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            'Authorization': `Bearer ${token}`
          },
          body: JSON.stringify(payload),
        });

        if (res.status === 401 || res.status === 403) {
          showToast(t('sessionExpired'), 'error');
          return;
        }

        const json = await res.json();
        if (res.ok && json.success) {
          closeTableModal();
          loadTables();
          const branchLabel = selectedBranch
            ? (branches.find(b => b.id === selectedBranch)?.name || 'Selected Branch')
            : (branches.length === 0 ? t('noBranch') || 'No Branch' : t('all') || 'All');
          showToast(`${t('tableAdded') || 'Table added!'} (${branchLabel})`, 'success');
          // Show QR code for the newly created table
          setTimeout(() => {
            const newTable = { ...basePayload, id: json.data.id };
            showQRModal(newTable);
          }, 800); // Increased delay to ensure modal is ready
        } else {
          showToast(json.message || 'Failed', 'error');
        }
      }
    } catch (err) {
      console.error('Table submit error:', err);
      showToast('Network error', 'error');
    } finally {
      setTimeout(() => {
        isTableSubmittingRef.current = false;
        setIsTableSubmitting(false);
      }, 500);
    }
  };

  // ── DELETE ─────────
  const deleteMenuItem = (id) => {
    openConfirm('Delete item?', async () => {
      try {
        const res = await fetch(`${API_URL}/api/menu/${id}`, {
          method: 'DELETE',
          headers: { 'Authorization': `Bearer ${token}` }
        });
        if (res.status === 401 || res.status === 403) {
          logout();
          return;
        }
        const json = await res.json();
        if (json.success) {
          setMenuItems(prev => prev.filter(item => item.id !== id));
          showToast(t('itemDeleted'), 'success');
          loadCategories();
        } else {
          showToast(json.message || t('actionFailed'), 'error');
        }
      } catch (err) {
        showToast(t('actionFailed'), 'error');
      }
      setConfirmModal({ show: false });
    });
  };

  const downloadMenuItems = () => {
    if (!menuItems || menuItems.length === 0) {
      alert(t('noMenuItemsToExport') || 'No menu items to export');
      return;
    }

    const reportData = [];
    const headers = [
      'Name',
      'Category',
      `Price (${currency})`,
      'Veg / Non-Veg',
      'Description',
      'Nutritional Info',
      'Vitamins',
      'Discount %',
      'Available',
      'Self Service',
      'Is Special',
      'Special Duration (days)',
      'Branch'
    ];
    reportData.push(headers);

    menuItems.forEach(item => {
      const isVegText = (item.is_veg === 0 || item.is_veg === false || item.is_veg === '0' || item.is_veg === 'false') ? 'Non-Veg' : 'Veg';
      const row = [
        item.name || '',
        item.category || '',
        (currency === 'INR' ? item.price_inr : item.price_usd) || 0,
        isVegText,
        item.description || '',
        item.nutritional_info || '',
        item.vitamins || '',
        item.discount_percentage || 0,
        item.is_available ? 'Yes' : 'No',
        item.is_self_service ? 'Yes' : 'No',
        item.is_special ? 'Yes' : 'No',
        item.special_duration || '',
        item.branch_name || t('allBranches')
      ];
      reportData.push(row);
    });

    const ws = XLSX.utils.aoa_to_sheet(reportData);
    const wscols = headers.map(() => ({ wch: 20 }));
    ws['!cols'] = wscols;

    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, 'Menu Items');

    const fileName = `Menu_Items_${new Date().toISOString().split('T')[0]}.xlsx`;
    XLSX.writeFile(wb, fileName);
  };

  const uploadMenuItems = (e) => {
    const file = e.target.files[0];
    if (!file) return;

    setIsUploading(true);
    const reader = new FileReader();
    reader.onload = async (evt) => {
      try {
        const bstr = evt.target.result;
        const wb = XLSX.read(bstr, { type: 'binary' });
        const wsname = wb.SheetNames[0];
        const ws = wb.Sheets[wsname];
        const data = XLSX.utils.sheet_to_json(ws, { header: 1 });

        // Skip header row
        const rows = data.slice(1).filter(row => row && row[0]); // Filter out empty rows or rows without names

        if (rows.length === 0) {
          showToast(t('noDataInFile') || 'No data found in the file', 'error');
          setIsUploading(false);
          return;
        }

        const headerRow = (data[0] || []).map(h => String(h || '').toLowerCase().trim());
        const findColIdx = (keywords, fallbackIdx) => {
          const idx = headerRow.findIndex(h => keywords.some(k => h.includes(k)));
          return idx !== -1 ? idx : fallbackIdx;
        };

        const hasVegHeader = headerRow.some(h => h.includes('veg') || h.includes('food type'));
        const hasIngHeader = headerRow.some(h => h.includes('ingredient'));

        const nameIdx = findColIdx(['name'], 0);
        const catIdx = findColIdx(['category'], 1);
        const priceIdx = findColIdx(['price'], 2);
        const vegIdx = hasVegHeader ? findColIdx(['veg', 'food type'], -1) : -1;
        const descIdx = findColIdx(['description'], hasVegHeader ? 4 : 3);
        const ingIdx = hasIngHeader ? findColIdx(['ingredient'], -1) : -1;
        const nutIdx = findColIdx(['nutrition'], hasVegHeader && hasIngHeader ? 6 : (hasVegHeader ? 5 : 4));
        const vitIdx = findColIdx(['vitamin'], hasVegHeader && hasIngHeader ? 7 : (hasVegHeader ? 6 : 5));
        const discIdx = findColIdx(['discount'], hasVegHeader && hasIngHeader ? 8 : (hasVegHeader ? 7 : 6));
        const availIdx = findColIdx(['avail'], hasVegHeader && hasIngHeader ? 9 : (hasVegHeader ? 8 : 7));
        const selfIdx = findColIdx(['self'], hasVegHeader && hasIngHeader ? 10 : (hasVegHeader ? 9 : 8));
        const specIdx = findColIdx(['special duration', 'special expiry'], -1);
        const isSpecIdx = findColIdx(['is special'], hasVegHeader && hasIngHeader ? 11 : (hasVegHeader ? 10 : 9));
        const durIdx = specIdx !== -1 ? specIdx : (hasVegHeader && hasIngHeader ? 12 : (hasVegHeader ? 11 : 10));

        let successCount = 0;
        let failCount = 0;
        let errors = [];

        const getBool = (val) => {
          if (val === undefined || val === null) return false;
          if (typeof val === 'string') {
            const s = val.toLowerCase().trim();
            return s === 'yes' || s === 'y' || s === 'true' || s === '1' || s === 'active';
          }
          return !!val;
        };

        const getVegBool = (val) => {
          if (val === undefined || val === null || val === '') return true;
          if (typeof val === 'string') {
            const s = val.toLowerCase().trim();
            if (s.includes('non') || s === 'no' || s === 'false' || s === '0' || s === 'n') return false;
            if (s.includes('veg') || s === 'yes' || s === 'true' || s === '1' || s === 'y') return true;
          }
          return val !== 0 && val !== false;
        };

        // Initial set of categories for quick lookup
        const existingCategoryNames = new Set(categories.map(c => String(c.name || '').toLowerCase().trim()));
        const createdInThisSession = new Set();

        // Process each item
        for (let i = 0; i < rows.length; i++) {
          const row = rows[i];
          try {
            const catName = String((catIdx !== -1 ? row[catIdx] : row[1]) || 'General').trim();
            const isSelfServe = selfIdx !== -1 ? getBool(row[selfIdx]) : getBool(row[8]);

            // Auto-create category if it doesn't exist
            if (catName.toLowerCase() !== 'general' &&
              !existingCategoryNames.has(catName.toLowerCase()) &&
              !createdInThisSession.has(catName.toLowerCase())) {
              try {
                const catRes = await fetch(`${API_URL}/api/categories`, {
                  method: 'POST',
                  headers: {
                    'Authorization': `Bearer ${token}`,
                    'Content-Type': 'application/json'
                  },
                  body: JSON.stringify({ name: catName, is_self_service: isSelfServe })
                });
                if (catRes.ok) {
                  createdInThisSession.add(catName.toLowerCase());
                }
              } catch (catErr) {
                console.warn(`Could not auto-create category "${catName}":`, catErr);
              }
            }

            const p = parseFloat(priceIdx !== -1 ? row[priceIdx] : row[2]) || 0;
            const itemData = {
              name: String((nameIdx !== -1 ? row[nameIdx] : row[0]) || '').trim(),
              category: catName,
              price: p,
              price_inr: currency === 'INR' ? p : p * 80, // Default conversion if needed
              price_usd: currency === 'USD' ? p : p / 80,
              currency_code: currency || 'INR',
              is_veg: vegIdx !== -1 ? getVegBool(row[vegIdx]) : true,
              description: String((descIdx !== -1 ? row[descIdx] : row[3]) || '').trim(),
              ingredients: ingIdx !== -1 ? String(row[ingIdx] || '').trim() : '',
              nutritional_info: String((nutIdx !== -1 ? row[nutIdx] : row[4]) || '').trim(),
              vitamins: String((vitIdx !== -1 ? row[vitIdx] : row[5]) || '').trim(),
              discount_percentage: parseFloat(discIdx !== -1 ? row[discIdx] : row[6]) || 0,
              is_available: availIdx !== -1 ? getBool(row[availIdx]) : getBool(row[7]),
              is_self_service: isSelfServe,
              is_special: isSpecIdx !== -1 ? getBool(row[isSpecIdx]) : getBool(row[9]),
              special_duration: (durIdx !== -1 && row[durIdx]) ? parseInt(row[durIdx]) : null,
              branch_id: selectedBranch || null
            };

            const res = await fetch(`${API_URL}/api/menu`, {
              method: 'POST',
              headers: {
                'Authorization': `Bearer ${token}`,
                'Content-Type': 'application/json'
              },
              body: JSON.stringify(itemData)
            });

            const json = await res.json();
            if (json.success) {
              successCount++;
            } else {
              failCount++;
              errors.push(`Row ${i + 2}: ${json.message || 'Unknown error'}`);
            }
          } catch (err) {
            failCount++;
            errors.push(`Row ${i + 2}: ${err.message}`);
          }
        }

        if (failCount === 0) {
          showToast(t('itemsImportedSuccessfully', { count: successCount }) || `${successCount} items imported successfully!`, 'success');
        } else {
          showToast(`${successCount} success, ${failCount} failed. Check console for details.`, 'warning');
          console.error('Import Errors:', errors);
        }

        loadMenu();
        loadCategories(); // Refresh categories in case new ones were added
      } catch (error) {
        console.error('Upload error:', error);
        showToast(t('uploadFailed') || 'Upload failed. Please check file format.', 'error');
      } finally {
        setIsUploading(false);
      }
    };
    reader.readAsBinaryString(file);
    // Reset file input
    e.target.value = '';
  };

  const deleteTable = (id) => {
    openConfirm('Delete table & QR?', async () => {
      try {
        const res = await fetch(`${API_URL}/api/tables/${id}`, {
          method: 'DELETE',
          headers: { 'Authorization': `Bearer ${token}` }
        });
        if (res.status === 401 || res.status === 403) {
          logout();
          return;
        }
        const json = await res.json();
        if (json.success) {
          loadTables();
          loadOrders();
          showToast('Deleted', 'success');
        } else {
          showToast(json.message || 'Failed to delete table', 'error');
        }
      } catch (err) {
        console.error('Error deleting table:', err);
        showToast('Error deleting table', 'error');
      } finally {
        setConfirmModal({ show: false });
      }
    });
  };

  // ── Modals ─────────────────────────────
  const showAddMenuModal = () => {
    setCurrentMenuItem({});
    setMenuImagePreview(null);
    setMenuImageFile(null);
    setShowNewCategoryInput(false);
    setNewCategoryName('');
    setIsMenuModalOpen(true);
  };

  const editMenuItem = (item) => {
    setCurrentMenuItem(item);
    setMenuImagePreview(item.image_url || null);
    setMenuImageFile(null);
    setShowNewCategoryInput(false);
    setNewCategoryName('');
    setIsMenuModalOpen(true);
  };

  const closeMenuModal = () => setIsMenuModalOpen(false);
  const closeTableModal = () => setIsTableModalOpen(false);

  const showAddTableModal = () => {
    setCurrentTable({ group_id: null });
    setIsTableModalOpen(true);
  };

  const editTable = (t) => {
    setCurrentTable(t);
    setIsTableModalOpen(true);
  };

  const showAddGroupModal = () => {
    setCurrentGroup({ name: '' });
    setIsGroupModalOpen(true);
  };

  const editGroup = (g) => {
    setCurrentGroup(g);
    setIsGroupModalOpen(true);
  };

  const closeGroupModal = () => {
    setIsGroupModalOpen(false);
    setCurrentGroup({ name: '' });
  };

  const getGroupName = (groupId) => {
    const group = tableGroups.find(g => g.id === groupId);
    return group ? group.name : (t('nonAc') || 'Non AC');
  };

  const showQRModal = (t) => {
    setSelectedTableForQR(t);
    setIsQRModalOpen(true);
    setTimeout(() => {
      generateQRCode(t.id, t.table_number, t.branch_id);
    }, 500); // Increased delay to ensure ref is populated
  };

  const closeQRModal = () => {
    setIsQRModalOpen(false);
    setSelectedTableForQR(null);
  };

  const showOrdersForTable = (t) => {
    const tableOrders = orders.filter(o => {
      const matchTable = String(o.table_number) === String(t.table_number);
      const oBranch = o.branch_id ? String(o.branch_id) : '';
      const tBranch = t.branch_id ? String(t.branch_id) : '';
      const matchBranch = !o.branch_id || !t.branch_id || oBranch === tBranch;
      return matchTable && !o.is_waiting && matchBranch;
    });
    setSelectedTableOrders(tableOrders);
    setSelectedTableNum(t.table_number);
    setShowOrdersModal(true);
  };

  const closeOrdersModal = () => {
    setShowOrdersModal(false);
    setSelectedTableOrders([]);
    setSelectedTableNum(null);
  };

  const handlePrintBillForSession = async (sessionId, ordersInSession) => {
    const companyName = companyProfile.name || 'Restaurant Invoice';
    const invoiceId = `INV-SES-${Date.now().toString().slice(-6)}`;
    const dateStr = new Date().toLocaleString();
    const tableNum = ordersInSession[0]?.table_number || selectedTableNum || 'N/A';

    // Extract customer name and phone from the session orders
    const sessionCustomerName = ordersInSession.find(o => o.customer_name)?.customer_name || null;
    const sessionCustomerPhone = ordersInSession.find(o => o.customer_phone)?.customer_phone || null;

    // Extract unique daily order numbers for company 38
    const orderNums = [...new Set(ordersInSession.map(o => o.order_number).filter(Boolean))].sort((a, b) => a - b);
    const orderNumbersStr = orderNums.join(', ');

    // Calculate totals across all orders in session
    let subtotal = 0;
    const combinedItems = [];
    ordersInSession.forEach(order => {
      const itemsArr = Array.isArray(order.items) ? order.items : (typeof order.items === 'string' ? JSON.parse(order.items || '[]') : []);
      itemsArr.forEach(item => {
        if (item.item_status !== 'cancelled') {
          const basePrice = parseFloat(currency === 'USD' ? (item.price_usd || item.price || 0) : (item.price_inr || item.price || item.item_price || item.basePrice || 0));
          const qty = parseInt(item.quantity || item.qty || 1);
          const discountPercent = parseFloat(item.discount_percentage || 0);
          const discountedPrice = basePrice * (1 - discountPercent / 100);
          const totalVal = discountedPrice * qty;
          subtotal += totalVal;

          combinedItems.push({
            name: item.item_name || item.name,
            quantity: qty,
            basePrice: basePrice,
            discountPercentage: discountPercent,
            discountedPrice: discountedPrice,
            total: totalVal,
            status: order.order_status
          });
        }
      });
    });

    // Tax settings
    const taxSettingsStr = localStorage.getItem('redsorm_tax_fees_settings');
    let sgstPercent = 2.5;
    let cgstPercent = 2.5;
    if (taxSettingsStr) {
      try {
        const parsedTax = JSON.parse(taxSettingsStr);
        sgstPercent = parseFloat(parsedTax.sgst_percentage || 2.5);
        cgstPercent = parseFloat(parsedTax.cgst_percentage || 2.5);
      } catch (e) {}
    }

    const sgstAmount = subtotal * (sgstPercent / 100);
    const cgstAmount = subtotal * (cgstPercent / 100);

    // Festival discount
    let festivalName = '';
    let festivalDiscountPercent = 0;
    let festivalDiscountAmount = 0;
    const activeFestivalStr = localStorage.getItem('redsorm_active_festival');
    if (activeFestivalStr) {
      try {
        const festival = JSON.parse(activeFestivalStr);
        festivalName = festival.festival_name || '';
        festivalDiscountPercent = parseFloat(festival.discount_percentage || 0);
        festivalDiscountAmount = subtotal * (festivalDiscountPercent / 100);
      } catch (e) {}
    }

    const currencySymbol = currency === 'USD' ? '$' : '₹';
    const grandTotal = subtotal + sgstAmount + cgstAmount - festivalDiscountAmount;

    // Resolve absolute QR URLs with robust localStorage cache fallback
    let rawQrs = companyProfile.qr_codes || [];
    let upiId = companyProfile.upi_id || '';

    // Cache fallback
    if (!upiId || !rawQrs || rawQrs.length === 0) {
      try {
        const cachedStr = localStorage.getItem('redsorm_company_profile');
        if (cachedStr) {
          const cached = JSON.parse(cachedStr);
          if (!upiId) upiId = cached.upi_id || '';
          if (!rawQrs || rawQrs.length === 0) rawQrs = cached.qr_codes || [];
        }
      } catch (e) {}
    }

    // Defensive parsing for string type qr_codes
    if (typeof rawQrs === 'string') {
      try {
        rawQrs = JSON.parse(rawQrs);
      } catch (e) {
        rawQrs = [];
      }
    }

    const getBase64Image = async (imgUrl) => {
      if (imgUrl.startsWith('data:')) {
        return imgUrl;
      }
      try {
        const res = await fetch(imgUrl);
        const blob = await res.blob();
        return new Promise((resolve) => {
          const reader = new FileReader();
          reader.onloadend = () => resolve(reader.result);
          reader.onerror = () => resolve(imgUrl);
          reader.readAsDataURL(blob);
        });
      } catch (e) {
        console.warn('[QR Base64] Failed to convert image to base64:', e);
        return imgUrl;
      }
    };

    const resolvedQrs = [];
    if (Array.isArray(rawQrs) && rawQrs.length > 0) {
      for (const qr of rawQrs) {
        const fullUrl = qr.startsWith('http') || qr.startsWith('data:')
          ? qr
          : `${API_URL}${qr.startsWith('/') ? '' : '/'}${qr}`;
        const b64 = await getBase64Image(fullUrl);
        resolvedQrs.push(b64);
      }
    }

    console.log('[Print Session Bill] Resolved UPI ID:', upiId);
    console.log('[Print Session Bill] Resolved QR codes:', resolvedQrs);

    // Generate Invoice HTML
    const invoiceHtml = `
      <div style="font-family: Arial, sans-serif; padding: 40px; color: #333; max-width: 800px; margin: auto; background: #fff;">
        <div style="border-bottom: 2px solid #eee; padding-bottom: 20px;">
          <table style="width: 100%; border-collapse: collapse;">
            <tr>
              <td style="vertical-align: top; text-align: left;">
                <h1 style="margin: 0; color: #1a1a1a; font-size: 28px;">${companyName}</h1>
                <p style="margin: 5px 0; color: #666; font-size: 14px;">Table: ${parseInt(companyProfile?.id) === 37 && (tableNum === 0 || tableNum === '0') ? 'Delivery' : (tableNum === 0 || tableNum === '0') ? 'No Table' : tableNum}</p>
                <p style="margin: 5px 0; color: #888; font-size: 11px;">Session ID: ${sessionId}</p>
                ${sessionCustomerName ? `<p style="margin: 5px 0; color: #1a1a1a; font-size: 14px; font-weight: bold;">Customer: ${sessionCustomerName}${sessionCustomerPhone ? ' &bull; ' + sessionCustomerPhone : ''}</p>` : ''}
                ${orderNumbersStr ? `<p style="margin: 5px 0; color: #1a1a1a; font-size: 16px; font-weight: bold;">Order Number: ${orderNumbersStr}</p>` : ''}
              </td>
              <td style="vertical-align: top; text-align: right;">
                <h2 style="margin: 0; color: #1a1a1a; font-size: 20px;">INVOICE</h2>
                <p style="margin: 5px 0; color: #666; font-size: 14px;">#${invoiceId}</p>
                <p style="margin: 5px 0; color: #666; font-size: 14px;">${dateStr}</p>
              </td>
            </tr>
          </table>
        </div>

        <div style="margin: 30px 0;">
          <table style="width: 100%; border-collapse: collapse;">
            <thead>
              <tr style="background: #f8f9fa; border-bottom: 2px solid #eee;">
                <th style="text-align: left; padding: 12px; font-size: 14px;">Item Description</th>
                <th style="text-align: center; padding: 12px; font-size: 14px;">Status</th>
                <th style="text-align: center; padding: 12px; font-size: 14px;">Qty</th>
                <th style="text-align: right; padding: 12px; font-size: 14px;">Price</th>
                <th style="text-align: right; padding: 12px; font-size: 14px;">Total</th>
              </tr>
            </thead>
            <tbody>
              ${combinedItems.map(item => `
                <tr style="border-bottom: 1px solid #eee;">
                  <td style="padding: 12px; font-size: 14px;">
                    <div style="font-weight: bold;">${String(item.name).trim()}</div>
                    ${item.discountPercentage > 0 ? `<div style="font-size: 11px; color: #e53e3e;">${item.discountPercentage}% Discount Applied</div>` : ''}
                  </td>
                  <td style="padding: 12px; text-align: center; font-size: 13px;">
                    <span style="padding: 3px 8px; border-radius: 4px; font-weight: bold; font-size: 11px; text-transform: uppercase; background: ${item.status === 'delivered' ? '#d1fae5' : '#fef3c7'}; color: ${item.status === 'delivered' ? '#065f46' : '#92400e'};">
                      ${item.status}
                    </span>
                  </td>
                  <td style="padding: 12px; text-align: center; font-size: 14px;">${item.quantity}</td>
                  <td style="padding: 12px; text-align: right; font-size: 14px;">${currencySymbol}${(item.discountPercentage > 0 ? item.discountedPrice : item.basePrice).toFixed(2)}</td>
                  <td style="padding: 12px; text-align: right; font-size: 14px; font-weight: bold;">${currencySymbol}${item.total.toFixed(2)}</td>
                </tr>
              `).join('')}
            </tbody>
          </table>
        </div>

        <table style="width: 100%; margin-top: 30px;">
          <tr>
            <td style="width: 55%;"></td>
            <td style="width: 45%; vertical-align: top;">
              <table style="width: 100%; border-collapse: collapse;">
                <tr>
                  <td style="padding: 5px 0; font-size: 14px; text-align: left;">Subtotal</td>
                  <td style="padding: 5px 0; font-size: 14px; text-align: right;">${currencySymbol}${subtotal.toFixed(2)}</td>
                </tr>
                <tr style="border-top: 1px solid #eee;">
                  <td style="padding: 5px 0; font-size: 14px; text-align: left; padding-top: 5px;">SGST (${sgstPercent}%)</td>
                  <td style="padding: 5px 0; font-size: 14px; text-align: right; padding-top: 5px;">${currencySymbol}${sgstAmount.toFixed(2)}</td>
                </tr>
                <tr>
                  <td style="padding: 5px 0; font-size: 14px; text-align: left;">CGST (${cgstPercent}%)</td>
                  <td style="padding: 5px 0; font-size: 14px; text-align: right;">${currencySymbol}${cgstAmount.toFixed(2)}</td>
                </tr>
                ${festivalDiscountPercent > 0 ? `
                <tr style="color: #e53e3e; font-weight: bold;">
                  <td style="padding: 5px 0; font-size: 14px; text-align: left;">${festivalName} (${festivalDiscountPercent}%)</td>
                  <td style="padding: 5px 0; font-size: 14px; text-align: right;">-${currencySymbol}${festivalDiscountAmount.toFixed(2)}</td>
                </tr>
                ` : ''}
                <tr style="border-top: 2px solid #1a1a1a; font-weight: bold; font-size: 20px;">
                  <td style="padding: 10px 0; text-align: left; padding-top: 10px;">Grand Total</td>
                  <td style="padding: 10px 0; text-align: right; padding-top: 10px; color: #1a1a1a;">${currencySymbol}${grandTotal.toFixed(2)}</td>
                </tr>
              </table>
            </td>
          </tr>
        </table>

        <div style="margin-top: 50px; border-top: 2px solid #eee; padding-top: 20px; text-align: center;">
          <p style="margin: 0; color: #666; font-size: 14px;">Thank you for dining with us!</p>
        </div>
        ${(resolvedQrs && resolvedQrs.length > 0) || upiId ? `
            <div style="margin-top: 30px; border-top: 1px dashed #eee; padding-top: 20px; text-align: center;">
                ${upiId ? `
                    <p style="margin: 0 0 10px 0; color: #1a1a1a; font-size: 14px; font-weight: bold;">
                        UPI ID: <span style="font-family: monospace; background: #f3f4f6; padding: 2px 6px; border-radius: 4px;">${upiId}</span>
                    </p>
                ` : ''}
                ${resolvedQrs && resolvedQrs.length > 0 ? `
                    <div style="margin-top: 15px;">
                        <p style="font-size: 11px; color: #999; text-transform: uppercase; letter-spacing: 1px; margin-bottom: 10px;">Scan QR to Pay</p>
                        <table style="width: 100%; border-collapse: collapse;">
                          <tr>
                            <td style="text-align: center;">
                              ${resolvedQrs.map(qr => `
                                  <div style="display: inline-block; border: 1px solid #eee; padding: 8px; border-radius: 8px; background: #fff; margin: 0 10px;">
                                      <img src="${qr}" style="width: 130px; height: 130px; object-fit: contain;" />
                                  </div>
                              `).join('')}
                            </td>
                          </tr>
                        </table>
                    </div>
                ` : ''}
            </div>
        ` : ''}
      </div>
    `;

    // Pre-preload the resolved QR codes to browser cache for instant rendering
    if (resolvedQrs && resolvedQrs.length > 0) {
      try {
        await Promise.all(resolvedQrs.map(qr => {
          return new Promise((resolve) => {
            const img = new Image();
            img.onload = () => resolve(img);
            img.onerror = () => resolve(null);
            img.src = qr;
          });
        }));
      } catch (e) {}
    }


    // Print using iframe to trigger native browser Print dialog
    try {
      showToast('Opening print view...', 'info');
      const iframe = document.createElement('iframe');
      iframe.style.position = 'fixed';
      iframe.style.right = '0';
      iframe.style.bottom = '0';
      iframe.style.width = '0';
      iframe.style.height = '0';
      iframe.style.border = '0';
      document.body.appendChild(iframe);

      const doc = iframe.contentWindow.document;
      doc.write(`
        <html>
          <head>
            <title>Print Bill</title>
            <style>
              @media print {
                  @page {
                      size: auto;
                      margin: 5mm 10mm;
                  }
                  body {
                      margin: 0;
                      -webkit-print-color-adjust: exact !important;
                      print-color-adjust: exact !important;
                  }
              }
              body {
                font-family: Arial, sans-serif;
                padding: 20px;
                color: #333;
                background: #fff;
                margin: 0;
              }
            </style>
          </head>
          <body>
            ${invoiceHtml}
            <script>
              window.onload = function() {
                window.print();
                setTimeout(function() {
                  window.frameElement.remove();
                }, 500);
              };
            </script>
          </body>
        </html>
      `);
      doc.close();
    } catch (printErr) {
      console.error('Print Fallback Error:', printErr);
      window.print();
    }
  };

  // ── Print QR ─────────────────────────────
  const printQR = (id) => {
    const el = document.getElementById(`qr-modal-${id}`) || document.getElementById(`qr-${id}`);
    if (!el?.parentElement) return showToast(t('serverError'), 'error');

    try {
      const printWindow = window.open('', '_blank', 'width=800,height=600,scrollbars=yes,resizable=yes');

      if (!printWindow) {
        const printContent = document.createElement('div');
        printContent.id = 'print-content';
        printContent.innerHTML = `
          <div style="display:flex;justify-content:center;align-items:center;height:100vh;background:#f9fafb;">
            <div style="background:white;padding:40px;border-radius:12px;box-shadow:0 10px 25px rgba(0,0,0,.1);text-align:center;">
              <h2 style="margin-bottom:20px;color:#333;">Table QR Code</h2>
              <div>${el.parentElement.innerHTML}</div>
              <p style="margin-top:20px;color:#666;font-size:14px;">Scan this QR code to place an order</p>
            </div>
          </div>
        `;
        printContent.style.cssText = 'position:fixed;top:0;left:0;width:100%;height:100%;background:white;z-index:9999';
        document.body.appendChild(printContent);
        window.print();
        setTimeout(() => document.body.removeChild(printContent), 1000);
        showToast('Printed (popup blocked)', 'info');
        return;
      }

      printWindow.document.write(`
        <html>
          <head>
            <title>QR - Table ${id}</title>
            <style>
              body {
                display:flex;
                justify-content:center;
                align-items:center;
                height:100vh;
                background:#f9fafb;
                font-family:Arial, sans-serif;
                margin:0;
                padding:20px;
                box-sizing:border-box;
              }
              .box {
                background:white;
                padding:40px;
                border-radius:12px;
                box-shadow:0 10px 25px rgba(0,0,0,.1);
                text-align:center;
              }
              h2 {
                margin-top:0;
                margin-bottom:20px;
                color:#333;
              }
              p {
                margin-top:20px;
                color:#666;
                font-size:14px;
              }
              .qr-container {
                display:inline-block;
                border:1px solid #e5e7eb;
                border-radius:8px;
                padding:10px;
                background:white;
              }
              @media print {
                body { background:white; }
                .box { box-shadow:none; }
              }
            </style>
          </head>
          <body>
            <div class="box">
              <h2>Table QR Code</h2>
              <div class="qr-container">${el.parentElement.innerHTML}</div>
              <p>Scan this QR code to place an order</p>
            </div>
          </body>
        </html>
      `);

      printWindow.document.close();
      printWindow.focus();

      setTimeout(() => {
        printWindow.print();
        printWindow.close();
      }, 500);

      showToast('QR sent to printer', 'success');
    } catch (err) {
      console.error('Print error:', err);
      showToast('Print failed', 'error');
    }
  };

  // ── Filters ───────────────────────────────────
  const filtered = menuItems.filter((i) => {
    if (!i || !i.name) {
      console.warn('Invalid menu item found:', i);
      return false;
    }
    const s = searchTerm.toLowerCase();
    const nameOk = i.name.toLowerCase().includes(s);
    const catOk = selectedCategory ? i.category === selectedCategory : true;
    return nameOk && catOk;
  });

  console.log('Menu filtering:', {
    totalItems: menuItems.length,
    filteredItems: filtered.length,
    searchTerm,
    selectedCategory
  });

  const filteredTables = (() => {
    if (selectedGroupFilter === 'all') {
      return tables;
    }
    if (selectedGroupFilter === 'non_ac') {
      return tables.filter(t => !t.group_id || t.group_id === null);
    }
    // Convert selectedGroupFilter to number for comparison
    const groupIdNum = parseInt(selectedGroupFilter, 10);
    return tables.filter(t => t.group_id === groupIdNum);
  })();




  // ── Trial Logic ──────────────────────────────
  // ── Trial Logic ──────────────────────────────
  const trialStatus = (() => {
    if (!companyProfile.trial_ends_at) return null;
    // Don't show trial if paid (optional, but usually helpful to hide it). 
    // Actually, let's keep it independent if we want to show "Trial Expired" even if paid? No, if paid, no trial.
    if (companyProfile.has_paid) return null;

    const end = new Date(companyProfile.trial_ends_at);
    const now = new Date();

    // Check strict expiration (time-based) for blocking
    const isExpired = end < now;

    // Calculate calendar days for display (Date-based)
    const endDay = new Date(end);
    endDay.setHours(0, 0, 0, 0);
    const nowDay = new Date(now);
    nowDay.setHours(0, 0, 0, 0);

    const diffTime = endDay - nowDay;
    // Round to ensure integer days
    const diffDays = Math.round(diffTime / (1000 * 60 * 60 * 24));

    if (isExpired) return { expired: true, days: 0 };
    return { expired: false, days: diffDays };
  })();

  // ── Paid Logic ──────────────────────────────
  const paidStatus = (() => {
    if (!companyProfile.has_paid) return null;

    // Format date readable
    const options = { year: 'numeric', month: 'long', day: 'numeric' };
    const end = companyProfile.subscription_end_date ? new Date(companyProfile.subscription_end_date).toLocaleDateString(undefined, options) : 'N/A';

    return {
      plan: companyProfile.subscription_plan || 'Pro',
      endDate: end
    };
  })();

  const updateOrderStatus = useCallback(async (orderId, newStatus) => {
    try {
      const res = await fetch(`${API_URL}/api/orders/${orderId}/status`, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify({ order_status: newStatus }),
      });
      if (res.status === 401 || res.status === 403) {
        logout();
        return;
      }
      const json = await res.json();
      if (json.success) {
        setOrders(prev => prev.map(o => o.id === orderId ? { ...o, order_status: newStatus, updated_at: new Date().toISOString() } : o));
        setSelectedTableOrders(prev => prev.map(o => o.id === orderId ? { ...o, order_status: newStatus, updated_at: new Date().toISOString() } : o));
        showToast('Status updated!');
        // Broadcast status update to KDS in real time
        try {
          const bc = new BroadcastChannel('redsorm_sync_channel');
          bc.postMessage({ type: 'ORDER_STATUS_CHANGED', data: { orderId, newStatus } });
          setTimeout(() => { try { bc.close(); } catch (e) {} }, 1000);
        } catch (e) {}

        // If order finished, reload tables to show they are available
        if (newStatus === 'completed' || newStatus === 'cancelled' || newStatus === 'delivered') {
          loadTables();
        }
      } else {
        showToast(json.message || 'Update failed', 'error');
      }
    } catch {
      showToast(t('actionFailed'), 'error');
    }
  }, [API_URL, token, logout, showToast, t, loadTables, setSelectedTableOrders]);

  const toggleSelectMenuItem = (id) => {
    setSelectedMenuItemIds(prev =>
      prev.includes(id) ? prev.filter(i => i !== id) : [...prev, id]
    );
  };

  const toggleSelectAllMenuItems = () => {
    if (selectedMenuItemIds.length === filtered.length && filtered.length > 0) {
      setSelectedMenuItemIds([]);
    } else {
      setSelectedMenuItemIds(filtered.map(item => item.id));
    }
  };

  const handleBulkDeleteMenuItems = async () => {
    if (selectedMenuItemIds.length === 0) return;

    if (window.confirm(`Are you sure you want to delete ${selectedMenuItemIds.length} items?`)) {
      try {
        const res = await fetch(`${API_URL}/api/menu/bulk-delete`, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            'Authorization': `Bearer ${token}`
          },
          body: JSON.stringify({ ids: selectedMenuItemIds })
        });
        const json = await res.json();
        if (json.success) {
          showToast(json.message || `Deleted ${selectedMenuItemIds.length} items`, 'success');
          setSelectedMenuItemIds([]);
          loadMenu();
        } else {
          showToast(json.message || 'Bulk delete failed', 'error');
        }
      } catch (err) {
        showToast('Network error during bulk delete', 'error');
      }
    }
  };

  const assignTableToOrder = async (orderId, tableId) => {
    try {
      const res = await fetch(`${API_URL}/api/orders/${orderId}/assign-table`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify({ table_id: tableId })
      });
      const json = await res.json();
      if (json.success) {
        showToast('Table assigned successfully!');
        loadOrders();
        loadTables();
      } else {
        showToast(json.message || 'Failed to assign table', 'error');
      }
    } catch (err) {
      showToast('Assignment error', 'error');
    }
  };

  const generateQRCode = (tableId, tableNumber, branchId) => {
    if (typeof window.QRCode === 'undefined') return;

    const el = qrRefs.current[tableId];
    if (el) {
      el.innerHTML = '';
      const branchParam = branchId ? `&branch_id=${branchId}` : '';
      // Redirect directly to customer page for table-based ordering
      new window.QRCode(el, {
        text: `${BASE_URL}/customer.html?table=${tableNumber}&companyId=${authUser?.company_id}${branchParam}`,
        width: 200,
        height: 200,
        colorDark: "#000000",
        colorLight: "#ffffff",
        correctLevel: window.QRCode.CorrectLevel.H
      });
    }
  };

  const showWaitingQRModal = () => {
    setIsWaitingQRModalOpen(true);
    setTimeout(() => {
      const el = document.getElementById('waiting-qr-container');
      if (el && typeof window.QRCode !== 'undefined') {
        el.innerHTML = '';
        new window.QRCode(el, {
          text: `${BASE_URL}/customer.html?table=waiting&companyId=${authUser?.company_id}`,
          width: 200,
          height: 200,
          colorDark: "#000000",
          colorLight: "#ffffff",
          correctLevel: window.QRCode.CorrectLevel.H
        });
      }
    }, 100);
  };

  const renderDeliveryQR = (bId) => {
    const el = document.getElementById('delivery-qr-container');
    if (el && typeof window.QRCode !== 'undefined') {
      el.innerHTML = '';
      let url = `${BASE_URL}/customer.html?table=0&delivery=true&companyId=${authUser?.company_id}`;
      if (bId) {
        url += `&branch_id=${bId}`;
      }
      new window.QRCode(el, {
        text: url,
        width: 200,
        height: 200,
        colorDark: "#000000",
        colorLight: "#ffffff",
        correctLevel: window.QRCode.CorrectLevel.H
      });
    }
  };

  const showDeliveryQRModal = () => {
    const initialBranchId = (selectedBranch && selectedBranch !== 'all') ? parseInt(selectedBranch) : (branches && branches.length > 0 ? branches[0].id : null);
    setQrBranchId(initialBranchId);
    setIsDeliveryQRModalOpen(true);
    setTimeout(() => {
      renderDeliveryQR(initialBranchId);
    }, 100);
  };

  const allowedTabs = ['menu', 'tables', 'groups', 'staff', 'users', 'roles', 'branches', 'settings'].filter(hasTabPermission);

  if (!isLoading && authUser && allowedTabs.length === 0) {
    return (
      <div className="digital-emerald-theme min-h-screen bg-gray-50 flex items-center justify-center p-4">
        <div className="bg-white p-8 rounded-2xl shadow-xl max-w-md w-full text-center border border-gray-100">
          <div className="w-16 h-16 bg-red-100 rounded-full flex items-center justify-center mx-auto mb-4">
            <i className="fas fa-lock text-2xl text-red-600"></i>
          </div>
          <h1 className="text-2xl font-bold text-gray-900 mb-2">{t('unauthorized')}</h1>
          <p className="text-gray-600 mb-6">{t('noPermissionAdmin') || "You do not have permission to access the admin dashboard."}</p>
          <button onClick={() => navigate('/homepage')} className="w-full btn-add-highlight py-2.5 rounded-xl font-bold">
            {t('goBackToHome') || "Go Back to Home"}
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="digital-emerald-theme min-h-screen bg-gray-50 p-2 md:p-6 pt-0 transition-all duration-500 fade-in">
      {/* ── Active Subscription Banner ── */}
      {paidStatus && (
        <div className="bg-emerald-600 text-white py-1 px-4 -mx-2 md:-mx-6 mb-2 shadow-sm flex justify-center items-center text-[10px] md:text-xs">
          <i className="fas fa-crown text-amber-300 mr-2"></i>
          <span className="font-bold tracking-tight uppercase">
            {paidStatus.plan} • {paidStatus.endDate}
          </span>
        </div>
      )}

      {/* ── Trial Banner ── */}
      {trialStatus && !paidStatus && (
        <div className={`${trialStatus.expired ? 'bg-rose-600' : 'bg-emerald-600'} text-white py-1.5 px-4 -mx-2 md:-mx-6 mb-2 shadow-md flex justify-between items-center text-xs md:text-sm`}>
          <div className="flex items-center gap-2">
            <i className={`fas ${trialStatus.expired ? 'fa-hourglass-end' : 'fa-clock'} text-xs md:text-base`}></i>
            <span className="font-bold tracking-tight">
              {trialStatus.expired ? t('trialExpiredBanner') : `${trialStatus.days} ${t('daysLeft')}`}
            </span>
          </div>
          <button onClick={() => navigate('/pricing')} className="bg-white text-emerald-600 px-3 py-1 rounded-lg font-black uppercase text-[10px] md:text-xs shadow-sm hover:bg-gray-50 transition-colors">
            {t('upgrade')}
          </button>
        </div>
      )}

      {/* ── FULL PAGE BLOCKER FOR EXPIRED TRIAL ── */}
      {trialStatus?.expired && !paidStatus && (
        <div className="fixed inset-0 z-50 bg-gray-50 flex flex-col items-center justify-center p-4">
          <div className="bg-white p-8 rounded-2xl shadow-2xl max-w-2xl w-full text-center border border-gray-100">
            <div className="w-24 h-24 bg-red-100 rounded-full flex items-center justify-center mx-auto mb-6 animate-pulse">
              <i className="fas fa-lock text-5xl text-red-600"></i>
            </div>
            <h1 className="text-4xl font-black text-gray-900 mb-4">{t('trialExpiredTitle')}</h1>
            <p className="text-xl text-gray-600 mb-8 max-w-lg mx-auto">
              {t('trialExpiredDesc').replace('{name}', companyProfile.name || 'DineFlow')}
            </p>

            <div className="grid md:grid-cols-3 gap-6 mb-8 text-left max-w-lg mx-auto">
              <div className="flex items-center gap-3">
                <div className="w-8 h-8 rounded-full bg-green-100 flex items-center justify-center flex-shrink-0">
                  <i className="fas fa-check text-green-600"></i>
                </div>
                <span className="font-medium text-gray-700">{t('unlimitedOrders')}</span>
              </div>
              <div className="flex items-center gap-3">
                <div className="w-8 h-8 rounded-full bg-green-100 flex items-center justify-center flex-shrink-0">
                  <i className="fas fa-check text-green-600"></i>
                </div>
                <span className="font-medium text-gray-700">{t('detailedAnalytics')}</span>
              </div>
              <div className="flex items-center gap-3">
                <div className="w-8 h-8 rounded-full bg-green-100 flex items-center justify-center flex-shrink-0">
                  <i className="fas fa-check text-green-600"></i>
                </div>
                <span className="font-medium text-gray-700">{t('support247')}</span>
              </div>
            </div>

            <button
              onClick={() => navigate('/pricing')}
              className="bg-gradient-to-r from-red-600 to-pink-600 hover:from-red-700 hover:to-pink-700 text-white text-xl font-bold py-4 px-10 rounded-xl shadow-lg transform hover:-translate-y-1 transition duration-200 animate-bounce"
            >
              <i className="fas fa-rocket mr-2"></i> {t('upgradeNowToContinue')}
            </button>
            <p className="mt-4 text-gray-400 text-sm">
              {t('securePaymentNote')}
            </p>
          </div>
          {/* Ensure SubscriptionModal can still open on top */}
          <SubscriptionModal isOpen={isSubscriptionOpen} onClose={() => setIsSubscriptionOpen(false)} />
        </div>
      )}
      <style>{`
        @keyframes fade { from { opacity: 0; transform: translateY(10px); } to { opacity: 1; transform: none; } }
        .fade { animation: fade .3s ease-out; }
        .animate-fade-in { animation: fade 0.25s ease-out forwards; }
        .card { transition:all .3s; border-radius: 12px; box-shadow: 0 4px 6px rgba(0, 0, 0, 0.05); }
        .card:hover { transform: translateY(-5px); box-shadow: 0 10px 20px rgba(0, 0, 0, .1); }
        .btn { transition:all .2s; cursor: pointer; border-radius: 8px; font-weight: 500; }
        .btn:active { transform: scale(0.95); }
        .btn-primary { background: linear-gradient(135deg, var(--primary-dark), var(--primary)); color: white; box-shadow: 0 4px 10px rgba(5, 150, 105, 0.3); }
        .btn-primary:hover { background: linear-gradient(135deg, var(--primary), var(--primary-light)); box-shadow: 0 6px 15px rgba(5, 150, 105, 0.4); }
        .btn-secondary { background: #f0fdf4; color: #065f46; border: 1px solid #d1fae5; }
        .btn-secondary:hover { background: #d1fae5; color: #064e3b; }
        .btn-danger { background: #fef2f2; color: #991b1b; border: 1px solid #fee2e2; }
        .btn-danger:hover { background: #fee2e2; color: #7f1d1d; }
        .input-field { width: 100%; padding: 0.5rem 1rem; border-radius: 0.5rem; border: 1px solid #e2e8f0; transition: all 0.2s; background: #ffffff; }
        .input-field:focus { border-color: #10b981; box-shadow: 0 0 0 3px rgba(16, 185, 129, 0.1); outline: none; }
        .modal-overlay { position: fixed; inset: 0; background: rgba(0, 0, 0, 0.15); display: flex; align-items: center; justify-content: center; z-index: 1000; backdrop-filter: blur(8px); }
        body { background-color: #ffffff !important; }
        .modal-content { background: white; border-radius: 1rem; padding: 2rem; width: 90%; max-width: 500px; max-height: 90vh; overflow-y: auto; box-shadow: 0 20px 25px -5px rgba(0, 0, 0, 0.1), 0 10px 10px -5px rgba(0, 0, 0, 0.04); animation: fade 0.3s ease-out; position: relative; z-index: 1001; }
        @media print {
            body * { visibility: hidden; }
            .qr-print-area, .qr-print-area * { visibility: visible; }
            .qr-print-area { position: absolute; left: 0; top: 0; width: 100%; }
        }
        /* Fix button hover visibility */
        .btn-secondary:hover { color: #1f2937 !important; background-color: #e5e7eb; }
        .btn-primary:hover { color: white !important; }
        .btn-danger:hover { color: white !important; background-color: #dc2626 !important; }
        .no-scrollbar::-webkit-scrollbar { display: none; }
        .no-scrollbar { -ms-overflow-style: none; scrollbar-width: none; }
        .btn-add-highlight { background: #ccfbf1; color: #0d9488; border-bottom: 3px solid #0d9488; font-weight: 800; text-transform: uppercase; letter-spacing: 0.5px; transition: all 0.3s; }
        .btn-add-highlight:hover { background: #99f6e4; transform: translateY(-2px); box-shadow: 0 4px 12px rgba(13, 148, 136, 0.2); }
        .btn-add-highlight:active { transform: translateY(0); }
      `}</style>

      <div className={`relative z-40 mb-8 rounded-3xl shadow-lg transition-all duration-500 dashboard-header-banner expand-in ${!companyProfile.banner_url ? 'bg-[#D1F4E8]' : 'text-white'}`}
        style={companyProfile.banner_url ? {
          backgroundImage: `linear-gradient(rgba(0, 0, 0, 0.6), rgba(0, 0, 0, 0.6)), url(${companyProfile.banner_url})`,
          backgroundSize: 'cover',
          backgroundPosition: 'center'
        } : {}}
      >
        <div className={`flex flex-row flex-nowrap justify-between items-center px-2 py-2 md:px-6 md:py-4 gap-2 md:gap-4 admin-header-content w-full ${companyProfile.banner_url ? 'backdrop-blur-lg' : ''}`} style={{ flexFlow: 'row nowrap', display: 'flex' }}>
          {/* LEFT: Branding & Branching - Strict Single Row */}
          <div className="flex items-center gap-2 md:gap-3 flex-shrink min-w-0">
            <div
              className={`flex-shrink-0 cursor-pointer p-0.5 rounded-full transition-all shadow-md ${companyProfile.banner_url ? '' : 'bg-white'}`}
              onClick={() => setIsProfileModalOpen(true)}
            >
              <div className="w-9 h-9 md:w-14 md:h-14 rounded-full overflow-hidden bg-white/20 shadow-inner flex items-center justify-center">
                {companyProfile.logo_url ? (
                  <img src={companyProfile.logo_url} alt="Logo" className="w-full h-full object-contain" />
                ) : (
                  <i className={`fas fa-store text-xs md:text-2xl ${companyProfile.banner_url ? 'text-white' : 'text-emerald-600'}`}></i>
                )}
              </div>
            </div>

            {!currentUser?.branch_id && (
              <div className="relative z-50 branch-dropdown-container">
                <button
                  onClick={() => setShowBranchDropdown(!showBranchDropdown)}
                  className={`flex items-center gap-2 md:gap-3 px-2 py-2 md:px-6 md:py-3.5 rounded-full shadow-lg transition-all duration-300 min-w-0 group hover:scale-105 active:scale-95 ${companyProfile.banner_url ? 'bg-white/10 text-white hover:bg-white/25' : 'bg-white text-gray-800 hover:bg-gray-50'}`}
                >
                  <i className="fas fa-university text-sm md:text-xl opacity-80"></i>
                  <span className="hidden xs:inline text-sm md:text-lg font-bold truncate max-w-[80px] sm:max-w-[150px] md:max-w-[250px]">
                    {selectedBranch ? (branches.find(b => b.id === selectedBranch)?.name || t('branch')) : (t('allBranchesCombined') || 'All Branches')}
                  </span>
                  <i className={`fas fa-chevron-down text-xs md:text-sm transition-transform duration-300 ${showBranchDropdown ? 'rotate-180' : 'opacity-40'}`}></i>
                </button>

                {showBranchDropdown && (
                  <div className="absolute left-0 mt-2 w-64 bg-white rounded-xl shadow-[0_20px_50px_rgba(0,0,0,0.2)] border border-gray-200 py-1.5 z-[100] animate-fade-in max-h-[350px] overflow-y-auto overflow-x-hidden scrollbar-hide" style={{ backgroundColor: '#ffffff', opacity: 1, backdropFilter: 'none' }}>
                    <div className="px-3 py-1.5 border-b border-gray-100 mb-1">
                      <p className="text-[9px] font-bold text-gray-400 uppercase tracking-widest">{t('selectBranch') || 'Select Location'}</p>
                    </div>
                    {branches && branches.length > 1 && (
                    <button
                      onClick={() => { setSelectedBranch(null); setShowBranchDropdown(false); }}
                      className={`w-full text-left px-3 py-2 text-xs hover:bg-emerald-50 transition-all flex items-center gap-2.5 transform hover:translate-x-1 duration-200 ${!selectedBranch ? 'text-emerald-600 font-black bg-emerald-50' : 'text-gray-700 font-bold'}`}
                    >
                      <div className={`w-7 h-7 rounded-lg flex items-center justify-center flex-shrink-0 transition-transform group-hover:scale-110 ${!selectedBranch ? 'bg-emerald-600 text-white shadow-lg shadow-emerald-200' : 'bg-gray-100 text-gray-400'}`}>
                        <i className="fas fa-globe text-[10px]"></i>
                      </div>
                      <span className="truncate">{t('allBranchesCombined') || 'All Branches'}</span>
                      {!selectedBranch && <i className="fas fa-check-circle ml-auto text-emerald-600 text-[10px]"></i>}
                    </button>
                    )}

                    {(branches || []).map(b => (
                      <button
                        key={b.id}
                        onClick={() => { setSelectedBranch(b.id); setShowBranchDropdown(false); }}
                        className={`w-full text-left px-3 py-2 text-xs hover:bg-emerald-50 transition-all flex items-center gap-2.5 transform hover:translate-x-1 duration-200 ${selectedBranch === b.id ? 'text-emerald-600 font-black bg-emerald-50' : 'text-gray-700 font-bold'}`}
                      >
                        <div className={`w-7 h-7 rounded-lg flex items-center justify-center flex-shrink-0 transition-transform group-hover:scale-110 ${selectedBranch === b.id ? 'bg-emerald-600 text-white shadow-lg shadow-emerald-200' : 'bg-gray-100 text-gray-400'}`}>
                          <i className="fas fa-store text-[10px]"></i>
                        </div>
                        <div className="flex flex-col min-w-0">
                          <span className="truncate">{b.name}</span>
                          <span className={`text-[8px] font-bold uppercase tracking-tighter ${b.is_active ? 'text-emerald-600' : 'text-gray-400'}`}>
                            {b.is_active ? 'Active' : 'Inactive'}
                          </span>
                        </div>
                        {selectedBranch === b.id && <i className="fas fa-check-circle ml-auto text-emerald-600 text-[10px]"></i>}
                      </button>
                    ))}
                  </div>
                )}
              </div>
            )}
          </div>

          {/* RIGHT: Global Actions - Strict Single Row */}
          <div className="flex items-center gap-1.5 md:gap-3 flex-shrink-0">
            <button
              onClick={handleGlobalRefresh}
              className={`w-9 h-9 md:w-11 md:h-11 rounded-full flex items-center justify-center shadow-md md:shadow-lg transition-all duration-300 hover:scale-110 active:scale-95 ${companyProfile.banner_url ? 'bg-white/10 text-white hover:bg-white/30' : 'bg-white text-gray-700 hover:text-emerald-600'}`}
              title={t('refresh')}
            >
              <i className="fas fa-redo-alt text-xs md:text-lg"></i>
            </button>

            <div className="relative z-50 language-dropdown-container">
              <button
                onClick={() => setShowLanguageDropdown(!showLanguageDropdown)}
                className={`h-9 px-2 md:h-11 md:px-4 rounded-full flex items-center gap-1 md:gap-2 shadow-md md:shadow-lg transition-all duration-300 hover:scale-110 active:scale-95 ${companyProfile.banner_url ? 'bg-white/10 text-white hover:bg-white/30' : 'bg-white text-gray-700 hover:text-emerald-600'}`}
              >
                <i className="fas fa-globe text-xs md:text-lg"></i>
                <span className="hidden sm:inline text-[10px] md:text-xs font-black uppercase tracking-tight md:tracking-wider">
                  {SUPPORTED_LANGUAGES.find(l => l.code === language)?.label || language}
                </span>
                <span className="inline sm:hidden text-[10px] font-black uppercase">
                  {language.toUpperCase()}
                </span>
                <i className={`fas fa-chevron-down text-[8px] md:text-[10px] ml-1 transition-transform duration-300 ${showLanguageDropdown ? 'rotate-180' : 'opacity-40'}`}></i>
              </button>

              {showLanguageDropdown && (
                <div className="absolute right-0 mt-2 w-64 bg-white rounded-lg shadow-[0_20px_50px_rgba(0,0,0,0.2)] border border-gray-100 py-1 z-[100] animate-fade-in max-h-96 overflow-y-auto" style={{ backgroundColor: '#ffffff', opacity: 1, backdropFilter: 'none' }}>
                  <div className="px-4 py-2 border-b-2 border-gray-100 mb-1 flex justify-between items-center">
                    <p className="text-[10px] font-black text-gray-500 uppercase tracking-widest">{t('selectLanguage') || 'Select Language'}</p>
                    <span className="text-[8px] bg-amber-100 text-amber-700 px-1.5 py-0.5 rounded font-black uppercase tracking-wider">Coming Soon</span>
                  </div>
                  {SUPPORTED_LANGUAGES.map((lang) => {
                    return (
                      <button
                        key={lang.code}
                        onClick={() => {
                          if (language === lang.code) {
                            setShowLanguageDropdown(false);
                          } else {
                            alert('This language is coming soon!');
                          }
                        }}
                        className={`w-full text-left px-4 py-2.5 text-sm transition flex items-center justify-between ${language === lang.code
                          ? 'text-emerald-600 font-bold bg-emerald-50 cursor-pointer'
                          : 'text-gray-400 font-bold opacity-70 cursor-not-allowed hover:bg-gray-50'
                          }`}
                      >
                        <span className="truncate">{lang.label}</span>
                        {language === lang.code && <i className="fas fa-check text-emerald-600 text-xs"></i>}
                        {language !== lang.code && <i className="fas fa-clock text-amber-500 text-[10px]"></i>}
                      </button>
                    );
                  })}
                  {!planFeatures.isPremium && (
                    <div className="border-t border-gray-100 mt-1 pt-2 px-3 pb-2">
                      <p className="text-xs text-gray-500 mb-2">
                        <i className="fas fa-crown text-amber-500 mr-1"></i>
                        {planFeatures.isStarter
                          ? t('upgradeForAllLanguages') || `Upgrade to Premium for all 8 languages`
                          : t('subscribeForLanguages') || `Subscribe to access language support`}
                      </p>
                    </div>
                  )}
                </div>
              )}
            </div>

            <button
              onClick={logout}
              className={`w-9 h-9 md:w-11 md:h-11 rounded-full flex items-center justify-center shadow-md md:shadow-lg transition-all duration-300 hover:scale-110 active:scale-95 ${companyProfile.banner_url ? 'bg-rose-500 text-white hover:bg-rose-600' : 'bg-white text-rose-500 hover:bg-rose-500 hover:text-white'}`}
              title={t('logout')}
            >
              <i className="fas fa-power-off text-xs md:text-lg"></i>
            </button>
          </div>
        </div>




      </div>



      <div className="max-w-7xl mx-auto mb-4 flex gap-2 md:gap-4 border-b overflow-x-auto analytics-tabs-container no-scrollbar expand-in" style={{ animationDelay: '0.1s' }}>
        {['menu', 'tables', 'groups', 'orders_today', 'qr_codes', 'staff', 'users', 'roles', 'branches', 'settings', 'activity_log', 'redeem'].filter(tab => {
          const isNoTablesMode = companyProfile && companyProfile.no_tables_mode;
          // In No Tables Mode: hide tables & groups, show orders_today instead
          if (tab === 'tables' || tab === 'groups') {
            if (isNoTablesMode) return false;
            if (companyProfile && companyProfile.id === 36) return false;
          }
          if (tab === 'orders_today') {
            return !!isNoTablesMode;
          }
          if (tab === 'qr_codes' && (!companyProfile || companyProfile.id !== 36)) {
            return false;
          }
          if (tab === 'redeem') {
            return companyProfile && companyProfile.id === 38;
          }
          if (tab === 'activity_log') {
            const roleStr = (authUser?.role || '').toLowerCase();
            return roleStr === 'admin' || roleStr === 'owner' || roleStr === 'superadmin';
          }
          return tab === 'qr_codes' || hasTabPermission(tab);
        }).map(tab => {
          // Define which tabs require premium
          const premiumTabs = {
            'branches': false // Unlocked so Starter users can manage their 1 allowed branch. Branch limit logic is handled inside BranchesTab.
          };

          const isPremiumTab = premiumTabs[tab];
          const isLocked = isPremiumTab && !planFeatures.isPremium && !planFeatures.isTrial;

          const getTabCount = (tabName) => {
            if (tabName === 'menu') return menuItems.length;
            if (tabName === 'tables') return tables.length;
            if (tabName === 'groups') return tableGroups.length;
            if (tabName === 'staff') return staffList.length;
            if (tabName === 'users') return usersList.filter(u => u.role !== 'customer' && isUserInBranch(u, selectedBranch)).length;
            if (tabName === 'roles') return rolesList.filter(r => isRoleInBranch(r, selectedBranch)).length;
            if (tabName === 'branches') return branches.length;
            if (tabName === 'orders_today') {
              const today = new Date().toISOString().slice(0, 10);
              return orders.filter(o => o.created_at && o.created_at.slice(0, 10) === today && o.order_status !== 'cancelled' && (!selectedBranch || selectedBranch === 'all' || String(o.branch_id) === String(selectedBranch))).length;
            }
            return null;
          };
          const count = getTabCount(tab);

          return (
            <button
              key={tab}
              onClick={() => {
                if (isLocked) {
                  openUpgradeModal();
                } else {
                  setActiveTab(tab);
                }
              }}
              className={`tab-link ${activeTab === tab ? 'active' : ''} ${isLocked ? 'locked' : ''}`}
              title={tab === 'groups' ? (t('tableGroups') || 'Table Groups') : undefined}
            >
              <span className="font-bold tracking-wide flex items-center gap-2">
                <span>{tab === 'orders_today' ? 'Orders' : tab === 'redeem' ? 'Redeem Settings' : tab === 'activity_log' ? (t('activityLog') || 'Activity Log') : tab === 'qr_codes' ? 'QR Codes' : t(tab)}</span>
                {count !== null && (
                  <span className={`inline-flex items-center justify-center min-w-[20px] h-[18px] px-1.5 text-[10px] font-black leading-none rounded ${
                    activeTab === tab
                      ? 'bg-emerald-600 text-white'
                      : 'bg-gray-100 text-gray-500'
                  }`}>
                    {count}
                  </span>
                )}
              </span>
              {isLocked && (
                <i className="fas fa-lock text-amber-500 text-xs animate-pulse"></i>
              )}
            </button>
          );
        })}
      </div>

      <div className="max-w-7xl mx-auto mb-4 md:mb-8">
        <div className="flex items-center justify-between mb-4 md:mb-8 gap-2 admin-content-header expand-in" style={{ animationDelay: '0.2s' }}>
          <h1 className="text-2xl md:text-3xl font-bold text-gray-900 tracking-tight text-center sm:text-left">
            {activeTab === 'menu' && t('menuItems')}
            {activeTab === 'qr_codes' && 'QR Codes'}
            {activeTab === 'tables' && t('allTables')}
            {activeTab === 'staff' && t('attendanceManagement')}
            {activeTab === 'branches' && t('branches')}
            {activeTab === 'roles' && t('roles')}
            {activeTab === 'users' && t('users')}
            {activeTab === 'settings' && t('companySettings')}
            {activeTab === 'groups' && t('tableGroups')}
            {activeTab === 'activity_log' && (t('activityLog') || 'Activity Log')}
            {activeTab === 'redeem' && 'Redeem Settings'}
          </h1>
          <div className="flex items-center justify-end gap-2 sm:gap-3">
            {activeTab === 'menu' && (
              <>
                <button
                   onClick={() => {
                    if (branches.length === 0) {
                      showToast('Please add a branch first in the branches section before adding menu items.', 'error');
                      return;
                    }
                    if (planFeatures.canAddMenuItem(menuItems.length)) {
                      showAddMenuModal();
                    } else {
                      openUpgradeModal();
                    }
                  }}
                  className="btn-add-highlight max-sm:px-3 max-sm:py-1.5 max-sm:text-xs px-6 py-2.5 rounded-lg sm:rounded-xl shadow-sm transition-all flex items-center gap-1.5 sm:gap-2 group border-0 flex-shrink-0 w-auto"
                >
                  <i className="fas fa-plus group-hover:rotate-90 transition-transform"></i>
                  <span className="hidden sm:inline">{t('addItem')}</span>
                </button>
                {!planFeatures.isPremium && menuItems.length >= planFeatures.limits.menuItems * 0.8 && (
                  <span className="text-xs text-amber-600 font-medium bg-amber-50 px-3 py-1.5 rounded-lg border border-amber-200">
                    <i className="fas fa-exclamation-triangle mr-1"></i>
                    {t('itemsUsed', { current: menuItems.length, total: planFeatures.limits.menuItems }) || `${menuItems.length}/${planFeatures.limits.menuItems} items used`}
                  </span>
                )}
              </>
            )}
            {activeTab === 'tables' && (
              <>
                {!planFeatures.isPremium && tables.length >= planFeatures.limits.tables * 0.8 && (
                  <span className="text-sm text-amber-600 font-medium bg-amber-50 px-4 py-2.5 rounded-xl border border-amber-200 flex items-center justify-center h-[44px]">
                    <i className="fas fa-exclamation-triangle mr-1.5"></i>
                    {t('tablesUsed', { current: tables.length, total: planFeatures.limits.tables }) || `${tables.length}/${planFeatures.limits.tables} tables used`}
                  </span>
                )}
                <button
                  onClick={() => {
                    if (branches.length === 0) {
                      showToast('Please add a branch first in the branches section before adding tables.', 'error');
                      return;
                    }
                    if (planFeatures.canAddTable(tables.length)) {
                      showAddTableModal();
                    } else {
                      openUpgradeModal();
                    }
                  }}
                  className="btn-add-highlight max-sm:px-3 max-sm:py-1.5 max-sm:text-xs px-6 py-2.5 rounded-lg sm:rounded-xl shadow-sm transition-all flex items-center gap-1.5 sm:gap-2 group border-0 flex-shrink-0 w-auto"
                >
                  <i className="fas fa-plus"></i> <span className="hidden sm:inline">{t('addTable')}</span>
                </button>
              </>
            )}
            {activeTab === 'groups' && (
              <button onClick={showAddGroupModal} className="btn-add-highlight max-sm:px-2.5 max-sm:py-1.5 max-sm:text-xs px-6 py-2.5 rounded-lg sm:rounded-xl shadow-sm transition-all flex items-center gap-1.5 sm:gap-2 group border-0 font-bold flex-shrink-0 w-auto">
                <i className="fas fa-plus"></i> <span className="hidden sm:inline">{t('addGroup')}</span>
              </button>
            )}
            {activeTab === 'branches' && (
              <>
                {(() => {
                  const branchLimit = companyProfile?.branch_limit || 1;
                  const isTrial = !companyProfile?.has_paid && companyProfile?.trial_ends_at && new Date(companyProfile.trial_ends_at) > new Date();
                  const activeCount = (branches || []).filter(b => b.is_active).length;
                  return (
                    <div className="flex items-center gap-2">
                      <span className={`text-xs px-3 py-2 rounded-xl border font-bold ${isTrial ? 'bg-emerald-50 text-emerald-600 border-emerald-150' : 'bg-amber-50 text-amber-600 border-amber-150'}`}>
                        {isTrial ? 'Trial: Unlimited Branches' : `Limit: ${activeCount} / ${branchLimit} ${branchLimit === 1 ? 'Branch' : 'Branches'}`}
                      </span>
                      {!isTrial && planFeatures.isPremium && (
                        <button
                          type="button"
                          onClick={() => window.dispatchEvent(new CustomEvent('openBuyBranchesModal'))}
                          className="text-xs px-3 py-2 rounded-xl border font-bold bg-purple-50 text-purple-600 border-purple-200 hover:bg-purple-100 transition shadow-sm"
                        >
                          Buy Outlet
                        </button>
                      )}
                    </div>
                  );
                })()}
                <button
                  onClick={() => {
                    window.dispatchEvent(new CustomEvent('openAddBranchModal'));
                  }}
                  className="btn-add-highlight max-sm:px-3 max-sm:py-1.5 max-sm:text-xs px-6 py-2.5 rounded-lg sm:rounded-xl shadow-sm transition-all flex items-center gap-1.5 sm:gap-2 group border-0 font-bold flex-shrink-0 w-auto"
                >
                  <i className="fas fa-plus group-hover:rotate-90 transition-transform"></i>
                  <span className="hidden sm:inline">{t('addBranch') || 'ADD BRANCH'}</span>
                </button>
              </>
            )}
            {activeTab === 'roles' && (
              <button onClick={() => { setCurrentRole({ name: '', permissions: {}, allowed_branches: branches.length === 1 ? [branches[0].id] : [] }); setIsRoleModalOpen(true); }} className="btn-add-highlight max-sm:px-2.5 max-sm:py-1.5 max-sm:text-xs px-6 py-2.5 rounded-lg sm:rounded-xl shadow-sm transition-all flex items-center gap-1.5 sm:gap-2 group border-0 font-bold flex-shrink-0 w-auto">
                <i className="fas fa-plus"></i> <span className="hidden sm:inline">{t('addRole') || 'ADD ROLE'}</span>
              </button>
            )}
            {activeTab === 'users' && (
              <button onClick={() => {
                setCurrentUser({ full_name: '', email: '', phone: '', role_id: '', password: '' });
                setIsUserModalOpen(true);
              }} className="btn-add-highlight max-sm:px-2.5 max-sm:py-1.5 max-sm:text-xs px-6 py-2.5 rounded-lg sm:rounded-xl shadow-sm transition-all flex items-center gap-1.5 sm:gap-2 group border-0 font-bold flex-shrink-0 w-auto">
                <i className="fas fa-plus"></i> <span className="hidden sm:inline">{t('addUser')}</span>
              </button>
            )}
            {activeTab === 'staff' && (
              <div className="flex gap-1.5 sm:gap-2 flex-shrink-0">
                <button
                  onClick={handleShowStaffQR}
                  className="bg-white text-gray-700 max-sm:px-2.5 max-sm:py-1.5 px-4 py-2.5 rounded-lg sm:rounded-xl shadow-sm hover:bg-gray-50 border border-gray-200 transition-all flex items-center gap-1.5 sm:gap-2 font-bold flex-shrink-0 w-auto"
                  title="Show Staff Portal QR"
                >
                  <i className="fas fa-qrcode text-emerald-600 max-sm:text-xs"></i>
                  <span className="hidden sm:inline">Portal QR</span>
                </button>
                <button
                  onClick={() => setIsWorkingHoursModalOpen(true)}
                  className="bg-white text-gray-700 max-sm:px-2.5 max-sm:py-1.5 px-3 py-2.5 rounded-lg sm:rounded-xl shadow-sm hover:bg-gray-50 border border-gray-200 transition-all flex items-center gap-1.5 font-bold flex-shrink-0 w-auto"
                  title="Working Hours & Attendance Settings"
                >
                  <i className="fas fa-cog text-gray-500 max-sm:text-xs"></i>
                  <span className="hidden sm:inline text-xs">Shift Settings</span>
                </button>
                <button onClick={() => {
                  if (branches.length === 0) {
                    showToast('Please add a branch first in the branches section before adding staff.', 'error');
                    return;
                  }
                  setEditingStaff(null);
                  setStaffData({ name: '', role: '', phone: '', pin: '', branch_id: selectedBranch !== 'all' ? selectedBranch : '', salary: 0, daily_rate: 0 });
                  setIsStaffModalOpen(true);
                }} className="btn-add-highlight max-sm:px-2.5 max-sm:py-1.5 max-sm:text-xs px-6 py-2.5 rounded-lg sm:rounded-xl shadow-sm transition-all flex items-center gap-1.5 sm:gap-2 group border-0 font-bold flex-shrink-0 w-auto">
                  <i className="fas fa-user-plus max-sm:text-xs"></i> <span className="hidden sm:inline">{t('addStaff')}</span>
                </button>
              </div>
            )}
            <button onClick={handleRefresh} className="btn btn-secondary max-sm:p-1.5 max-sm:text-xs p-2.5 rounded-lg sm:rounded-xl shadow hover:bg-gray-100 transition-all flex items-center justify-center flex-shrink-0 w-auto" title={t('refresh') || 'Refresh'}>
              <i className="fas fa-sync-alt"></i>
            </button>
          </div>
        </div>

        {activeTab === 'menu' && (
          <div className="max-w-7xl mx-auto">
            {branches.length === 0 ? (
              <div className="text-center py-16 bg-white rounded-xl shadow-sm border border-gray-100 mb-8">
                <div className="flex flex-col items-center gap-2">
                  <i className="fas fa-exclamation-triangle text-4xl text-amber-500 mb-2"></i>
                  <p className="text-lg font-black text-amber-600">Please Add a Branch First</p>
                  <p className="text-sm text-gray-500 font-medium max-w-sm">
                    You need to add at least one branch in the **Branches** tab before you can manage menu items.
                  </p>
                </div>
              </div>
            ) : (
              <>
                <div className="flex flex-col lg:flex-row gap-4 mb-6 justify-between admin-filters-container expand-in" style={{ animationDelay: '0.3s' }}>
              <div className="flex flex-col sm:flex-row gap-2 flex-1">
                <div className="flex items-center gap-3">
                  <input
                    type="checkbox"
                    className="w-5 h-5 text-emerald-600 rounded cursor-pointer border-gray-300 focus:ring-emerald-500"
                    checked={filtered.length > 0 && selectedMenuItemIds.length === filtered.length}
                    onChange={toggleSelectAllMenuItems}
                    title="Select All"
                  />
                  <input placeholder={t('search')} value={searchTerm} onChange={e => setSearchTerm(e.target.value)} className="input-field w-full sm:max-w-xs" />
                </div>
                <div className="flex gap-2">
                  <select
                    value={selectedCategory}
                    onChange={e => {
                      if (e.target.value === 'add_new_category_option') {
                        setIsManageCategoriesModalOpen(true);
                        // Reset selection so it doesn't get stuck on "Add New"
                        setSelectedCategory('');
                      } else {
                        setSelectedCategory(e.target.value);
                      }
                    }}
                    className="input-field flex-1 sm:max-w-xs"
                  >
                    <option value="">{t('allCategories')}</option>
                    {categories.map(c => <option key={c.id || c.name} value={c.name}>{c.name} {c.is_self_service ? '(Self-Serve)' : ''}</option>)}
                    <option value="add_new_category_option" className="font-bold text-emerald-600">➕ {t('addNew') || 'Add New Category'}</option>
                  </select>
                  <button onClick={() => setIsManageCategoriesModalOpen(true)} className="btn btn-secondary px-3 py-2 flex-shrink-0" title="Manage Categories">
                    <i className="fas fa-cog"></i>
                  </button>
                  {planFeatures.features.bulkUpload ? (
                    <>
                      <button
                        onClick={downloadMenuItems}
                        className="bg-green-600 text-white hover:bg-green-700 px-3 py-2 rounded-lg shadow-md transition-all flex items-center justify-center"
                        title={t('downloadExcel') || 'Download Excel'}
                      >
                        <i className="fas fa-file-download text-lg"></i>
                      </button>
                      <button
                        onClick={() => document.getElementById('menu-upload-input').click()}
                        className={`${isUploading ? 'bg-gray-400' : 'bg-emerald-600'} text-white hover:bg-blue-700 px-3 py-2 rounded-lg shadow-md transition-all flex items-center justify-center`}
                        title={t('uploadExcel') || 'Upload Excel'}
                        disabled={isUploading}
                      >
                        <i className={`fas ${isUploading ? 'fa-spinner animate-spin' : 'fa-file-upload'} text-lg`}></i>
                      </button>
                      <input
                        type="file"
                        id="menu-upload-input"
                        accept=".xlsx,.xls"
                        onChange={uploadMenuItems}
                        className="hidden"
                      />
                    </>
                  ) : (
                    <button
                      onClick={() => openUpgradeModal()}
                      className="bg-gradient-to-r from-emerald-500 to-teal-500 text-white px-3 py-2 rounded-lg shadow-md transition-all flex items-center gap-2 hover:from-emerald-600 hover:to-teal-600"
                      title="Upgrade for Bulk Upload/Download"
                    >
                      <i className="fas fa-crown text-sm"></i>
                      <span className="hidden sm:inline text-xs font-medium">Bulk</span>
                    </button>
                  )}
                  {selectedMenuItemIds.length > 0 && (
                    <button
                      onClick={handleBulkDeleteMenuItems}
                      className="bg-red-500 hover:bg-red-600 text-white px-3 py-2 rounded-lg text-xs font-black uppercase tracking-widest transition-all shadow-lg active:scale-95 flex items-center gap-1.5 animate-pulse"
                    >
                      <i className="fas fa-trash-alt"></i>
                      <span>DEL</span>
                      ({selectedMenuItemIds.length})
                    </button>
                  )}
                </div>
              </div>
            </div>
            {isMenuLoading ? <div className="text-center py-10">{t('loading')}...</div> : (
              filtered.length === 0 ? (
                <div className="text-center py-16 bg-white rounded-xl shadow-sm border border-gray-100">
                  <div className="w-20 h-20 bg-red-50 rounded-full flex items-center justify-center mx-auto mb-4">
                    <i className="fas fa-utensils text-3xl text-red-200"></i>
                  </div>
                  <h3 className="text-xl font-normal mb-2" style={{ color: '#dc2626' }}>No Items In Category</h3>
                  <p className="text-sm font-normal max-w-xs mx-auto" style={{ color: '#f87171' }}>Add items to start building your menu</p>
                </div>
              ) : (
                <div className="grid gap-4 expand-in" style={{ gridTemplateColumns: 'repeat(auto-fill, minmax(180px, 1fr))', animationDelay: '0.4s' }}>
                  {filtered.map(item => (
                    <div key={item.id} className={`bg-white rounded-xl overflow-hidden shadow-md hover:shadow-xl transition-all duration-300 border ${selectedMenuItemIds.includes(item.id) ? 'border-emerald-500 ring-2 ring-emerald-500/20' : 'border-gray-100'}`}>
                      {/* Image Section - Reduced Height */}
                      <div className="relative h-24 overflow-hidden bg-gray-100">
                        {/* Checkbox Overlay */}
                        <div className="absolute top-2 left-2 z-10">
                          <input
                            type="checkbox"
                            checked={selectedMenuItemIds.includes(item.id)}
                            onChange={() => toggleSelectMenuItem(item.id)}
                            className="w-4 h-4 text-emerald-600 rounded cursor-pointer border-white shadow-md focus:ring-emerald-500 bg-white/80 backdrop-blur-sm"
                          />
                        </div>

                        {item.image_url ? (
                          <img
                            src={item.image_url}
                            alt={item.name}
                            className={`w-full h-full object-cover transition-transform duration-300 hover:scale-110 ${!item.is_available ? 'opacity-40 grayscale' : ''}`}
                          />
                        ) : (
                          <div className="flex items-center justify-center h-full bg-gray-50">
                            <i className="fas fa-utensils text-3xl text-gray-300"></i>
                          </div>
                        )}
                      </div>

                      {/* Content Section - Compact Padding */}
                      <div className="p-2">
                        {/* Name and Price Row */}
                        <div className="flex justify-between items-start gap-1 mb-2">
                          <div className="flex-1 min-w-0">
                            <div className="flex items-center gap-1.5 mb-0.5">
                              <span className={`w-3.5 h-3.5 rounded-sm border flex items-center justify-center flex-shrink-0 bg-white ${
                                (item.is_veg === 0 || item.is_veg === false || item.is_veg === '0' || item.is_veg === 'false')
                                  ? 'border-red-600'
                                  : 'border-emerald-600'
                              }`} title={(item.is_veg === 0 || item.is_veg === false || item.is_veg === '0' || item.is_veg === 'false') ? 'Non-Veg' : 'Veg'}>
                                <span className={`w-1.5 h-1.5 rounded-full ${
                                  (item.is_veg === 0 || item.is_veg === false || item.is_veg === '0' || item.is_veg === 'false')
                                    ? 'bg-red-600'
                                    : 'bg-emerald-600'
                                }`}></span>
                              </span>
                              <h3 className="font-bold text-sm text-gray-900 line-clamp-1" title={getLocalizedText(item, 'name', language)}>
                                {getLocalizedText(item, 'name', language)}
                              </h3>
                            </div>
                            {language !== 'en' && getLocalizedText(item, 'name', language) !== item.name && (
                              <div className="text-[10px] text-gray-500 truncate">{item.name}</div>
                            )}
                          </div>
                          <div className="flex flex-col items-end">
                            {item.discount_percentage > 0 && (
                              <span className="text-[9px] text-gray-500 line-through">
                                {formatAmount(parseFloat(currency === 'INR' ? (item.price_inr || item.price || item.price_usd || 0) : (item.price_usd || item.price || item.price_inr || 0)))}
                              </span>
                            )}
                            <span className="bg-emerald-600 text-white px-2 py-0.5 rounded text-xs font-black whitespace-nowrap">
                              {item.discount_percentage > 0
                                ? formatAmount(parseFloat(currency === 'INR' ? (item.price_inr || item.price || item.price_usd || 0) : (item.price_usd || item.price || item.price_inr || 0)) * (1 - item.discount_percentage / 100))
                                : formatAmount(parseFloat(currency === 'INR' ? (item.price_inr || item.price || item.price_usd || 0) : (item.price_usd || item.price || item.price_inr || 0)))}
                            </span>
                          </div>
                        </div>

                        {/* Category Tag - Smaller */}
                        <div className="mb-2">
                          <span className="text-xs font-bold text-emerald-700 block">{getLocalizedText(categories.find(c => c.name === item.category), 'name', language, 'category')}</span>
                        </div>

                        {/* Actions Row */}
                        <div className="flex items-center justify-between gap-1">
                          {/* Edit and Delete Buttons */}
                          <div className="flex gap-2">
                            <button
                              onClick={() => editMenuItem(item)}
                              className="flex items-center justify-center w-8 h-8 bg-emerald-50 text-emerald-600 hover:bg-emerald-600 hover:text-white transition-all rounded-lg shadow-sm"
                              title={t('edit')}
                            >
                              <i className="fas fa-edit text-xs"></i>
                            </button>
                            <button
                              onClick={() => deleteMenuItem(item.id)}
                              className="flex items-center justify-center w-8 h-8 bg-rose-50 text-rose-600 hover:bg-rose-600 hover:text-white transition-all rounded-lg shadow-sm"
                              title={t('delete')}
                            >
                              <i className="fas fa-trash text-xs"></i>
                            </button>
                          </div>

                          {/* Availability Toggle - Better Visibility */}
                          <div className="flex items-center gap-1">
                            <label className="relative inline-flex items-center cursor-pointer scale-[0.85] origin-right">
                              <input
                                type="checkbox"
                                className="sr-only peer"
                                checked={!!item.is_available}
                                onChange={() => toggleMenuItemAvailability(item.id, !!item.is_available)}
                              />
                              <div className="w-9 h-5 bg-gray-200 peer-focus:outline-none peer-focus:ring-2 peer-focus:ring-emerald-300 rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-4 after:w-4 after:transition-all peer-checked:bg-emerald-600"></div>
                            </label>
                          </div>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              )
            )}
              </>
            )}
          </div>
        )}

        {activeTab === 'tables' && (
          <div className="max-w-7xl mx-auto">
            {branches.length === 0 ? (
              <div className="text-center py-16 bg-white rounded-xl shadow-sm border border-gray-100 mb-8">
                <div className="flex flex-col items-center gap-2">
                  <i className="fas fa-exclamation-triangle text-4xl text-amber-500 mb-2"></i>
                  <p className="text-lg font-black text-amber-600">Please Add a Branch First</p>
                  <p className="text-sm text-gray-500 font-medium max-w-sm">
                    You need to add at least one branch in the **Branches** tab before you can manage tables and QR codes. Once a branch is added, table "No Table" (table number 0) will be created automatically for it.
                  </p>
                </div>
              </div>
            ) : (
              <>
                <div className="flex flex-col sm:flex-row justify-between items-center mb-6 admin-filters-container expand-in" style={{ animationDelay: '0.3s' }}>
              <div className="flex gap-2 w-full sm:w-auto">
                <select value={selectedGroupFilter} onChange={e => setSelectedGroupFilter(e.target.value)} className="input-field w-full">
                  <option value="all">{t('allGroups')}</option>
                  <option value="non_ac">{t('nonAc')}</option>
                  {tableGroups.map(g => <option key={g.id} value={g.id}>{g.name}</option>)}
                </select>
              </div>
              <div className="flex bg-gray-100 p-1.5 rounded-xl self-start">
                <button
                  onClick={() => setTablePerspective('tables')}
                  className={`px-4 py-1.5 rounded-lg text-xs font-black transition-all ${tablePerspective === 'tables' ? 'bg-white text-emerald-600 shadow-sm' : 'text-gray-500 hover:text-gray-700'}`}
                >
                  <i className="fas fa-th-large mr-2"></i>{t('tables') || 'Tables'}
                </button>
                <button
                  onClick={() => setTablePerspective('waiting')}
                  className={`px-4 py-1.5 rounded-lg text-xs font-black transition-all ${tablePerspective === 'waiting' ? 'bg-white text-orange-600 shadow-sm' : 'text-gray-500 hover:text-gray-700'}`}
                >
                  <i className="fas fa-hourglass-half mr-2"></i>Waiting List
                  {orders.filter(o => o.is_waiting && !['completed', 'cancelled'].includes(o.order_status) && (!selectedBranch || selectedBranch === 'all' || o.branch_id === parseInt(selectedBranch))).length > 0 && (
                    <span className="ml-1 px-1.5 py-0.5 bg-orange-600 text-white text-[8px] rounded-full">
                      {orders.filter(o => o.is_waiting && !['completed', 'cancelled'].includes(o.order_status) && (!selectedBranch || selectedBranch === 'all' || o.branch_id === parseInt(selectedBranch))).length}
                    </span>
                  )}
                </button>
                {(companyProfile?.id === 37 || Number(companyProfile?.id) === 37) && (
                  <button
                    onClick={() => setTablePerspective('delivery')}
                    className={`px-4 py-1.5 rounded-lg text-xs font-black transition-all ${tablePerspective === 'delivery' ? 'bg-white text-indigo-650 shadow-sm' : 'text-gray-500 hover:text-gray-700'}`}
                  >
                    <i className="fas fa-truck mr-2"></i>Delivery
                    {orders.filter(o => o.is_offline_delivery && !['completed', 'cancelled', 'delivered'].includes(o.order_status) && (!selectedBranch || selectedBranch === 'all' || o.branch_id === parseInt(selectedBranch))).length > 0 && (
                      <span className="ml-1 px-1.5 py-0.5 bg-indigo-600 text-white text-[8px] rounded-full">
                        {orders.filter(o => o.is_offline_delivery && !['completed', 'cancelled', 'delivered'].includes(o.order_status) && (!selectedBranch || selectedBranch === 'all' || o.branch_id === parseInt(selectedBranch))).length}
                      </span>
                    )}
                  </button>
                )}
              </div>

              <div className="flex gap-2 mt-2 sm:mt-0">
                {tablePerspective === 'waiting' && (
                  <button onClick={showWaitingQRModal} className="btn bg-orange-600 hover:bg-orange-700 text-white px-4 py-2 rounded-lg shadow-sm flex items-center gap-2">
                    <i className="fas fa-clock"></i> {t('waitingQR') || 'Waiting QR'}
                  </button>
                )}
                {tablePerspective === 'delivery' && (authUser?.company_id === 37 || Number(authUser?.company_id) === 37) && (
                  <button onClick={showDeliveryQRModal} className="btn bg-indigo-600 hover:bg-indigo-700 text-white px-4 py-2 rounded-lg shadow-sm flex items-center gap-2">
                    <i className="fas fa-truck"></i> Delivery QR
                  </button>
                )}
              </div>
            </div>

            {/* Waiting Orders View */}
            {tablePerspective === 'waiting' && (
              <div className="mb-8 bg-orange-50 p-6 rounded-2xl border border-orange-100 shadow-sm expand-in">
                <div className="flex items-center gap-3 mb-4">
                  <div className="w-10 h-10 bg-orange-600 rounded-full flex items-center justify-center text-white">
                    <i className="fas fa-hourglass-half animate-spin-slow"></i>
                  </div>
                  <div>
                    <h3 className="text-lg font-black text-orange-900">Waiting Orders Management</h3>
                    <p className="text-sm text-orange-700 font-medium font-bold">Assign these pre-orders to available tables</p>
                  </div>
                </div>

                {orders.filter(o => o.is_waiting && !['completed', 'cancelled'].includes(o.order_status) && (!selectedBranch || selectedBranch === 'all' || o.branch_id === parseInt(selectedBranch))).length > 0 ? (
                  <div className="grid gap-4" style={{ gridTemplateColumns: 'repeat(auto-fill, minmax(300px, 1fr))' }}>
                    {orders.filter(o => o.is_waiting && !['completed', 'cancelled'].includes(o.order_status) && (!selectedBranch || selectedBranch === 'all' || o.branch_id === parseInt(selectedBranch))).sort((a, b) => a.waiting_number - b.waiting_number).map(order => (
                      <div key={order.id} className="bg-white p-4 rounded-xl border border-orange-200 shadow-sm flex flex-col gap-3">
                        <div className="flex justify-between items-start">
                          <div>
                            <span className="bg-orange-600 text-white text-[10px] font-black px-2 py-0.5 rounded-full uppercase mb-1 inline-block">#{order.waiting_number}</span>
                            <h4 className="font-bold text-gray-900">{order.customer_name || 'Guest User'}</h4>
                          </div>
                          <div className="text-right">
                            <div className="text-xs font-bold text-gray-400 uppercase tracking-tighter">TOTAL</div>
                            <div className="text-lg font-black text-orange-600 leading-none">{formatAmount(order.total_amount_inr)}</div>
                          </div>
                        </div>
                        <div className="bg-orange-50 rounded-lg p-2 text-xs text-orange-800 font-medium">
                          {order.items?.map(it => `${it.quantity}x ${it.item_name}`).join(', ')}
                        </div>
                        <div className="flex gap-2">
                          <select
                            className="input-field py-1 text-xs flex-1 bg-white border-orange-200"
                            onChange={(e) => {
                              if (e.target.value) {
                                assignTableToOrder(order.id, e.target.value);
                              }
                            }}
                            value=""
                          >
                            <option value="">Assign Table...</option>
                             {tables.filter(t => {
                              const counts = getTableCounts(t.table_number, t.branch_id);
                              return (counts.pending + counts.preparing + counts.ready) === 0;
                            }).map(table => (
                              <option key={table.id} value={table.id}>{t('table')} {table.table_number} ({table.table_name || 'No Name'})</option>
                            ))}
                          </select>
                          <button
                            onClick={() => updateOrderStatus(order.id, 'cancelled')}
                            className="p-2 bg-red-50 text-red-600 rounded-lg hover:bg-red-600 hover:text-white transition-all text-xs"
                            title="Cancel Order"
                          >
                            <i className="fas fa-times"></i>
                          </button>
                        </div>
                      </div>
                    ))}
                  </div>
                ) : (
                  <div className="py-8 text-center bg-white/50 rounded-xl border border-dashed border-red-200">
                    <i className="fas fa-clipboard-list text-3xl text-red-200 mb-2"></i>
                    <p className="text-red-600 font-normal text-xl">No Active Waiting Orders</p>
                    <p className="text-red-400 text-sm font-normal mt-1">Customers who scan the Waiting QR code will appear here instantly.</p>
                  </div>
                )}
              </div>
            )}

            {/* Delivery Orders View (Company 37 Only) */}
            {tablePerspective === 'delivery' && (
              <div className="mb-8 bg-indigo-50/50 p-6 rounded-2xl border border-indigo-100 shadow-sm expand-in">
                <div className="flex items-center gap-3 mb-4">
                  <div className="w-10 h-10 bg-indigo-600 rounded-full flex items-center justify-center text-white">
                    <i className="fas fa-truck"></i>
                  </div>
                  <div>
                    <h3 className="text-lg font-black text-indigo-900">Delivery Orders Management</h3>
                    <p className="text-sm text-indigo-700 font-medium font-bold">Track and update Shiprocket deliveries in real-time</p>
                  </div>
                </div>

                {/* Inline Delivery QR Code card */}
                <div className="mb-6 bg-white p-4 rounded-xl border border-indigo-100 shadow-sm flex flex-col sm:flex-row items-center justify-between gap-4">
                  <div className="flex items-center gap-3">
                    <div className="w-12 h-12 bg-indigo-50 text-indigo-600 rounded-xl flex items-center justify-center text-xl flex-shrink-0">
                      <i className="fas fa-qrcode"></i>
                    </div>
                    <div>
                      <h4 className="font-bold text-gray-900 text-sm">Offline Delivery QR Code</h4>
                      <p className="text-xs text-gray-500 font-medium leading-relaxed">Print this QR code to place on flyers, posters, or share with clients so they can order table-free delivery.</p>
                    </div>
                  </div>
                  <button 
                    onClick={showDeliveryQRModal}
                    className="w-full sm:w-auto bg-indigo-600 hover:bg-indigo-700 text-white text-xs font-black px-4 py-2.5 rounded-lg shadow-sm flex items-center justify-center gap-2 transition flex-shrink-0"
                  >
                    <i className="fas fa-print"></i> Print & Share QR
                  </button>
                </div>

                {orders.filter(o => o.is_offline_delivery && !['completed', 'cancelled'].includes(o.order_status) && (!selectedBranch || selectedBranch === 'all' || o.branch_id === parseInt(selectedBranch))).length > 0 ? (
                  <div className="grid gap-4" style={{ gridTemplateColumns: 'repeat(auto-fill, minmax(320px, 1fr))' }}>
                    {orders.filter(o => o.is_offline_delivery && !['completed', 'cancelled'].includes(o.order_status) && (!selectedBranch || selectedBranch === 'all' || o.branch_id === parseInt(selectedBranch))).sort((a, b) => new Date(b.created_at) - new Date(a.created_at)).map(order => (
                      <div key={order.id} className="bg-white p-4 rounded-xl border border-indigo-100 shadow-sm flex flex-col gap-3">
                        <div className="flex justify-between items-start">
                          <div>
                            <span className="bg-indigo-600 text-white text-[10px] font-black px-2 py-0.5 rounded-full uppercase mb-1 inline-block">Order #{order.order_number || order.id}</span>
                            <h4 className="font-bold text-gray-900">{order.customer_name || 'Guest User'}</h4>
                            <p className="text-[10px] text-gray-400 font-mono mt-0.5">{order.customer_phone}</p>
                          </div>
                          <div className="text-right">
                            <div className="text-xs font-bold text-gray-400 uppercase tracking-tighter">TOTAL</div>
                            <div className="text-lg font-black text-indigo-600 leading-none">{formatAmount(order.total_amount_inr)}</div>
                          </div>
                        </div>
                        
                        <div className="bg-indigo-50/50 rounded-lg p-2.5 text-xs text-indigo-900 border border-indigo-100/50">
                          <p className="font-black mb-1">Items:</p>
                          <p className="text-gray-700 mb-2">{order.items?.map(it => `${it.quantity}x ${it.item_name}`).join(', ')}</p>
                          
                          <p className="font-black mb-1">Delivery Address:</p>
                          <p className="text-gray-700">{order.delivery_address}, {order.delivery_city}, {order.delivery_state} - {order.delivery_pincode}</p>
                          
                          {order.delivery_lat && order.delivery_lng && (
                            <a 
                              href={`https://www.google.com/maps/search/?api=1&query=${order.delivery_lat},${order.delivery_lng}`}
                              target="_blank"
                              rel="noopener noreferrer"
                              className="mt-2 text-[10px] text-indigo-600 font-bold hover:underline flex items-center gap-1"
                            >
                              <i className="fas fa-map-marked-alt"></i> View location on Google Maps
                            </a>
                          )}
                        </div>
                        
                        {order.shiprocket_order_id ? (
                          <div className="border border-gray-100 rounded-lg p-2.5 bg-gray-50/50 space-y-1.5 text-[11px] text-gray-600">
                            <div className="flex justify-between">
                              <span>Shiprocket Ord ID:</span>
                              <span className="font-bold text-gray-800">{order.shiprocket_order_id}</span>
                            </div>
                            {order.shiprocket_shipment_id && (
                              <a 
                                href={`https://shiprocket.co/tracking/shipment/${order.shiprocket_shipment_id}`}
                                target="_blank"
                                rel="noopener noreferrer"
                                className="w-full bg-indigo-50 hover:bg-indigo-100 text-indigo-700 font-bold py-1.5 px-3 rounded-lg text-center block transition-all shadow-xs border border-indigo-100"
                              >
                                <i className="fas fa-shipping-fast mr-1"></i> Track on Shiprocket Portal
                              </a>
                            )}
                          </div>
                        ) : (
                          <div className="text-[10px] text-orange-600 font-bold bg-orange-50 border border-orange-100 rounded p-1 text-center">
                            ⚠️ Courier registration pending or failed
                          </div>
                        )}

                        <div className="flex gap-2 items-center">
                          <span className="text-xs font-bold text-gray-600">Status:</span>
                          <select
                            value={order.order_status}
                            onChange={(e) => updateOrderStatus(order.id, e.target.value)}
                            className="input-field py-1 text-xs flex-1 bg-white"
                          >
                            <option value="pending">Pending</option>
                            <option value="preparing">Cooking</option>
                            <option value="ready">Ready</option>
                            <option value="delivered">Delivered</option>
                          </select>
                          <button
                            onClick={() => updateOrderStatus(order.id, 'cancelled')}
                            className="p-2 bg-red-50 text-red-600 rounded-lg hover:bg-red-600 hover:text-white transition-all text-xs"
                            title="Cancel Order"
                          >
                            <i className="fas fa-times"></i>
                          </button>
                        </div>
                      </div>
                    ))}
                  </div>
                ) : (
                  <div className="py-12 text-center bg-white rounded-xl border border-dashed border-indigo-200">
                    <i className="fas fa-truck text-3xl text-indigo-300 mb-2"></i>
                    <p className="text-indigo-600 font-bold text-lg">No Active Delivery Orders</p>
                    <p className="text-indigo-400 text-xs mt-1">Delivery orders submitted by guests will show up here instantly.</p>
                  </div>
                )}
              </div>
            )}
            {/* Tables View */}
            {tablePerspective === 'tables' && (
              isTablesLoading ? <div className="text-center py-10">{t('loading')}...</div> : (
                filteredTables.length === 0 ? (
                  <div className="text-center py-16 bg-white rounded-xl shadow-sm border border-gray-100">
                    <div className="flex flex-col items-center gap-2">
                      <i className="fas fa-chair text-4xl text-red-200"></i>
                      <p className="text-lg font-normal text-red-600">No Tables Found</p>
                      <p className="text-sm text-red-400 font-normal">Click 'Add Table' in the top right to add your first table</p>
                    </div>
                  </div>
                ) : (
                  <div className="grid gap-4 expand-in" style={{ gridTemplateColumns: 'repeat(auto-fill, minmax(240px, 1fr))', animationDelay: '0.4s' }}>
                    {filteredTables.map(tableItem => {
                      const counts = getTableCounts(tableItem.table_number, tableItem.branch_id);
                      const activeCount = counts.pending + counts.preparing + counts.ready + counts.delivered;
                      const isTableOccupied = (tableItem.table_number === 0 || tableItem.table_number === '0')
                        ? false
                        : ((counts.pending + counts.preparing + counts.ready) > 0 || !tableItem.is_available);
                      const tableOrdersForPrint = orders.filter(o => {
                        const matchTable = String(o.table_number) === String(tableItem.table_number);
                        const oBranch = o.branch_id ? String(o.branch_id) : '';
                        const tBranch = tableItem.branch_id ? String(tableItem.branch_id) : '';
                        const matchBranch = !o.branch_id || !tableItem.branch_id || oBranch === tBranch;
                        return matchTable && !o.is_waiting && matchBranch && o.order_status !== 'cancelled';
                      });
                      const latestSessionOrders = (() => {
                        if (!tableOrdersForPrint || tableOrdersForPrint.length === 0) return [];
                        // Ensure we consider newest orders first (orders array is expected to be sorted by created_at desc)
                        const sorted = [...tableOrdersForPrint].sort((a, b) => new Date(b.created_at) - new Date(a.created_at));
                        const chosenSid = sorted[0]?.session_id || 'no-session';
                        // Return all orders that belong to that session id
                        return tableOrdersForPrint.filter(o => (o.session_id || 'no-session') === (chosenSid || 'no-session'));
                      })();
                      const isTable0 = tableItem.table_number === 0 || tableItem.table_number === '0';
                      const hasPrintOrders = isTable0
                        ? latestSessionOrders.length > 0
                        : (isTableOccupied && latestSessionOrders.length > 0);
                      return (
                        <div key={tableItem.id} className="card bg-white p-4 overflow-visible relative shadow-md hover:shadow-xl transition-all duration-300 border border-gray-100 rounded-xl">
                          <button
                            onClick={() => {
                              if (!hasPrintOrders) {
                                showToast('No orders available to print for this table', 'error');
                                return;
                              }
                              const sessionId = latestSessionOrders[0]?.session_id || 'no-session';
                              handlePrintBillForSession(sessionId, latestSessionOrders);
                            }}
                            className={`absolute -top-3 -right-3 h-9 w-9 rounded-full flex items-center justify-center shadow-md z-10 border-4 border-white ${hasPrintOrders ? 'bg-emerald-600 text-white hover:bg-emerald-700' : 'bg-gray-200 text-gray-500'}`}
                            disabled={!hasPrintOrders}
                            title={hasPrintOrders ? 'Print current session orders' : 'No orders to print'}
                          >
                            <i className="fas fa-print text-[12px]"></i>
                          </button>
                          <div className="flex justify-between items-start mb-2">
                            <div>
                              <h3 className="text-lg font-bold flex items-center">
                                {parseInt(companyProfile?.id) === 38 && (tableItem.table_number === 0 || tableItem.table_number === '0') ? 'No Table' : `${t('table')} ${tableItem.table_number}`}
                                {(tableItem.is_active === 0 || tableItem.is_active === false) && (
                                  <span className="text-[9px] bg-red-100 text-red-700 px-1.5 py-0.5 rounded font-black uppercase tracking-wider ml-2">Inactive</span>
                                )}
                              </h3>
                              <div className="text-gray-500 text-sm">{t(tableItem.table_name) || t('noName') || 'No Name'}</div>
                              {language !== 'en' && tableItem.table_name && t(tableItem.table_name) !== tableItem.table_name && (
                                <div className="text-[10px] text-gray-400">{tableItem.table_name}</div>
                              )}
                            </div>
                            <div className="flex flex-col items-end gap-1">
                              <div className={`w-3 h-3 rounded-full ${isTableOccupied ? 'bg-green-500 animate-pulse' : 'bg-gray-300'}`}></div>
                              <div className="flex items-center gap-1.5 mt-1">
                                <span className={`text-[9px] font-black uppercase tracking-tighter ${isTableOccupied ? 'text-rose-600' : 'text-emerald-600'}`}>
                                  {isTableOccupied ? 'Occupied' : 'Available'}
                                </span>
                                <button
                                  onClick={() => {
                                    if (activeCount > 0) {
                                      showToast('Cannot manually change status while there are active orders', 'error');
                                    } else {
                                      toggleTableAvailability(tableItem.id, tableItem.is_available);
                                    }
                                  }}
                                  className={`relative inline-flex h-4 w-8 items-center rounded-full transition-colors focus:outline-none cursor-pointer ${isTableOccupied ? 'bg-rose-500' : 'bg-emerald-500'}`}
                                  title={activeCount > 0 ? "Status managed automatically during active orders" : "Click to manually toggle table status"}
                                >
                                  <span
                                    className={`inline-block h-3 w-3 transform rounded-full bg-white transition-transform ${isTableOccupied ? 'translate-x-1' : 'translate-x-4'}`}
                                  />
                                </button>
                              </div>
                            </div>
                          </div>

                          <div className="flex justify-between items-center gap-2 mb-3">
                            <span className="text-xs bg-gray-100 text-gray-600 px-2.5 py-1 rounded font-bold whitespace-nowrap">
                              {getGroupName(tableItem.group_id, tableGroups.find(g => g.id === tableItem.group_id)?.name)}
                            </span>
                            <span 
                              className="text-xs bg-emerald-50 text-emerald-700 border border-emerald-100 px-2.5 py-1 rounded font-bold whitespace-nowrap min-w-0"
                              title={tableItem.branch_id ? (branches.find(b => b.id === tableItem.branch_id)?.name || `Branch #${tableItem.branch_id}`) : 'All Branches'}
                            >
                              <i className="fas fa-store mr-1 text-[10px]"></i>
                              {(() => {
                                const fullBranchName = tableItem.branch_id ? (branches.find(b => b.id === tableItem.branch_id)?.name || `Branch #${tableItem.branch_id}`) : 'All Branches';
                                return fullBranchName.length > 18 ? fullBranchName.slice(0, 15) + '...' : fullBranchName;
                              })()}
                            </span>
                          </div>

                          <div className="grid grid-cols-4 gap-1 text-center mb-4 text-xs">
                            <div className="bg-yellow-50 p-1 rounded border border-yellow-100">
                              <div className="font-bold text-yellow-700">{counts.pending}</div>
                              <div className="text-yellow-600 text-[10px] uppercase">P</div>
                            </div>
                            <div className="bg-teal-50 p-1 rounded border border-teal-100">
                              <div className="font-bold text-teal-700">{counts.preparing}</div>
                              <div className="text-teal-600 text-[10px] uppercase">Prep</div>
                            </div>
                            <div className="bg-green-50 p-1 rounded border border-green-100">
                              <div className="font-bold text-green-700">{counts.ready}</div>
                              <div className="text-green-600 text-[10px] uppercase">R</div>
                            </div>
                            <div className="bg-gray-50 p-1 rounded border border-gray-100">
                              <div className="font-bold text-gray-700">{counts.delivered}</div>
                              <div className="text-gray-600 text-[10px] uppercase">D</div>
                            </div>
                          </div>

                          <div className="flex gap-2 flex-wrap">
                            <button onClick={() => editTable(tableItem)} className="btn btn-primary flex-1 min-w-[60px] py-1 text-xs sm:text-sm flex items-center justify-center shadow-md">{t('edit')}</button>
                            <button onClick={() => showQRModal(tableItem)} className="btn bg-teal-600 hover:bg-teal-700 text-white flex-1 min-w-[60px] py-1 text-xs sm:text-sm flex items-center justify-center shadow-md">{t('qr')}</button>
                            <button onClick={() => deleteTable(tableItem.id)} className="btn bg-red-500 hover:bg-red-600 text-white flex-1 min-w-[60px] py-1 text-xs sm:text-sm flex items-center justify-center shadow-md transition-colors">{t('delete')}</button>
                          </div>
                          <button onClick={() => showOrdersForTable(tableItem)} className="w-full mt-2 bg-emerald-600 hover:bg-emerald-700 text-white py-2 rounded text-sm disabled:opacity-40 shadow-sm" disabled={activeCount === 0}>{t('showOrders')} ({activeCount})</button>
                        </div>
                      );
                    })}
                  </div>
                )
              )
            )}
              </>
            )}
          </div>
        )
        }

        {activeTab === 'groups' && (
          <div className="max-w-7xl mx-auto">
            {branches.length === 0 ? (
              <div className="text-center py-16 bg-white rounded-xl shadow-sm border border-gray-100 mb-8">
                <div className="flex flex-col items-center gap-2">
                  <i className="fas fa-exclamation-triangle text-4xl text-amber-500 mb-2"></i>
                  <p className="text-lg font-black text-amber-600">Please Add a Branch First</p>
                  <p className="text-sm text-gray-500 font-medium max-w-sm">
                    You need to add at least one branch in the **Branches** tab before you can manage table groups.
                  </p>
                </div>
              </div>
            ) : (
              <>

            {tableGroups.length === 0 ? (
              <div className="text-center py-16 bg-white rounded-xl shadow-sm border border-gray-100">
                <div className="flex flex-col items-center gap-2">
                  <i className="fas fa-layer-group text-4xl text-red-200"></i>
                  <p className="text-lg font-normal text-red-600">No Groups Found</p>
                  <p className="text-sm text-red-400 font-normal">Click 'Add Group' in the top right to add a group</p>
                </div>
              </div>
            ) : (
              <div className="grid gap-3 expand-in" style={{ gridTemplateColumns: 'repeat(auto-fill, minmax(160px, 1fr))', animationDelay: '0.3s' }}>
                {tableGroups.map(g => (
                  <div key={g.id} className="bg-white p-2 flex justify-between items-center group hover:shadow-md transition-all border border-gray-100 rounded-lg">
                    <div className="flex items-center gap-2">
                      <div className="w-7 h-7 rounded-lg bg-emerald-50 text-emerald-600 flex items-center justify-center text-xs">
                        <i className="fas fa-layer-group"></i>
                      </div>
                      <span className="font-bold text-xs text-gray-800 truncate max-w-[60px]" title={g.name}>{g.name}</span>
                    </div>
                    <div className="flex gap-2 opacity-100 sm:opacity-0 sm:group-hover:opacity-100 transition-opacity">
                      <button onClick={() => editGroup(g)} className="text-gray-500 hover:text-emerald-600 p-2 rounded-full hover:bg-gray-50 transition-colors" title={t('edit')}>
                        <i className="fas fa-edit"></i>
                      </button>
                      <button onClick={() => deleteGroup(g.id)} className="text-gray-500 hover:text-red-500 p-2 rounded-full hover:bg-red-50 transition-colors" title={t('delete')}>
                        <i className="fas fa-trash-alt"></i>
                      </button>
                    </div>
                  </div>
                ))}
              </div>
            )}
              </>
            )}
          </div>
        )
        }

        {activeTab === 'orders_today' && (
          <div className="max-w-7xl mx-auto space-y-6">
            {/* Top Card: Direct Order QR Code for No Tables Mode */}
            <div className="bg-white rounded-2xl shadow-sm border border-gray-100 p-6 flex flex-col md:flex-row items-center justify-between gap-6 expand-in">
              <div className="flex flex-col md:flex-row items-center gap-5 text-center md:text-left">
                <div className="bg-emerald-50 p-3 rounded-2xl border border-emerald-100 flex-shrink-0">
                  <img
                    src={`https://api.qrserver.com/v1/create-qr-code/?size=180x180&data=${encodeURIComponent(
                      window.location.origin + `/?company_id=${companyProfile.id}${selectedBranch && selectedBranch !== 'all' ? `&branch_id=${selectedBranch}` : ''}&no_table=1`
                    )}`}
                    alt="Order QR Code"
                    className="w-32 h-32 rounded-lg"
                  />
                </div>
                <div>
                  <span className="bg-emerald-100 text-emerald-800 text-xs font-black px-2.5 py-1 rounded-full uppercase tracking-wider">
                    Counter / Takeaway QR Code
                  </span>
                  <h3 className="text-xl font-bold text-gray-900 mt-2">Order QR</h3>
                  <p className="text-xs text-gray-500 max-w-md mt-1">
                    Customers can scan this QR code to view the menu and place orders directly without selecting a table.
                  </p>
                </div>
              </div>

              <button
                type="button"
                onClick={() => {
                  const qrUrl = `https://api.qrserver.com/v1/create-qr-code/?size=400x400&data=${encodeURIComponent(
                    window.location.origin + `/?company_id=${companyProfile.id}${selectedBranch && selectedBranch !== 'all' ? `&branch_id=${selectedBranch}` : ''}&no_table=1`
                  )}`;
                  const printWin = window.open('', '_blank', 'width=800,height=600');
                  if (printWin) {
                    printWin.document.write(`
                      <html>
                        <head>
                          <title>Print Order QR Code</title>
                          <style>
                            body { display: flex; flex-direction: column; align-items: center; justify-content: center; height: 100vh; font-family: sans-serif; margin: 0; text-align: center; }
                            h2 { font-size: 26px; color: #111827; margin-bottom: 6px; }
                            p { font-size: 15px; color: #6b7280; margin-bottom: 24px; }
                            img { width: 300px; height: 300px; border: 1px solid #e5e7eb; padding: 12px; border-radius: 16px; }
                            @media print { @page { size: auto; margin: 0; } body { margin: 2cm; } }
                          </style>
                        </head>
                        <body>
                          <h2>${companyProfile.name || 'Order Menu'}</h2>
                          <p>Scan QR code to view menu &amp; place order</p>
                          <img src="${qrUrl}" />
                          <script>setTimeout(() => { window.print(); }, 800);</script>
                        </body>
                      </html>
                    `);
                    printWin.document.close();
                  }
                }}
                className="btn btn-primary px-5 py-2.5 text-sm flex items-center gap-2 shadow-md flex-shrink-0"
              >
                <i className="fas fa-print"></i> Print / Download QR
              </button>
            </div>

            {/* Orders Section Header */}
            <div className="bg-white rounded-2xl shadow-sm border border-gray-100 p-6 expand-in" style={{ animationDelay: '0.1s' }}>
              <div className="flex flex-col sm:flex-row items-center justify-between gap-4 mb-6">
                <div>
                  <h3 className="text-xl font-bold text-gray-900">Today's Orders</h3>
                  <p className="text-xs text-gray-500">Live order management for today</p>
                </div>
                <div className="text-xs font-bold text-emerald-700 bg-emerald-50 border border-emerald-200 px-3 py-1.5 rounded-full">
                  {(() => {
                    const todayStr = new Date().toISOString().slice(0, 10);
                    const todaysCount = orders.filter(o => o.created_at && o.created_at.slice(0, 10) === todayStr && o.order_status !== 'cancelled' && (!selectedBranch || selectedBranch === 'all' || String(o.branch_id) === String(selectedBranch))).length;
                    return `Total Orders Today: ${todaysCount}`;
                  })()}
                </div>
              </div>

              {/* Today's Orders Cards Grid */}
              {(() => {
                const todayStr = new Date().toISOString().slice(0, 10);
                const todaysOrders = orders.filter(o => {
                  if (!o.created_at) return false;
                  const isToday = o.created_at.slice(0, 10) === todayStr;
                  const matchesBranch = !selectedBranch || selectedBranch === 'all' || String(o.branch_id) === String(selectedBranch);
                  const isNotCancelled = o.order_status !== 'cancelled';
                  return isToday && matchesBranch && isNotCancelled;
                }).sort((a, b) => new Date(b.created_at) - new Date(a.created_at));

                if (todaysOrders.length === 0) {
                  return (
                    <div className="text-center py-16 bg-gray-50 rounded-xl border border-dashed border-gray-200">
                      <i className="fas fa-receipt text-4xl text-gray-300 mb-3"></i>
                      <p className="text-base font-bold text-gray-600">No orders placed today</p>
                      <p className="text-xs text-gray-400 mt-1">Orders placed via the Counter QR or Online will show up here</p>
                    </div>
                  );
                }

                return (
                  <div className="grid gap-4 grid-cols-1 md:grid-cols-2 lg:grid-cols-3">
                    {todaysOrders.map(ord => {
                      const itemsList = Array.isArray(ord.items) ? ord.items : (typeof ord.items === 'string' ? JSON.parse(ord.items || '[]') : []);
                      return (
                        <div key={ord.id} className="bg-white rounded-xl border border-gray-200 p-4 shadow-sm hover:shadow-md transition-all flex flex-col justify-between">
                          <div>
                            {/* Order Header */}
                            <div className="flex justify-between items-start mb-3 pb-2 border-b border-gray-100">
                              <div>
                                <span className="text-xs font-black text-emerald-700 bg-emerald-50 px-2 py-0.5 rounded border border-emerald-100">
                                  Order #{ord.order_number || ord.id}
                                </span>
                                <div className="text-[11px] text-gray-400 mt-1 flex items-center gap-1">
                                  <i className="far fa-clock"></i>
                                  {new Date(ord.created_at).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}
                                </div>
                              </div>
                              <select
                                value={ord.order_status === 'pending' ? 'preparing' : ord.order_status}
                                onChange={(e) => updateOrderStatus(ord.id, e.target.value)}
                                className={`text-xs font-bold px-2.5 py-1.5 rounded-lg border outline-none cursor-pointer ${
                                  (ord.order_status === 'preparing' || ord.order_status === 'pending') ? 'bg-amber-50 text-amber-700 border-amber-200' :
                                  'bg-emerald-50 text-emerald-700 border-emerald-200'
                                }`}
                              >
                                <option value="preparing">Preparing</option>
                                <option value="ready">Ready</option>
                              </select>
                            </div>

                            {/* Customer Info */}
                            {(ord.customer_name || ord.customer_phone) && (
                              <div className="mb-3 bg-gray-50 p-2.5 rounded-lg text-xs">
                                {ord.customer_name && (
                                  <div className="font-bold text-gray-800 flex items-center gap-1.5">
                                    <i className="fas fa-user text-gray-400 text-[10px]"></i>
                                    {ord.customer_name}
                                  </div>
                                )}
                                {ord.customer_phone && (
                                  <div className="text-gray-500 font-mono text-[11px] mt-0.5 flex items-center gap-1.5">
                                    <i className="fas fa-phone text-gray-400 text-[10px]"></i>
                                    {ord.customer_phone}
                                  </div>
                                )}
                              </div>
                            )}

                            {/* Items List */}
                            <div className="space-y-1 mb-4">
                              {itemsList.map((it, idx) => {
                                const qty = parseInt(it.quantity || it.qty || 1);
                                const rawPrice = parseFloat(it.price_inr || it.price || it.basePrice || it.item_price || 0);
                                const discPercent = parseFloat(it.discount_percentage || 0);
                                const lineBase = rawPrice * qty;
                                const lineFinal = lineBase * (1 - discPercent / 100);
                                return (
                                  <div key={idx} className="flex justify-between items-center text-xs text-gray-700">
                                    <span className="truncate max-w-[180px]">
                                      <span className="font-bold text-emerald-700 mr-1.5">{qty}x</span>
                                      {it.name || it.item_name}
                                    </span>
                                    <span className="font-semibold text-gray-900">
                                      {discPercent > 0 ? (
                                        <>
                                          <span className="text-gray-400 line-through text-[10px] mr-1">{getCurrencySymbol()}{lineBase.toFixed(2)}</span>
                                          <span className="font-bold text-red-600">{getCurrencySymbol()}{lineFinal.toFixed(2)}</span>
                                        </>
                                      ) : (
                                        <span>{getCurrencySymbol()}{lineFinal.toFixed(2)}</span>
                                      )}
                                    </span>
                                  </div>
                                );
                              })}
                            </div>
                          </div>

                          {/* Footer & Print Action */}
                          <div className="pt-3 border-t border-gray-100 flex items-center justify-between">
                            <div>
                              <div className="text-[10px] text-gray-400 uppercase font-black tracking-wider">Total Amount</div>
                              <div className="text-base font-extrabold text-gray-900">
                                {getCurrencySymbol()}{parseFloat(ord.total_amount_inr || ord.total_amount || ord.total_inr || 0).toFixed(2)}
                              </div>
                            </div>
                            <button
                              type="button"
                              onClick={() => handlePrintBillForSession(ord.session_id || 'no-session', [ord])}
                              className="btn bg-emerald-600 hover:bg-emerald-700 text-white px-3 py-1.5 text-xs font-bold flex items-center gap-1.5 shadow-sm rounded-lg"
                            >
                              <i className="fas fa-print"></i> Print Invoice
                            </button>
                          </div>
                        </div>
                      );
                    })}
                  </div>
                );
              })()}
            </div>
          </div>
        )}


        {activeTab === 'settings' && (
          <div className="max-w-4xl mx-auto">
            <div className="bg-white rounded-xl shadow-sm border border-gray-100 p-6 expand-in" style={{ animationDelay: '0.3s' }}>
              <h2 className="text-xl font-bold mb-6">{t('companySettings')}</h2>
              {isProfileLoading ? <div className="text-center py-10">Loading...</div> : (
                <form onSubmit={handleProfileSubmit}>
                  <div className="mb-4">
                    <label className="block text-sm font-medium text-gray-700 mb-1">{t('companyName')}</label>
                    <input name="name" defaultValue={companyProfile.name} required className="input-field" />
                  </div>

                  <div className="mb-4">
                    <div className="flex justify-between items-center mb-1">
                      <label className="block text-sm font-medium text-gray-700">{t('address') || 'Address'}</label>
                      <button
                        type="button"
                        onClick={handleDetectCompanyLocation}
                        disabled={isCompanyLocating}
                        className="text-xs font-bold text-emerald-600 hover:text-emerald-700 flex items-center gap-1 transition disabled:opacity-50"
                      >
                        {isCompanyLocating ? (
                          <>
                            <i className="fas fa-spinner animate-spin"></i> Locating...
                          </>
                        ) : (
                          <>
                            <i className="fas fa-map-marker-alt"></i> Use Current Location
                          </>
                        )}
                      </button>
                    </div>
                    <input name="address" value={companyProfile.address || ''} onChange={e => setCompanyProfile({ ...companyProfile, address: e.target.value })} className="input-field" placeholder="Company Address" />
                  </div>

                  <div className="mb-4">
                    <label className="block text-sm font-medium text-gray-700 mb-1">Company Phone</label>
                    <input name="phone" value={companyProfile.phone || ''} onChange={e => setCompanyProfile({ ...companyProfile, phone: e.target.value })} className="input-field" placeholder="Company Phone" />
                  </div>

                  <div className="grid grid-cols-3 gap-4 mb-4">
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">City</label>
                      <input name="city" value={companyProfile.city || ''} onChange={e => setCompanyProfile({ ...companyProfile, city: e.target.value })} className="input-field" placeholder="City" />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">State</label>
                      <input name="state" value={companyProfile.state || ''} onChange={e => setCompanyProfile({ ...companyProfile, state: e.target.value })} className="input-field" placeholder="State" />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">Pincode</label>
                      <input name="pincode" value={companyProfile.pincode || ''} onChange={e => setCompanyProfile({ ...companyProfile, pincode: e.target.value })} className="input-field" placeholder="Pincode" />
                    </div>
                  </div>

                  <div className="mb-4">
                    <label className="block text-sm font-medium text-gray-700 mb-1">{t('Default Currency') || 'Default Currency'}</label>
                    <select
                      name="default_currency"
                      defaultValue={companyProfile.default_currency || 'INR'}
                      className="input-field"
                    >
                      {Object.entries(currencyMeta).map(([code, meta]) => (
                        <option key={code} value={code}>{meta.symbol} {code} ({meta.name})</option>
                      ))}
                    </select>
                  </div>

                  <div className="mb-4">
                    <label className="block text-sm font-medium text-gray-700 mb-1">{t('logo')}</label>
                    <div className="flex items-center gap-4">
                      {companyProfile.logo_url && (
                        <div className="relative inline-block">
                          <img src={companyProfile.logo_url} alt="Logo" className="h-16 w-16 object-contain rounded border" />
                          <button type="button" onClick={() => setCompanyProfile(prev => ({ ...prev, logo_url: '' }))} className="absolute -top-2 -right-2 bg-red-100 text-red-600 rounded-full w-6 h-6 flex items-center justify-center hover:bg-red-200 shadow-sm border border-red-200 p-0 leading-none">
                            <i className="fas fa-times text-xs"></i>
                          </button>
                        </div>
                      )}
                      <input
                        type="file"
                        accept="image/*"
                        onChange={async (e) => {
                          if (e.target.files[0]) {
                            showToast('Uploading logo...', 'info');
                            const url = await handleFileUpload(e.target.files[0]);
                            if (url) {
                              setCompanyProfile(prev => ({ ...prev, logo_url: url }));
                              showToast('Logo uploaded!', 'success');
                            }
                          }
                        }}
                        className="block w-full text-sm text-gray-500 file:mr-4 file:py-2 file:px-4 file:rounded-full file:border-0 file:text-sm file:font-semibold file:bg-emerald-50 file:text-emerald-700 hover:file:bg-emerald-100"
                      />
                    </div>
                    <input type="hidden" name="logo_url" value={companyProfile.logo_url || ''} />
                  </div>
                  {/* <div className="mb-6">
                    <label className="block text-sm font-medium text-gray-700 mb-1">{t('banner')}</label>
                    <div className="space-y-2">
                      {companyProfile.banner_url && (
                        <div className="relative inline-block w-full">
                          <img src={companyProfile.banner_url} alt="Banner" className="w-full h-32 object-cover rounded border" />
                          <button type="button" onClick={() => setCompanyProfile(prev => ({ ...prev, banner_url: '' }))} className="absolute top-2 right-2 bg-red-100 text-red-600 rounded-full w-8 h-8 flex items-center justify-center hover:bg-red-200 shadow-sm border border-red-200 p-0 leading-none">
                            <i className="fas fa-times text-sm"></i>
                          </button>
                        </div>
                      )}
                      <input
                        type="file"
                        accept="image/*"
                        onChange={async (e) => {
                          if (e.target.files[0]) {
                            showToast('Uploading banner...', 'info');
                            const url = await handleFileUpload(e.target.files[0]);
                            if (url) {
                              setCompanyProfile(prev => ({ ...prev, banner_url: url }));
                              showToast('Banner uploaded!', 'success');
                            }
                          }
                        }}
                        className="block w-full text-sm text-gray-500 file:mr-4 file:py-2 file:px-4 file:rounded-full file:border-0 file:text-sm file:font-semibold file:bg-emerald-50 file:text-emerald-700 hover:file:bg-emerald-100"
                      />
                    </div>
                    <input type="hidden" name="banner_url" value={companyProfile.banner_url || ''} />
                  </div> */}

                  <div className="mb-6 border-t pt-6">
                    <div className="flex justify-between items-center mb-4">
                      <h3 className="text-lg font-bold">{t('billingSettings') || 'Billing & Payment Settings'}</h3>
                      {!isPaymentSettingsUnlocked && (
                        <button
                          type="button"
                          onClick={() => setShowUnlockModal(true)}
                          className="flex items-center gap-2 text-emerald-600 font-bold hover:text-emerald-700 transition"
                        >
                          <i className="fas fa-lock"></i> {t('unlockToEdit') || 'Unlock to Edit'}
                        </button>
                      )}
                      {isPaymentSettingsUnlocked && (
                        <span className="text-green-600 font-bold flex items-center gap-2">
                          <i className="fas fa-lock-open"></i> {t('unlocked') || 'Unlocked'}
                        </span>
                      )}
                    </div>

                    {!isPaymentSettingsUnlocked ? (
                      <div className="bg-gray-50 border-2 border-dashed border-gray-200 rounded-xl p-8 text-center">
                        <div className="w-16 h-16 bg-white rounded-full flex items-center justify-center mx-auto mb-4 shadow-sm">
                          <i className="fas fa-shield-alt text-2xl text-gray-400"></i>
                        </div>
                        <h4 className="font-bold text-gray-800 mb-2">{t('paymentSettingsLocked') || 'Payment Settings Locked'}</h4>
                        <p className="text-sm text-gray-500 mb-6">{t('lockMessage') || 'For security reasons, your QR codes and UPI settings and billing structure are locked. Please unlock using your admin password.'}</p>
                        <button
                          type="button"
                          onClick={() => setShowUnlockModal(true)}
                          className="btn btn-primary px-8 py-2.5 shadow-md flex items-center gap-2 mx-auto"
                        >
                          <i className="fas fa-key"></i> {t('unlockNow') || 'Unlock Now'}
                        </button>
                      </div>
                    ) : (
                      <div className="animate-fade-in">
                        <div className="grid grid-cols-2 gap-4 mb-4">
                          <div>
                            <label className="block text-sm font-medium text-gray-700 mb-1">SGST (%)</label>
                            <input name="sgst" type="number" step="0.01" min="0" defaultValue={companyProfile.sgst || 0} className="input-field" />
                          </div>
                          <div>
                            <label className="block text-sm font-medium text-gray-700 mb-1">CGST (%)</label>
                            <input name="cgst" type="number" step="0.01" min="0" defaultValue={companyProfile.cgst || 0} className="input-field" />
                          </div>
                        </div>

                        <div className="mb-4">
                          <label className="block text-sm font-medium text-gray-700 mb-1">UPI ID for Click-to-Pay</label>
                          <div className="relative">
                            <input
                              name="upi_id"
                              type={showUpiId ? "text" : "password"}
                              defaultValue={companyProfile.upi_id || ''}
                              placeholder="e.g., yourname@okaxis"
                              className="input-field pr-10"
                            />
                            <button
                              type="button"
                              onClick={() => setShowUpiId(!showUpiId)}
                              className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600 transition-colors"
                            >
                              <i className={`fas ${showUpiId ? 'fa-eye-slash' : 'fa-eye'}`}></i>
                            </button>
                          </div>
                          <p className="text-xs text-gray-500 mt-1">Entering a UPI ID will enable GPay, PhonePe, and Paytm buttons on the bill.</p>
                        </div>

                        <div className="mb-4">
                          <div className="flex justify-between items-center mb-2">
                            <label className="block text-sm font-medium text-gray-700">{t('paymentQRCodes') || 'Payment QR Codes'} (Max 3)</label>
                            {(Array.isArray(companyProfile.qr_codes) && companyProfile.qr_codes.length > 0) && (
                              <button
                                type="button"
                                onClick={() => setShowQrCodes(!showQrCodes)}
                                className="text-xs text-emerald-600 hover:text-emerald-700 font-extrabold flex items-center gap-1 transition-all"
                              >
                                <i className={`fas ${showQrCodes ? 'fa-eye-slash' : 'fa-eye'}`}></i>
                                {showQrCodes ? 'Mask QR Codes' : 'Show QR Codes'}
                              </button>
                            )}
                          </div>
                          <div className="flex flex-wrap gap-4">
                            {(Array.isArray(companyProfile.qr_codes) ? companyProfile.qr_codes : []).map((qrUrl, idx) => (
                              <div key={idx} className="relative w-32 h-32 border rounded overflow-hidden group">
                                <img
                                  src={qrUrl}
                                  alt={`QR ${idx + 1}`}
                                  className={`w-full h-full object-cover transition-all duration-300 ${!showQrCodes ? 'blur-md grayscale' : ''}`}
                                />
                                {!showQrCodes && (
                                  <div className="absolute inset-0 bg-black/40 flex flex-col items-center justify-center text-white text-[10px] font-black tracking-wider pointer-events-none select-none">
                                    <i className="fas fa-eye-slash mb-1 text-sm text-gray-200"></i>
                                    <span>MASKED</span>
                                  </div>
                                )}
                                <button
                                  type="button"
                                  onClick={() => {
                                    const newQrs = [...(companyProfile.qr_codes || [])];
                                    newQrs.splice(idx, 1);
                                    setCompanyProfile(prev => ({ ...prev, qr_codes: newQrs }));
                                  }}
                                  className="absolute top-1 right-1 bg-red-500 text-white rounded-full w-6 h-6 flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity"
                                >
                                  &times;
                                </button>
                              </div>
                            ))}
                            {(!companyProfile.qr_codes || companyProfile.qr_codes.length < 3) && (
                              <label className="w-32 h-32 border-2 border-dashed border-gray-300 rounded flex flex-col items-center justify-center cursor-pointer hover:bg-gray-50 text-gray-500">
                                <i className="fas fa-plus text-2xl mb-1"></i>
                                <span className="text-xs">{t('addQR') || 'Add QR'}</span>
                                <input
                                  type="file"
                                  accept="image/*"
                                  className="hidden"
                                  onChange={async (e) => {
                                    if (e.target.files[0]) {
                                      showToast('Uploading QR...', 'info');
                                      const url = await handleFileUpload(e.target.files[0]);
                                      if (url) {
                                        const currentQrs = Array.isArray(companyProfile.qr_codes) ? companyProfile.qr_codes : [];
                                        setCompanyProfile(prev => ({ ...prev, qr_codes: [...currentQrs, url] }));
                                        showToast('QR Code added!', 'success');
                                      }
                                    }
                                  }}
                                />
                              </label>
                            )}
                          </div>
                        </div>
                      </div>
                    )}

                    <div className="mb-4 border-t pt-4">
                      <h4 className="font-bold text-gray-800 mb-3">{t('festivalOffer') || 'Festival Offer'}</h4>
                      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                        <div>
                          <label className="block text-sm font-medium text-gray-700 mb-1">{t('festivalName') || 'Festival Name'}</label>
                          <input name="festival_name" defaultValue={companyProfile.festival_name} placeholder="e.g., Diwali, Christmas" className="input-field" />
                        </div>
                        <div>
                          <label className="block text-sm font-medium text-gray-700 mb-1">{t('festivalDiscount') || 'Festival Discount (%)'}</label>
                          <input name="festival_discount" type="number" step="0.01" min="0" defaultValue={companyProfile.festival_discount || 0} className="input-field" />
                        </div>
                      </div>
                    </div>
                    {/* No Tables Mode Toggle */}
                    <div className="mb-4 border-t pt-4">
                      <h4 className="font-bold text-gray-800 mb-2">No Tables Mode</h4>
                      <p className="text-xs text-gray-500 mb-3">Enable this for counter/takeaway restaurants that don't use table assignments. Hides Tables &amp; Groups tabs and shows an Orders Today view with a scan QR for customers instead.</p>
                      <label className="flex items-center gap-3 cursor-pointer select-none">
                        <div
                          className={`relative inline-flex w-11 h-6 items-center rounded-full transition-colors duration-200 ${companyProfile.no_tables_mode ? 'bg-emerald-500' : 'bg-gray-300'}`}
                          onClick={() => setCompanyProfile(prev => ({ ...prev, no_tables_mode: !prev.no_tables_mode }))}
                        >
                          <span className={`inline-block w-4 h-4 bg-white rounded-full shadow transition-transform duration-200 ${companyProfile.no_tables_mode ? 'translate-x-6' : 'translate-x-1'}`} />
                        </div>
                        <span className="text-sm font-semibold text-gray-700">
                          {companyProfile.no_tables_mode ? '✅ Enabled — Counter/Takeaway mode (no table assignment)' : '⛔ Disabled — Standard table-based mode'}
                        </span>
                      </label>
                    </div>

                    {/* Customer Info Collection Toggle - not for company 38 */}
                    {parseInt(companyProfile?.id) !== 38 && (
                      <div className="mb-4 border-t pt-4">
                        <h4 className="font-bold text-gray-800 mb-2">Customer Info Collection</h4>
                        <p className="text-xs text-gray-500 mb-3">When enabled, customers must enter their name and phone number before viewing the menu (after scanning QR code). Phone number is used as a unique identifier to track order history.</p>
                        <label className="flex items-center gap-3 cursor-pointer select-none">
                          <div
                            className={`relative inline-flex w-11 h-6 items-center rounded-full transition-colors duration-200 ${companyProfile.collect_customer_info !== false ? 'bg-emerald-500' : 'bg-gray-300'}`}
                            onClick={() => setCompanyProfile(prev => ({ ...prev, collect_customer_info: prev.collect_customer_info === false ? true : false }))}
                          >
                            <span className={`inline-block w-4 h-4 bg-white rounded-full shadow transition-transform duration-200 ${companyProfile.collect_customer_info !== false ? 'translate-x-6' : 'translate-x-1'}`} />
                          </div>
                          <span className="text-sm font-semibold text-gray-700">
                            {companyProfile.collect_customer_info !== false ? '✅ Enabled — Collecting name & phone on scan' : '⛔ Disabled — No gate, menu opens directly'}
                          </span>
                        </label>
                      </div>
                    )}

                    {/* Inventory Auto-Deduction Mode Toggle - for Company 39 */}
                    {(parseInt(companyProfile?.id || companyProfile?.company_id || authUser?.company_id, 10) === 39) && (
                      <div className="mb-4 border-t pt-4">
                        <h4 className="font-bold text-gray-800 mb-2">Inventory Auto-Deduction Mode</h4>
                        <p className="text-xs text-gray-500 mb-3">When enabled, orders placed will automatically deduct stock quantities for mapped inventory items in your Ingredients list.</p>
                        <label className="flex items-center gap-3 cursor-pointer select-none">
                          <div
                            className={`relative inline-flex w-11 h-6 items-center rounded-full transition-colors duration-200 ${companyProfile.auto_deduct_inventory ? 'bg-emerald-500' : 'bg-gray-300'}`}
                            onClick={() => setCompanyProfile(prev => ({ ...prev, auto_deduct_inventory: !prev.auto_deduct_inventory }))}
                          >
                            <span className={`inline-block w-4 h-4 bg-white rounded-full shadow transition-transform duration-200 ${companyProfile.auto_deduct_inventory ? 'translate-x-6' : 'translate-x-1'}`} />
                          </div>
                          <span className="text-sm font-semibold text-gray-700">
                            {companyProfile.auto_deduct_inventory ? '✅ Enabled — Automatic stock deduction on orders' : '⛔ Disabled — Standard manual inventory mode'}
                          </span>
                        </label>
                      </div>
                    )}
                    {/* Company Location Restriction */}
                    <div className="mb-4 pt-4 border-t border-gray-100">
                      <div className="rounded-xl border border-blue-100 bg-blue-50/50 p-4">
                        <div className="flex items-start justify-between gap-3 mb-3">
                          <div>
                            <h4 className="font-bold text-gray-800 text-sm">QR Code Scanning Restriction (Company Level)</h4>
                            <p className="text-xs text-gray-500 mt-0.5">Restrict all QR codes (menu, staff portal, tables) to only work when scanned within a specific radius of this location. (Applies to all branches unless overridden at branch level).</p>
                          </div>
                          <i className="fas fa-location-crosshairs text-blue-500 text-base mt-0.5"></i>
                        </div>
                        <div className="grid grid-cols-2 gap-3 mb-3">
                          <label className="flex items-center gap-2 rounded-lg bg-white border border-gray-200 p-3 cursor-pointer col-span-2">
                            <input
                              type="checkbox"
                              className="w-4 h-4 accent-blue-600"
                              checked={!!companyProfile.master_qr_location_restriction_enabled}
                              onChange={e => setCompanyProfile(prev => ({ 
                                ...prev, 
                                master_qr_location_restriction_enabled: e.target.checked,
                                attendance_location_restriction_enabled: e.target.checked 
                              }))}
                            />
                            <span>
                              <span className="block text-sm font-semibold text-gray-800">Enable Global QR Restriction</span>
                              <span className="block text-xs text-gray-500">Requires customers and staff to be at the location to scan QRs or punch in/out.</span>
                            </span>
                          </label>
                          <div className="col-span-2">
                            <label className="block text-xs font-medium text-gray-700 mb-1">Global QR radius (meters)</label>
                            <input
                              type="number" min="1" max="5000"
                              value={companyProfile.master_qr_radius_meters || 200}
                              onChange={e => setCompanyProfile(prev => ({ 
                                ...prev, 
                                master_qr_radius_meters: e.target.value,
                                attendance_radius_meters: e.target.value 
                              }))}
                              className="input-field w-full"
                              disabled={!companyProfile.master_qr_location_restriction_enabled}
                            />
                          </div>
                        </div>
                        <div className="flex justify-between items-center mb-1 mt-2">
                          <label className="block text-xs font-medium text-gray-700">Company Location Coordinates</label>
                          <button
                            type="button"
                            onClick={handleDetectCompanyLocation}
                            disabled={isCompanyLocating}
                            className="text-xs font-bold text-emerald-600 hover:text-emerald-700 flex items-center gap-1 transition disabled:opacity-50"
                          >
                            {isCompanyLocating ? (
                              <><i className="fas fa-spinner animate-spin"></i> Locating...</>
                            ) : (
                              <><i className="fas fa-map-marker-alt"></i> Use Current Location</>
                            )}
                          </button>
                        </div>
                        <div className="p-2.5 bg-white rounded-lg border border-gray-200 flex items-center gap-2 mt-2">
                          <i className={`fas fa-map-pin text-xs ${companyProfile.qr_latitude && companyProfile.qr_longitude ? 'text-blue-500' : 'text-gray-300'}`}></i>
                          <span className="text-xs text-gray-600">
                            <span className="font-semibold">Coordinates: </span>
                            {companyProfile.qr_latitude && companyProfile.qr_longitude
                              ? `${Number(companyProfile.qr_latitude).toFixed(6)}, ${Number(companyProfile.qr_longitude).toFixed(6)}`
                              : <span className="text-amber-600">Not set — click "Use Current Location" above</span>}
                          </span>
                        </div>
                      </div>
                    </div>

                  </div>
                  <button type="submit" className="btn btn-primary px-6 py-2">{t('saveChanges')}</button>
                </form>
              )}
            </div>
          </div>
        )
        }

        {activeTab === 'qr_codes' && (
          <div className="max-w-7xl mx-auto space-y-8">
            <div className="bg-white rounded-xl shadow-lg border border-gray-100 p-8 flex flex-col items-center justify-center expand-in">
              <h2 className="text-2xl font-bold mb-2">Self-Service QR Code</h2>
              <p className="text-gray-500 mb-8 text-center max-w-md">
                Customers can scan this QR code to place orders without a specific table. 
                They will receive a queue number upon payment.
              </p>
              
              <div id="company36-qr-container" className="bg-white p-4 rounded-xl border shadow-sm mb-6 inline-block">
                <img 
                  src={`https://api.qrserver.com/v1/create-qr-code/?size=300x300&data=${encodeURIComponent(window.location.origin + `/?company_id=36&branch_id=${selectedBranch || ''}`)}`} 
                  alt="Branch QR" 
                  className="w-64 h-64"
                />
              </div>

              <button 
                onClick={() => {
                  const printWindow = window.open('', '_blank', 'width=800,height=600');
                  if (printWindow) {
                    printWindow.document.write(`
                      <html>
                        <head><title>Print QR</title><style>
                          body { display: flex; flex-direction: column; align-items: center; justify-content: center; height: 100vh; font-family: sans-serif; margin: 0; }
                          h2 { font-size: 24px; margin-bottom: 10px; }
                          p { font-size: 16px; margin-bottom: 30px; color: #555; }
                          img { width: 300px; height: 300px; }
                          @media print { @page { size: auto; margin: 0; } body { margin: 1.6cm; } }
                        </style></head>
                        <body>
                          <h2>Self-Service Orders</h2>
                          <p>Scan to place your order</p>
                          <img src="https://api.qrserver.com/v1/create-qr-code/?size=300x300&data=${encodeURIComponent(window.location.origin + `/?company_id=36&branch_id=${selectedBranch || ''}`)}" />
                          <script>setTimeout(() => { window.print(); }, 1000);</script>
                        </body>
                      </html>
                    `);
                    printWindow.document.close();
                  }
                }} 
                className="bg-emerald-600 text-white px-8 py-3 rounded-xl font-bold shadow hover:bg-emerald-700 transition flex items-center gap-2"
              >
                <i className="fas fa-print"></i> Print QR Code
              </button>
            </div>
          </div>
        )}

        {activeTab === 'staff' && (
          <div className="space-y-8">
            <div className="grid grid-cols-1 gap-8">
              <div className="bg-white rounded-xl shadow-sm border border-gray-100 overflow-hidden expand-in" style={{ animationDelay: '0.1s' }}>
                <div className="overflow-x-auto">
                  <table className="w-full text-left">
                    <thead className="bg-gray-50 border-b border-gray-100">
                      <tr>
                        <th className="p-4 font-semibold text-gray-600">{t('staffName')}</th>
                        <th className="p-4 font-semibold text-gray-600">{t('role')}</th>
                        <th className="p-4 font-semibold text-gray-600">{t('phone')}</th>
                        <th className="p-4 font-semibold text-gray-600">{t('pin')}</th>
                        <th className="p-4 font-semibold text-gray-600">{t('salary') || 'Salary'}</th>
                        <th className="p-4 font-semibold text-gray-600">{t('dailyRate') || 'Daily Rate'}</th>
                        <th className="p-4 font-semibold text-gray-600">{t('branch')}</th>
                        <th className="p-4 font-semibold text-gray-600 text-right">{t('actions')}</th>
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-gray-100 text-sm">
                      {staffList.filter(s => !selectedBranch || selectedBranch === 'all' || s.branch_id === parseInt(selectedBranch) || s.branch_id === null || s.branch_id === undefined).length === 0 ? (
                        <tr>
                          <td colSpan="8" className="px-6 py-12 text-center text-red-600 font-normal">
                            <div className="flex flex-col items-center gap-2">
                              <i className="fas fa-search text-4xl text-red-200"></i>
                              <p className="font-normal">No Staff Found</p>
                            </div>
                          </td>
                        </tr>
                      ) : (
                        staffList.filter(s => !selectedBranch || selectedBranch === 'all' || s.branch_id === parseInt(selectedBranch) || s.branch_id === null || s.branch_id === undefined).map((s, idx) => (
                          <tr key={s.id || idx} className="hover:bg-gray-50 transition-colors">
                            <td className="p-4 font-medium text-gray-800">{s.name}</td>
                            <td className="p-4">
                              <span className={`px-2 py-1 rounded-full text-xs font-normal ${s.role === 'admin' ? 'bg-purple-100 text-purple-800' :
                                s.role === 'manager' ? 'bg-blue-100 text-blue-800' :
                                  'bg-emerald-100 text-emerald-800'
                                }`}>
                                {t(s.role) || s.role}
                              </span>
                            </td>
                            <td className="p-4 text-gray-600">{s.phone}</td>
                            <td className="p-4 font-mono font-black text-emerald-600 tracking-widest">
                              {visiblePins[s.id] ? s.pin : '••••'}
                              <button
                                onClick={() => togglePin(s.id)}
                                className="ml-2 text-gray-400 hover:text-emerald-500 transition-colors"
                              >
                                <i className={`fas fa-eye${visiblePins[s.id] ? '-slash' : ''}`}></i>
                              </button>
                            </td>
                            <td className="p-4 text-gray-600 font-medium">{formatAmount(s.salary || 0)}</td>
                            <td className="p-4 text-gray-600 font-medium">{formatAmount(s.daily_rate || 0)}</td>
                            <td className="p-4">
                              {(() => {
                                const branchName = branches.length === 0
                                  ? (t('noBranch') || 'No branch')
                                  : (s.branch_name || (s.branch_id ? (branches.find(b => b.id === s.branch_id)?.name || t('unknown')) : t('noBranch') || 'No branch'));
                                return (
                                  <span className="bg-gray-100 text-gray-700 text-xs px-2 py-0.5 rounded font-medium border border-gray-200">
                                    {branchName}
                                  </span>
                                );
                              })()}
                            </td>
                            <td className="p-4 text-right">
                              <button
                                onClick={() => {
                                  setEditingStaff(s);
                                  setStaffData({
                                    name: s.name,
                                    role: s.role,
                                    phone: s.phone,
                                    pin: s.pin,
                                    branch_id: s.branch_id,
                                    salary: s.salary || 0,
                                    daily_rate: s.daily_rate || 0
                                  });
                                  setIsStaffModalOpen(true);
                                }}
                                className="text-emerald-600 hover:text-emerald-800 mr-3 transition"
                                title={t('editStaff') || "Edit Staff"}
                              >
                                <i className="fas fa-edit"></i>
                              </button>
                              <button
                                onClick={() => deleteStaff(s.id)}
                                className="text-red-500 hover:text-red-700 transition"
                                title={t('deleteStaff')}
                              >
                                <i className="fas fa-trash-alt"></i>
                              </button>
                            </td>
                          </tr>
                        )))}

                    </tbody>
                  </table>
                </div>
              </div>

              {/* Attendance & Leaves Management Link */}
              <div className="mt-8 bg-white rounded-xl shadow-sm border border-gray-100 p-8 text-center expand-in" style={{ animationDelay: '0.2s' }}>
                <div className="w-16 h-16 bg-emerald-50 rounded-full flex items-center justify-center mx-auto mb-4">
                  <i className="fas fa-clipboard-check text-2xl text-emerald-500"></i>
                </div>
                <h2 className="text-xl font-bold mb-2">{t('manageAttendanceTitle')}</h2>
                <p className="text-gray-500 mb-6 max-w-md mx-auto">
                  {t('manageAttendanceDesc')}
                </p>
                <a href="/staff" target="_blank" className="btn btn-primary px-6 py-3 shadow-lg hover:shadow-xl transform hover:-translate-y-1 transition-all inline-flex items-center gap-2">
                  {t('openAttendanceManager')} <i className="fas fa-external-link-alt text-xs"></i>
                </a>
              </div>

            </div>
          </div>
        )
        }

        {
          isStaffQRModalOpen && (
            <div className="fixed inset-0 modal-overlay flex items-center justify-center p-4" onClick={() => setIsStaffQRModalOpen(false)}>
              <div className="bg-white rounded-lg max-w-sm w-full p-6 text-center modal-content" onClick={(e) => e.stopPropagation()}>
                <h3 className="text-xl font-bold mb-4">{t('staffPortalQr')}</h3>
                <div className="bg-white p-4 inline-block rounded-lg shadow-inner mb-4 border">
                  <img
                    src={`https://api.qrserver.com/v1/create-qr-code/?size=200x200&data=${encodeURIComponent(`${BASE_URL}/staff`)}`}
                    alt="Staff Portal QR"
                    className="w-48 h-48"
                  />
                </div>
                <p className="text-sm text-gray-500 mb-4">{t('scanToAccessStaff')}</p>
                <p className="text-xs text-gray-400 break-all mb-4">{`${BASE_URL}/staff`}</p>
                <button onClick={() => setIsStaffQRModalOpen(false)} className="btn btn-secondary w-full">{t('close')}</button>
              </div>
            </div>
          )
        }

        {/* Working Hours & Attendance Settings Modal */}
        {isWorkingHoursModalOpen && (
          <div className="fixed inset-0 modal-overlay flex items-center justify-center p-4 z-50" onClick={() => setIsWorkingHoursModalOpen(false)}>
            <div className="bg-white rounded-2xl max-w-lg w-full shadow-2xl flex flex-col max-h-[90vh]" onClick={e => e.stopPropagation()}>
              <div className="bg-white border-b border-gray-100 px-6 py-4 rounded-t-2xl flex items-center justify-between z-20 shadow-sm flex-shrink-0">
                <div className="flex items-center gap-2">
                  <i className="fas fa-cog text-emerald-600"></i>
                  <h3 className="text-lg font-bold text-gray-900">Working Hours &amp; Attendance Settings</h3>
                </div>
                <button onClick={() => setIsWorkingHoursModalOpen(false)} className="text-gray-400 hover:text-gray-600 w-8 h-8 rounded-full hover:bg-gray-100 flex items-center justify-center transition">
                  <i className="fas fa-times"></i>
                </button>
              </div>
              <div className="p-6 space-y-6 overflow-y-auto flex-1 modal-content">
                {/* Global Settings */}
                <div>
                  <h4 className="text-sm font-bold text-gray-700 uppercase tracking-wider mb-3 flex items-center gap-2">
                    <i className="fas fa-sliders-h text-blue-500"></i> Global Configuration
                  </h4>
                  <div className="grid grid-cols-2 gap-3">
                    <div>
                      <label className="block text-xs font-medium text-gray-600 mb-1">Late Allowance (mins)</label>
                      <input
                        type="number" min="0" max="120"
                        value={workingHoursSettings.late_minutes}
                        onChange={e => setWorkingHoursSettings(prev => ({ ...prev, late_minutes: parseInt(e.target.value) || 0 }))}
                        className="input-field w-full text-sm"
                        placeholder="15"
                      />
                      <p className="text-xs text-gray-400 mt-0.5">Check-in after this = Late</p>
                    </div>
                    <div>
                      <label className="block text-xs font-medium text-gray-600 mb-1">Half Day Threshold (mins)</label>
                      <input
                        type="number" min="0" max="600"
                        value={workingHoursSettings.half_day_minutes}
                        onChange={e => setWorkingHoursSettings(prev => ({ ...prev, half_day_minutes: parseInt(e.target.value) || 0 }))}
                        className="input-field w-full text-sm"
                        placeholder="240"
                      />
                      <p className="text-xs text-gray-400 mt-0.5">Less than this = Half Day</p>
                    </div>
                    <div>
                      <label className="block text-xs font-medium text-gray-600 mb-1">Full Day Hours</label>
                      <input
                        type="number" min="1" max="24"
                        value={workingHoursSettings.full_day_hours}
                        onChange={e => setWorkingHoursSettings(prev => ({ ...prev, full_day_hours: parseInt(e.target.value) || 8 }))}
                        className="input-field w-full text-sm"
                        placeholder="8"
                      />
                      <p className="text-xs text-gray-400 mt-0.5">Required hours for full day</p>
                    </div>
                    <div>
                      <label className="block text-xs font-medium text-gray-600 mb-1">Lates to Mark Half Day</label>
                      <input
                        type="number" min="1" max="31"
                        value={workingHoursSettings.lates_for_half_day}
                        onChange={e => setWorkingHoursSettings(prev => ({ ...prev, lates_for_half_day: parseInt(e.target.value) || 3 }))}
                        className="input-field w-full text-sm"
                        placeholder="3"
                      />
                      <p className="text-xs text-gray-400 mt-0.5">Count of late marks = 1 half day</p>
                    </div>
                  </div>
                  
                  {/* Salary and Holiday Config */}
                  <div className="mt-4 pt-4 border-t border-gray-100">
                    <div className="grid grid-cols-1 gap-4">
                      {/* Salary Type */}
                      <div className="bg-emerald-50/50 p-3 rounded-lg border border-emerald-100">
                        <label className="block text-xs font-bold text-gray-700 mb-2">Salary Calculation Type</label>
                        <div className="flex gap-2">
                          <button
                            type="button"
                            onClick={() => setWorkingHoursSettings(prev => ({ ...prev, salary_calculation_type: 'daily' }))}
                            className={`flex-1 py-1.5 px-2 rounded text-xs font-semibold border transition-colors ${workingHoursSettings.salary_calculation_type !== 'monthly' ? 'bg-emerald-600 text-white border-emerald-600' : 'bg-white text-gray-600 border-gray-200 hover:bg-gray-50'}`}
                          >
                            Daily Based
                          </button>
                          <button
                            type="button"
                            onClick={() => setWorkingHoursSettings(prev => ({ ...prev, salary_calculation_type: 'monthly' }))}
                            className={`flex-1 py-1.5 px-2 rounded text-xs font-semibold border transition-colors ${workingHoursSettings.salary_calculation_type === 'monthly' ? 'bg-emerald-600 text-white border-emerald-600' : 'bg-white text-gray-600 border-gray-200 hover:bg-gray-50'}`}
                          >
                            Monthly Based
                          </button>
                        </div>
                        {workingHoursSettings.salary_calculation_type === 'monthly' && (
                          <div className="mt-3">
                            <label className="flex items-center gap-2 cursor-pointer">
                              <input
                                type="checkbox"
                                className="w-4 h-4 accent-emerald-600"
                                checked={workingHoursSettings.deduct_absent_from_monthly !== false}
                                onChange={e => setWorkingHoursSettings(prev => ({ ...prev, deduct_absent_from_monthly: e.target.checked }))}
                              />
                              <span className="text-[11px] font-semibold text-gray-700">Deduct salary for absent days</span>
                            </label>
                          </div>
                        )}
                        <p className="text-[10px] text-gray-500 mt-2">
                          {workingHoursSettings.salary_calculation_type !== 'monthly' 
                            ? 'Salary calculated based on present days' 
                            : (workingHoursSettings.deduct_absent_from_monthly !== false ? 'Fixed monthly salary minus absent days deduction' : 'Fixed monthly salary, independent of daily presence')}
                        </p>
                      </div>

                      {/* Holiday Calendar */}
                      <div className="bg-blue-50/50 p-3 rounded-lg border border-blue-100">
                        <label className="block text-xs font-bold text-gray-700 mb-2">Holiday Calendar (Fixed)</label>
                        <div className="flex gap-2 mb-2">
                          <input 
                            type="date"
                            id="holiday-date-picker"
                            className="input-field text-xs py-1 px-2 w-full"
                          />
                          <button
                            type="button"
                            onClick={() => {
                              const input = document.getElementById('holiday-date-picker');
                              const date = input.value;
                              if (date && !(workingHoursSettings.holidays || []).includes(date)) {
                                setWorkingHoursSettings(prev => ({
                                  ...prev,
                                  holidays: [...(prev.holidays || []), date].sort()
                                }));
                                input.value = '';
                              }
                            }}
                            className="bg-blue-600 hover:bg-blue-700 text-white px-3 py-1 rounded text-xs font-bold whitespace-nowrap"
                          >
                            Add
                          </button>
                        </div>
                        <div className="flex flex-wrap gap-1 max-h-[80px] overflow-y-auto">
                          {(workingHoursSettings.holidays || []).map(date => (
                            <span key={date} className="inline-flex items-center gap-1 bg-white border border-blue-200 text-blue-700 text-[10px] px-2 py-0.5 rounded-full font-medium">
                              {new Date(date).toLocaleDateString()}
                              <button 
                                type="button" 
                                onClick={() => setWorkingHoursSettings(prev => ({
                                  ...prev,
                                  holidays: prev.holidays.filter(d => d !== date)
                                }))}
                                className="hover:text-red-600 ml-0.5"
                              >
                                &times;
                              </button>
                            </span>
                          ))}
                          {(!workingHoursSettings.holidays || workingHoursSettings.holidays.length === 0) && (
                            <span className="text-[10px] text-gray-400">No holidays added</span>
                          )}
                        </div>
                      </div>
                    </div>
                  </div>

                </div>

                {/* Per-Day Schedule */}
                <div>
                  <h4 className="text-sm font-bold text-gray-700 uppercase tracking-wider mb-3 flex items-center gap-2">
                    <i className="fas fa-calendar-week text-emerald-500"></i> Daily Schedule
                  </h4>
                  <div className="rounded-xl border border-gray-100 overflow-hidden">
                    <div className="grid grid-cols-[80px_1fr_1fr_40px] gap-0 bg-gray-50 border-b border-gray-100 px-3 py-2">
                      <span className="text-xs font-bold text-gray-500">Day</span>
                      <span className="text-xs font-bold text-gray-500">Start</span>
                      <span className="text-xs font-bold text-gray-500">End</span>
                      <span className="text-xs font-bold text-gray-500 text-center">On</span>
                    </div>
                    {['Mon','Tue','Wed','Thu','Fri','Sat','Sun'].map(day => (
                      <div key={day} className={`grid grid-cols-[80px_1fr_1fr_40px] gap-2 items-center px-3 py-2 border-b border-gray-50 last:border-0 ${!workingHoursSettings.schedule[day]?.enabled ? 'opacity-50 bg-gray-50/50' : 'bg-white'}`}>
                        <span className="text-sm font-semibold text-gray-700">{day}</span>
                        <input
                          type="time"
                          value={workingHoursSettings.schedule[day]?.start || '09:00'}
                          onChange={e => setWorkingHoursSettings(prev => ({ ...prev, schedule: { ...prev.schedule, [day]: { ...prev.schedule[day], start: e.target.value } } }))}
                          className="input-field text-sm py-1.5"
                          disabled={!workingHoursSettings.schedule[day]?.enabled}
                        />
                        <input
                          type="time"
                          value={workingHoursSettings.schedule[day]?.end || '18:00'}
                          onChange={e => setWorkingHoursSettings(prev => ({ ...prev, schedule: { ...prev.schedule, [day]: { ...prev.schedule[day], end: e.target.value } } }))}
                          className="input-field text-sm py-1.5"
                          disabled={!workingHoursSettings.schedule[day]?.enabled}
                        />
                        <div className="flex justify-center">
                          <input
                            type="checkbox"
                            checked={!!workingHoursSettings.schedule[day]?.enabled}
                            onChange={e => setWorkingHoursSettings(prev => ({ ...prev, schedule: { ...prev.schedule, [day]: { ...prev.schedule[day], enabled: e.target.checked } } }))}
                            className="w-4 h-4 accent-emerald-600 cursor-pointer"
                          />
                        </div>
                      </div>
                    ))}
                  </div>
                </div>

                <div className="bg-blue-50 rounded-xl p-3 border border-blue-100">
                  <p className="text-xs text-blue-700 font-medium">
                    <i className="fas fa-info-circle mr-1"></i>
                    These settings apply globally to all staff. Salary is calculated as: Full Day = daily rate, Half Day = daily rate × 0.5. {workingHoursSettings.lates_for_half_day} late marks = 1 half day deduction.
                  </p>
                </div>

                <div className="flex gap-3 pt-2">
                  <button
                    disabled={isSavingWorkingHours}
                    onClick={async () => {
                      setIsSavingWorkingHours(true);
                      try {
                        const res = await fetch(`${API_URL}/api/company/profile`, {
                          method: 'PUT',
                          headers: { 'Content-Type': 'application/json', 'Authorization': `Bearer ${token}` },
                          body: JSON.stringify({
                            ...companyProfile,
                            attendance_settings: workingHoursSettings,
                            qr_codes: companyProfile.qr_codes || [],
                            master_qr_location_restriction_enabled: !!companyProfile.master_qr_location_restriction_enabled,
                            master_qr_radius_meters: companyProfile.master_qr_radius_meters || 200,
                          })
                        });
                        const json = await res.json();
                        if (json.success) {
                          showToast('Working hours settings saved!', 'success');
                          setIsWorkingHoursModalOpen(false);
                        } else {
                          showToast(json.message || 'Failed to save', 'error');
                        }
                      } catch(err) {
                        showToast('Failed to save settings', 'error');
                      } finally {
                        setIsSavingWorkingHours(false);
                      }
                    }}
                    className="btn btn-primary flex-1 py-2.5 flex items-center justify-center gap-2 disabled:opacity-50"
                  >
                    {isSavingWorkingHours ? <><i className="fas fa-spinner animate-spin"></i> Saving...</> : <><i className="fas fa-save"></i> Save Settings</>}
                  </button>
                  <button onClick={() => setIsWorkingHoursModalOpen(false)} className="btn btn-secondary flex-1 py-2.5">Cancel</button>
                </div>
              </div>
            </div>
          </div>
        )}

        {
          isWaitingQRModalOpen && (
            <div className="fixed inset-0 modal-overlay flex items-center justify-center p-4" onClick={() => setIsWaitingQRModalOpen(false)}>
              <div className="bg-white rounded-3xl max-w-sm w-full p-8 text-center modal-content shadow-[0_20px_50px_rgba(0,0,0,0.3)] border border-gray-100" onClick={(e) => e.stopPropagation()}>
                <div className="w-16 h-16 bg-orange-100 rounded-full flex items-center justify-center mx-auto mb-6">
                  <i className="fas fa-clock text-2xl text-orange-600"></i>
                </div>
                <h3 className="text-2xl font-black text-gray-900 mb-2 uppercase tracking-tight">{t('waitingQR') || 'Waiting QR Code'}</h3>
                <p className="text-sm text-gray-500 mb-8 font-medium">Clients scan this when all tables are occupied to pre-order</p>
                <div className="flex justify-center items-center p-4 bg-white rounded-2xl shadow-inner border order-orange-100 mb-8">
                  <div id="waiting-qr-container" className="qr-container"></div>
                </div>
                <div className="flex gap-3">
                  <button
                    onClick={() => {
                      const content = document.getElementById('waiting-qr-container').innerHTML;
                      const win = window.open('', '_blank');
                      win.document.write(`<html><head><title>Waiting QR</title><style>body{display:flex;flex-direction:column;align-items:center;justify-content:center;height:100vh;font-family:sans-serif;text-align:center;} .qr-card{padding:40px;border:5px solid #ea580c;border-radius:30px;} h1{color:#ea580c;font-size:32px;margin-bottom:10px;} p{color:#6b7280;margin-bottom:30px;}</style></head><body><div class="qr-card"><h1>WAITING LIST</h1><p>Scan to join the queue and pre-order</p>${content}</div><script>window.onload=function(){window.print();window.close();};</script></body></html>`);
                      win.document.close();
                    }}
                    className="btn bg-emerald-600 hover:bg-emerald-700 text-white flex-1 py-3 rounded-xl font-bold shadow-lg shadow-emerald-200 transition-all active:scale-95"
                  >
                    <i className="fas fa-print mr-2"></i> {t('print')}
                  </button>
                  <button onClick={() => setIsWaitingQRModalOpen(false)} className="btn btn-secondary flex-1 py-3 rounded-xl font-bold transition-all active:scale-95">{t('close')}</button>
                </div>
              </div>
            </div>
          )
        }

        {
          isDeliveryQRModalOpen && (
            <div className="fixed inset-0 modal-overlay flex items-center justify-center p-4" onClick={() => setIsDeliveryQRModalOpen(false)}>
              <div className="bg-white rounded-3xl max-w-sm w-full p-8 text-center modal-content shadow-[0_20px_50px_rgba(0,0,0,0.3)] border border-gray-100" onClick={(e) => e.stopPropagation()}>
                <div className="w-16 h-16 bg-indigo-100 rounded-full flex items-center justify-center mx-auto mb-6">
                  <i className="fas fa-truck text-2xl text-indigo-600"></i>
                </div>
                <h3 className="text-2xl font-black text-gray-900 mb-2 uppercase tracking-tight">Delivery QR Code</h3>
                <p className="text-sm text-gray-500 mb-6 font-medium">Clients scan this to place independent delivery/offline orders</p>
                
                {branches && branches.length > 0 && (
                  <div className="mb-6 text-left">
                    <label className="block text-[10px] font-black text-indigo-900 uppercase mb-2 tracking-wider">Select Branch / Outlet</label>
                    <select
                      value={qrBranchId || ''}
                      onChange={(e) => {
                        const val = e.target.value ? parseInt(e.target.value) : null;
                        setQrBranchId(val);
                        renderDeliveryQR(val);
                      }}
                      className="w-full px-3 py-2 border border-gray-300 rounded-xl text-sm font-bold text-gray-800 focus:outline-none focus:ring-2 focus:ring-indigo-500"
                    >
                      <option value="">General (Settings Fallback Address)</option>
                      {branches.map(b => (
                        <option key={b.id} value={b.id}>
                          {b.name} ({b.city || 'No Address'})
                        </option>
                      ))}
                    </select>
                  </div>
                )}

                <div className="flex justify-center items-center p-4 bg-white rounded-2xl shadow-inner border border-indigo-100 mb-8 font-bold">
                  <div id="delivery-qr-container" className="qr-container"></div>
                </div>
                <div className="flex gap-3">
                  <button
                    onClick={() => {
                      const content = document.getElementById('delivery-qr-container').innerHTML;
                      const win = window.open('', '_blank');
                      win.document.write(`<html><head><title>Delivery QR</title><style>body{display:flex;flex-direction:column;align-items:center;justify-content:center;height:100vh;font-family:sans-serif;text-align:center;} .qr-card{padding:40px;border:5px solid #4f46e5;border-radius:30px;} h1{color:#4f46e5;font-size:32px;margin-bottom:10px;} p{color:#6b7280;margin-bottom:30px;}</style></head><body><div class="qr-card"><h1>OFFLINE DELIVERY</h1><p>Scan to place delivery and take-away orders</p>${content}</div><script>window.onload=function(){window.print();window.close();};</script></body></html>`);
                      win.document.close();
                    }}
                    className="btn bg-indigo-600 hover:bg-indigo-700 text-white flex-1 py-3 rounded-xl font-bold shadow-lg shadow-indigo-200 transition-all active:scale-95"
                  >
                    <i className="fas fa-print mr-2"></i> {t('print')}
                  </button>
                  <button onClick={() => setIsDeliveryQRModalOpen(false)} className="btn btn-secondary flex-1 py-3 rounded-xl font-bold transition-all active:scale-95">{t('close')}</button>
                </div>
              </div>
            </div>
          )
        }

        {isMenuModalOpen && (
          <div className="fixed inset-0 modal-overlay flex items-center justify-center p-4" onClick={closeMenuModal}>
            <div className="bg-white rounded-lg max-w-md w-full p-6 max-h-[90vh] overflow-y-auto modal-content" onClick={(e) => e.stopPropagation()}>
              <h3 className="text-xl font-bold mb-4">{currentMenuItem.id ? t('edit') : t('addItem')}</h3>
              <form onSubmit={handleMenuSubmit}>
                <input type="hidden" name="id" value={currentMenuItem.id || ''} />
                {menuImagePreview && <img src={menuImagePreview} alt="prev" className="w-full h-48 object-cover rounded mb-3" />}
                <input type="file" accept="image/*" onChange={handleImageChange} className="w-full mb-3" />
                <input name="name" defaultValue={currentMenuItem.name} placeholder={t('itemNamePlaceholder') || "Item Name (e.g., Grilled Chicken)"} required className="input-field w-full mb-3" />
                
                {/* Veg / Non-Veg Selection */}
                <div className="mb-3">
                  <label className="block text-xs font-bold text-gray-700 mb-1.5 uppercase tracking-wider">{t('foodType') || 'Food Type'}</label>
                  <div className="grid grid-cols-2 gap-3">
                    <label className={`flex items-center justify-center gap-2 p-2.5 rounded-xl border-2 cursor-pointer transition-all ${
                      (currentMenuItem.is_veg === undefined || currentMenuItem.is_veg === 1 || currentMenuItem.is_veg === true || currentMenuItem.is_veg === '1') 
                      ? 'border-emerald-500 bg-emerald-50 text-emerald-800 font-bold shadow-sm' 
                      : 'border-gray-200 hover:border-gray-300 text-gray-600'
                    }`}>
                      <input 
                        type="radio" 
                        name="is_veg" 
                        value="1" 
                        defaultChecked={currentMenuItem.is_veg === undefined || currentMenuItem.is_veg === 1 || currentMenuItem.is_veg === true || currentMenuItem.is_veg === '1'} 
                        className="sr-only"
                        onChange={() => setCurrentMenuItem(prev => ({ ...prev, is_veg: 1 }))}
                      />
                      <span className="w-4 h-4 rounded-sm border-2 border-emerald-600 flex items-center justify-center p-0.5 bg-white">
                        <span className="w-2 h-2 rounded-full bg-emerald-600"></span>
                      </span>
                      <span className="text-sm">{t('veg') || 'Veg'}</span>
                    </label>
                    <label className={`flex items-center justify-center gap-2 p-2.5 rounded-xl border-2 cursor-pointer transition-all ${
                      (currentMenuItem.is_veg === 0 || currentMenuItem.is_veg === false || currentMenuItem.is_veg === '0') 
                      ? 'border-red-500 bg-red-50 text-red-800 font-bold shadow-sm' 
                      : 'border-gray-200 hover:border-gray-300 text-gray-600'
                    }`}>
                      <input 
                        type="radio" 
                        name="is_veg" 
                        value="0" 
                        defaultChecked={currentMenuItem.is_veg === 0 || currentMenuItem.is_veg === false || currentMenuItem.is_veg === '0'} 
                        className="sr-only"
                        onChange={() => setCurrentMenuItem(prev => ({ ...prev, is_veg: 0 }))}
                      />
                      <span className="w-4 h-4 rounded-sm border-2 border-red-600 flex items-center justify-center p-0.5 bg-white">
                        <span className="w-2 h-2 rounded-full bg-red-600"></span>
                      </span>
                      <span className="text-sm">{t('nonVeg') || 'Non-Veg'}</span>
                    </label>
                  </div>
                </div>

                <div className="relative mb-3">
                  <textarea name="description" defaultValue={currentMenuItem.description} placeholder={t('itemDescriptionPlaceholder') || "Item description & ingredients (e.g., Delicious wood-fired pizza with tomato, mozzarella, basil)"} className="input-field w-full" rows="3" />
                  <button type="button" onClick={async () => {
                    const name = document.querySelector('input[name="name"]').value;
                    const desc = document.querySelector('textarea[name="description"]').value;
                    if (!name) { showToast(t('name') + ' required', 'error'); return; }
                    showToast(t('generating'), 'info');
                    try {
                      const res = await fetch(`${API_URL}/api/ai/nutrition`, { method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify({ name, description: desc }) });
                      const json = await res.json();
                      if (json.success) {
                        document.querySelector('input[name="nutritional_info"]').value = json.data.nutritional_info;
                        document.querySelector('input[name="vitamins"]').value = json.data.vitamins;
                        showToast(t('aiSuccess'), 'success');
                      } else { showToast(json.message || 'Generation failed', 'error'); }
                    } catch (error) { console.error(error); showToast('Network error', 'error'); }
                  }} className="absolute right-2 bottom-2 text-xs bg-emerald-100 text-emerald-700 px-2 py-1 rounded hover:bg-emerald-200">✨ {t('aiRecommendation')}</button>
                </div>
                <input name="nutritional_info" defaultValue={currentMenuItem.nutritional_info} placeholder={t('nutritionalInfo') || "Nutritional Info"} className="input-field w-full mb-3" />
                <input name="vitamins" defaultValue={currentMenuItem.vitamins} placeholder={t('vitamins') || "Vitamins"} className="input-field w-full mb-3" />
                <div className="grid grid-cols-3 gap-3 mb-3">
                  <div className="col-span-2">
                    <label className="block text-sm font-medium text-gray-700 mb-1">{t('price') || 'Price'}</label>
                    <input name="price" type="number" step="0.01" min="0" defaultValue={parseFloat(currentMenuItem.price) > 0 ? currentMenuItem.price : (currency === 'INR' ? currentMenuItem.price_inr : currentMenuItem.price_usd)} placeholder="0.00" required className="input-field w-full" />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">{t('currency') || 'Currency'}</label>
                    <select name="currency_code" defaultValue={currentMenuItem.currency_code || currency} className="input-field w-full">
                      {Object.keys(currencyMeta).map(c => <option key={c} value={c}>{c}</option>)}
                    </select>
                  </div>
                </div>
                <p className="text-xs text-gray-500 mb-3">
                  {`✓ Prices will be displayed to customers in your company's default currency (${currency})`}
                </p>

                <div className="mb-3">
                  <label className="block text-sm font-medium text-gray-700 mb-1">Discount (%)</label>
                  <input name="discount_percentage" type="number" step="0.01" min="0" max="100" defaultValue={currentMenuItem.discount_percentage || 0} placeholder="0" className="input-field w-full" />
                  <p className="text-xs text-gray-500 mt-1">Optional: Add a discount percentage (e.g., 10 for 10% off)</p>
                </div>
                <div className="mb-3">
                  <select
                    name="category"
                    defaultValue={currentMenuItem.category || ''}
                    required
                    className="input-field w-full"
                    onChange={(e) => {
                      e.target.setCustomValidity('');
                      if (e.target.value === 'add-new') setShowNewCategoryInput(true);
                    }}
                    onInvalid={(e) => e.target.setCustomValidity('Please select any Category from list')}
                    onInput={(e) => e.target.setCustomValidity('')}
                  >
                    <option value="">{t('allCategories')}</option>
                    {categories.map((c) => <option key={c.id || c.name} value={c.name}>{c.name} {c.is_self_service ? '(Self-Serve)' : ''}</option>)}
                    <option value="add-new">➕ Add New</option>
                  </select>
                  {showNewCategoryInput && (
                    <div className="mt-2 bg-gray-50 p-2 rounded border">
                      <div className="flex gap-2 mb-2">
                        <input type="text" placeholder="New category name" value={newCategoryName} onChange={(e) => setNewCategoryName(e.target.value)} className="input-field flex-1" autoFocus />
                        <button type="button" onClick={addNewCategory} className="btn bg-green-600 hover:bg-green-700 text-white px-4 py-2 rounded text-sm">Add</button>
                        <button type="button" onClick={() => { setShowNewCategoryInput(false); setNewCategoryName(''); setIsSelfServiceNewCategory(false); }} className="btn btn-secondary px-3 py-2 rounded text-sm">✕</button>
                      </div>
                      <label className="flex items-center text-sm text-gray-700 cursor-pointer">
                        <input
                          type="checkbox"
                          checked={isSelfServiceNewCategory}
                          onChange={e => setIsSelfServiceNewCategory(e.target.checked)}
                          className="mr-2 rounded text-emerald-600 focus:ring-emerald-500"
                        />
                        {t('selfService')}
                      </label>
                    </div>
                  )}
                </div>
                <label className="flex items-center mb-4"><input type="checkbox" name="is_available" defaultChecked={currentMenuItem.id ? !!currentMenuItem.is_available : true} className="mr-2" />{t('available')}</label>

                <div className="bg-emerald-50 p-4 rounded-xl border border-emerald-100 mb-4">
                  <label className="flex items-center font-bold text-emerald-800 mb-2 cursor-pointer">
                    <input
                      type="checkbox"
                      name="is_special"
                      defaultChecked={!!currentMenuItem.is_special}
                      className="mr-2 h-4 w-4 rounded text-emerald-600 focus:ring-emerald-500"
                      onChange={(e) => {
                        // Force a rerender if needed, but defaultChecked/FormData is enough for submit
                        // We use a state to show/hide duration if we want to be fancy
                      }}
                    />
                    <i className="fas fa-star text-amber-500 mr-2"></i> {t('markAsSpecial')}
                  </label>
                  <div className="flex flex-col gap-2 ml-6">
                    <p className="text-xs text-emerald-600 mb-1">{t('specialItemDesc')}</p>
                    <select name="special_duration" defaultValue={currentMenuItem.special_duration || '2'} className="input-field w-full text-sm">
                      <option value="1">{t('expireIn1Day')}</option>
                      <option value="2">{t('expireIn2Days')}</option>
                      <option value="3">{t('expireIn3Days')}</option>
                      <option value="7">{t('expireIn7Days')}</option>
                      <option value="30">{t('expireIn30Days')}</option>
                    </select>
                  </div>
                </div>

                <div className="flex gap-3">
                  <button type="submit" disabled={isSubmittingMenu} className="btn btn-primary flex-1 py-2 flex items-center justify-center gap-2 disabled:opacity-50">
                    {isSubmittingMenu ? (
                      <>
                        <span className="animate-spin rounded-full h-4 w-4 border-2 border-white border-t-transparent inline-block"></span>
                        <span>{t('saving') || 'Saving...'}</span>
                      </>
                    ) : (
                      t('save')
                    )}
                  </button>
                  <button type="button" onClick={closeMenuModal} disabled={isSubmittingMenu} className="btn btn-secondary flex-1 py-2">{t('cancel')}</button>
                </div>
              </form>
            </div>
          </div>
        )
        }

        {
          isStaffModalOpen && (
            <div className="fixed inset-0 modal-overlay flex items-center justify-center p-4" onClick={() => setIsStaffModalOpen(false)}>
              <div key={editingStaff ? `edit-${editingStaff.id}` : 'add-new'} className="bg-white rounded-lg max-w-md w-full p-6 modal-content shadow-[0_20px_50px_rgba(0,0,0,0.3)] border border-gray-100" onClick={(e) => e.stopPropagation()}>
                <h3 className="text-xl font-bold mb-4">{editingStaff ? t('editStaff') : t('addStaff')}</h3>
                <form onSubmit={handleStaffSubmit}>
                  <div className="mb-4">
                    <label className="block text-sm font-medium text-gray-700 mb-1">{t('name')} <span className="text-red-500">*</span></label>
                    <input
                      name="name"
                      defaultValue={staffData.name}
                      required
                      className="input-field w-full"
                      placeholder={t('enterName') || "Enter Name"}
                    />
                  </div>
                  <div className="mb-4">
                    <label className="block text-sm font-medium text-gray-700 mb-1">{t('role')} <span className="text-red-500">*</span></label>
                    <input
                      name="role"
                      defaultValue={staffData.role}
                      required
                      className="input-field w-full"
                      placeholder={t('enterRole') || "Enter Role"}
                    />
                  </div>
                  <div className="mb-4">
                    <label className="block text-sm font-medium text-gray-700 mb-1">{t('phone')}</label>
                    <input
                      name="phone"
                      defaultValue={staffData.phone}
                      pattern="[0-9]{10}"
                      title="Phone number must be exactly 10 digits"
                      className="input-field w-full"
                      placeholder={t('enterPhoneNumber') || "Enter Phone Number"}
                    />
                  </div>
                  <div className="mb-4">
                    <label className="block text-sm font-medium text-gray-700 mb-1">{t('pin')} (6 digits) <span className="text-red-500">*</span></label>
                    <input
                      name="pin"
                      defaultValue={staffData.pin}
                      required
                      maxLength="6"
                      pattern="[0-9]{6}"
                      title="PIN must be exactly 6 digits"
                      className="input-field w-full"
                      placeholder={t('enterPin') || "Enter PIN"}
                    />
                  </div>
                  <div className="mb-4">
                    <label className="block text-sm font-medium text-gray-700 mb-1">Branch</label>
                    <select
                      name="branch_id"
                      defaultValue={staffData.branch_id || ''}
                      className="input-field w-full"
                    >
                      <option value="">{t('noBranch') || 'No branch'}</option>
                      {branches.map(b => (
                        <option key={b.id} value={b.id}>{b.name}</option>
                      ))}
                    </select>
                  </div>
                  <div className="mb-4">
                    <label className="block text-sm font-medium text-gray-700 mb-1">{t('salary') || 'Salary'}</label>
                    <input name="salary" type="number" min="0" defaultValue={staffData.salary || 0} className="input-field w-full" placeholder="0" />
                  </div>
                  <div className="mb-4">
                    <label className="block text-sm font-medium text-gray-700 mb-1">{t('dailyRate') || 'Daily Rate'}</label>
                    <input name="daily_rate" type="number" min="0" defaultValue={staffData.daily_rate || 0} className="input-field w-full" placeholder="0" />
                  </div>
                  <div className="flex gap-3">
                    <button type="submit" className="btn btn-primary flex-1 py-2">{t('save')}</button>
                    <button type="button" onClick={() => setIsStaffModalOpen(false)} className="btn btn-secondary flex-1 py-2">{t('cancel')}</button>
                  </div>
                </form>
              </div>
            </div>
          )
        }

        {
          isGroupModalOpen && (
            <div className="fixed inset-0 modal-overlay flex items-center justify-center p-4" onClick={closeGroupModal}>
              <div className="bg-white rounded-lg max-w-md w-full p-6 modal-content" onClick={(e) => e.stopPropagation()}>
                <h3 className="text-xl font-bold mb-4">{currentGroup.id ? 'Edit' : 'Add'} Group</h3>
                <form onSubmit={handleGroupSubmit}>
                  <input type="hidden" name="id" value={currentGroup.id || ''} />
                  <input name="name" defaultValue={currentGroup.name} placeholder={t('groupNamePlaceholder')} required className="input-field w-full mb-3" />
                  <div className="flex gap-3">
                    <button type="submit" className="btn btn-primary flex-1 py-2">{t('save')}</button>
                    <button type="button" onClick={closeGroupModal} className="btn btn-secondary flex-1 py-2">{t('cancel')}</button>
                  </div>
                </form>
              </div>
            </div>
          )
        }

        {
          isProfileModalOpen && (
            <div className="fixed inset-0 modal-overlay flex items-center justify-center p-4" onClick={() => setIsProfileModalOpen(false)}>
              <div className="bg-white rounded-lg max-w-md w-full p-6 modal-content max-h-[85vh] overflow-y-auto" onClick={(e) => e.stopPropagation()}>
                <h3 className="text-xl font-bold mb-4">{t('edit')} {t('companySettings')}</h3>
                <form onSubmit={(e) => { handleProfileSubmit(e); setIsProfileModalOpen(false); }}>
                  <div className="mb-4">
                    <label className="block text-sm font-medium text-gray-700 mb-1">{t('companyName')}</label>
                    <input name="name" defaultValue={companyProfile.name} required className="input-field" />
                  </div>
                  <div className="mb-4">
                    <label className="block text-sm font-medium text-gray-700 mb-1">{t('logo')}</label>
                    <div className="flex items-center gap-4">
                      {companyProfile.logo_url && (
                        <div className="relative inline-block">
                          <img src={companyProfile.logo_url} alt="Logo Preview" className="h-16 w-16 object-contain border rounded p-1" />
                          <button type="button" onClick={() => setCompanyProfile(prev => ({ ...prev, logo_url: '' }))} className="absolute -top-2 -right-2 bg-red-100 text-red-600 rounded-full w-6 h-6 flex items-center justify-center hover:bg-red-200 shadow-sm border border-red-200 p-0 leading-none">
                            <i className="fas fa-times text-xs"></i>
                          </button>
                        </div>
                      )}
                      <input
                        type="file"
                        accept="image/*"
                        onChange={async (e) => {
                          if (e.target.files[0]) {
                            showToast('Uploading logo...', 'info');
                            const url = await handleFileUpload(e.target.files[0]);
                            if (url) {
                              setCompanyProfile(prev => ({ ...prev, logo_url: url }));
                              showToast('Logo uploaded!', 'success');
                            }
                          }
                        }}
                        className="block w-full text-sm text-gray-500 file:mr-4 file:py-2 file:px-4 file:rounded-full file:border-0 file:text-sm file:font-semibold file:bg-emerald-50 file:text-emerald-700 hover:file:bg-emerald-100"
                      />
                    </div>
                    <input type="hidden" name="logo_url" value={companyProfile.logo_url || ''} />
                  </div>
                  {/* <div className="mb-6">
                    <label className="block text-sm font-medium text-gray-700 mb-1">{t('banner')}</label>
                    <div className="space-y-2">
                      {companyProfile.banner_url && (
                        <div className="relative inline-block w-full">
                          <img src={companyProfile.banner_url} alt="Banner Preview" className="w-full h-24 object-cover rounded" />
                          <button type="button" onClick={() => setCompanyProfile(prev => ({ ...prev, banner_url: '' }))} className="absolute top-2 right-2 bg-red-100 text-red-600 rounded-full w-8 h-8 flex items-center justify-center hover:bg-red-200 shadow-sm border border-red-200 p-0 leading-none">
                            <i className="fas fa-times text-sm"></i>
                          </button>
                        </div>
                      )}
                      <input
                        type="file"
                        accept="image/*"
                        onChange={async (e) => {
                          if (e.target.files[0]) {
                            showToast('Uploading banner...', 'info');
                            const url = await handleFileUpload(e.target.files[0]);
                            if (url) {
                              setCompanyProfile(prev => ({ ...prev, banner_url: url }));
                              showToast('Banner uploaded!', 'success');
                            }
                          }
                        }}
                        className="block w-full text-sm text-gray-500 file:mr-4 file:py-2 file:px-4 file:rounded-full file:border-0 file:text-sm file:font-semibold file:bg-emerald-50 file:text-emerald-700 hover:file:bg-emerald-100"
                      />
                    </div>
                    <input type="hidden" name="banner_url" value={companyProfile.banner_url || ''} />
                  </div> */}

                  <h4 className="font-bold text-gray-800 mb-3 mt-6">{t('socialMediaLinks')}</h4>
                  <div className="space-y-3 mb-6">
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">
                        <i className="fab fa-twitter text-emerald-400 mr-2"></i> {t('twitter')}
                      </label>
                      <input name="twitter_url" defaultValue={companyProfile.twitter_url} placeholder="https://twitter.com/yourhandle" className="input-field" />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">
                        <i className="fab fa-youtube text-emerald-600 mr-2"></i> {t('youtube')}
                      </label>
                      <input name="youtube_url" defaultValue={companyProfile.youtube_url} placeholder="https://youtube.com/@yourchannel" className="input-field" />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">
                        <i className="fab fa-instagram text-teal-600 mr-2"></i> {t('instagram')}
                      </label>
                      <input name="instagram_url" defaultValue={companyProfile.instagram_url} placeholder="https://instagram.com/yourhandle" className="input-field" />
                    </div>
                  </div>

                  <h4 className="font-bold text-gray-800 mb-2 mt-6">Payment Settings</h4>
                  <div className="mb-6">
                    <label className="block text-sm font-medium text-gray-700 mb-1">UPI ID for Click-to-Pay</label>
                    <div className="relative">
                      <input
                        name="upi_id"
                        type={showUpiId ? "text" : "password"}
                        defaultValue={companyProfile.upi_id || ''}
                        placeholder="e.g., yourname@okaxis"
                        className="input-field pr-10"
                      />
                      <button
                        type="button"
                        onClick={() => setShowUpiId(!showUpiId)}
                        className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600 transition-colors"
                      >
                        <i className={`fas ${showUpiId ? 'fa-eye-slash' : 'fa-eye'}`}></i>
                      </button>
                    </div>
                  </div>

                  <h4 className="font-bold text-gray-800 mb-3 mt-6">Festival Offer</h4>
                  <div className="space-y-3 mb-6">
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">Festival Name</label>
                      <input name="festival_name" defaultValue={companyProfile.festival_name} placeholder="e.g., Diwali, Christmas, New Year" className="input-field" />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">Festival Discount (%)</label>
                      <input name="festival_discount" type="number" step="0.01" min="0" max="100" defaultValue={companyProfile.festival_discount || 0} className="input-field" />
                      <p className="text-xs text-gray-500 mt-1">This discount will be applied to the overall bill</p>
                    </div>
                  </div>
                  
                  {(Number(companyProfile?.id) === 39 || Number(companyProfile?.company_id) === 39 || Number(authUser?.company_id) === 39) && (
                    <div className="mb-6 pt-4 border-t border-gray-100">
                      <h4 className="font-bold text-gray-800 mb-2">Inventory Auto-Deduction Mode</h4>
                      <div className="flex items-center justify-between p-3.5 bg-emerald-50/50 border border-emerald-100 rounded-xl">
                        <div>
                          <span className="text-sm font-bold text-gray-800 block">Auto Deduct Stock on Orders</span>
                          <span className="text-xs text-gray-500 block mt-0.5">Automatically deduct mapped inventory ingredients when an order is created.</span>
                        </div>
                        <label className="relative inline-flex items-center cursor-pointer ml-3 flex-shrink-0">
                          <input 
                            type="checkbox" 
                            name="auto_deduct_inventory"
                            checked={!!companyProfile.auto_deduct_inventory}
                            onChange={(e) => setCompanyProfile(prev => ({ ...prev, auto_deduct_inventory: e.target.checked }))}
                            className="sr-only peer"
                          />
                          <div className="w-11 h-6 bg-gray-200 peer-focus:outline-none rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-emerald-600"></div>
                        </label>
                      </div>
                    </div>
                  )}


                  {/* Subscription Plan Card */}
                  {((authUser?.role || '').toLowerCase() === 'admin' || 
                    (authUser?.role || '').toLowerCase() === 'owner' || 
                    (authUser?.role || '').toLowerCase() === 'superadmin') && (
                    <div className="mb-6 pt-4 border-t border-gray-100">
                      <label className="block text-sm font-bold text-gray-700 mb-2">Subscription Plan</label>
                      <div className="bg-gradient-to-br from-indigo-50 to-purple-50 border border-indigo-100 rounded-2xl p-4 flex flex-col gap-3">
                        <div className="flex justify-between items-center text-xs">
                          <div className="text-left">
                            <p className="text-gray-400 font-bold uppercase tracking-wider text-[10px]">Current Plan</p>
                            <p className="font-extrabold text-indigo-950 mt-0.5 text-left">
                              {(!companyProfile.has_paid || (companyProfile.subscription_plan || '').toLowerCase() === 'trial')
                                ? 'FREE TRIAL'
                                : (companyProfile.subscription_plan || 'Starter').toUpperCase()}
                            </p>
                          </div>
                          <div className="text-right">
                            <p className="text-gray-400 font-bold uppercase tracking-wider text-[10px]">
                              {(!companyProfile.has_paid || (companyProfile.subscription_plan || '').toLowerCase() === 'trial') ? 'Trial Expires On' : 'Expires On'}
                            </p>
                            <p className="font-bold text-gray-800 mt-0.5 text-right">
                              {(companyProfile.subscription_end_date || companyProfile.trial_ends_at || companyProfile.trial_end_date) 
                                ? new Date(companyProfile.subscription_end_date || companyProfile.trial_ends_at || companyProfile.trial_end_date).toLocaleDateString(undefined, { year: 'numeric', month: 'long', day: 'numeric' })
                                : 'N/A'
                              }
                            </p>
                          </div>
                        </div>
                        <button
                          type="button"
                          onClick={() => {
                            setIsProfileModalOpen(false);
                            setIsRenewalModalOpen(true);
                          }}
                          className="w-full bg-gradient-to-r from-indigo-600 to-purple-600 hover:from-indigo-700 hover:to-purple-700 text-white font-bold py-2.5 rounded-xl transition flex items-center justify-center gap-2 text-xs shadow-sm hover:shadow active:scale-95"
                        >
                          <i className="fas fa-sync-alt"></i>
                          <span>Renew / Extend Plan</span>
                        </button>
                      </div>
                    </div>
                  )}

                  {((authUser?.role || '').toLowerCase() === 'admin' || 
                    (authUser?.role || '').toLowerCase() === 'owner' || 
                    (authUser?.role || '').toLowerCase() === 'superadmin') && (
                    <div className="mb-6 pt-4 border-t border-gray-100">
                      <button
                        type="button"
                        onClick={() => {
                          setActiveTab('activity_log');
                          setIsProfileModalOpen(false);
                        }}
                        className="w-full bg-emerald-50 hover:bg-emerald-100 text-emerald-700 font-bold py-2.5 px-4 rounded-xl border border-emerald-100 transition flex items-center justify-center gap-2 text-xs"
                      >
                        <i className="fas fa-history text-sm"></i>
                        <span>{t('viewActivityLog') || 'View Activity Log'}</span>
                      </button>
                    </div>
                  )}

                  <div className="flex gap-3">
                    <button type="submit" className="btn btn-primary flex-1 py-2">{t('saveChanges')}</button>
                    <button type="button" onClick={() => setIsProfileModalOpen(false)} className="btn btn-secondary flex-1 py-2">{t('cancel')}</button>
                  </div>
                </form>
              </div>
            </div>
          )
        }

        {
          isTableModalOpen && (
            <div className="fixed inset-0 modal-overlay flex items-center justify-center p-4" onClick={closeTableModal}>
              <div className="bg-white rounded-lg max-w-md w-full p-6 modal-content" onClick={(e) => e.stopPropagation()}>
                <h3 className="text-xl font-bold mb-4">{currentTable.id ? t('editTable') : t('addTable')}</h3>
                <form onSubmit={handleTableSubmit}>
                  <input type="hidden" name="id" value={currentTable.id || ''} />
                  <input name="table_number" type="number" min="0" defaultValue={currentTable.table_number} placeholder={t('tableNumberPlaceholder') || "Table Number (Eg: 1)"} required className="input-field w-full mb-3" />
                  <input name="table_name" defaultValue={currentTable.table_name} placeholder={t('tableNamePlaceholder') || "Table Name (Eg: Gargen 1)"} className="input-field w-full mb-3" />
                  <select name="group_id" defaultValue={currentTable.group_id || ''} className="input-field w-full mb-3">
                    <option value="">{t('nonACDefault')}</option>
                    {tableGroups.map((g) => (
                      <option key={g.id} value={g.id}>{g.name}</option>
                    ))}
                  </select>
                  <div className="flex items-center gap-2 mb-4">
                    <input 
                      type="checkbox" 
                      id="is_active" 
                      name="is_active" 
                      defaultChecked={currentTable.is_active !== false} 
                      className="w-4 h-4 text-emerald-600 border-gray-300 rounded focus:ring-emerald-500 cursor-pointer" 
                    />
                    <label htmlFor="is_active" className="text-sm font-semibold text-gray-700 cursor-pointer select-none">
                      Active (Visible for scanning)
                    </label>
                  </div>
                  <div className="flex gap-3">
                    <button type="submit" disabled={isTableSubmitting} className={`btn btn-primary flex-1 py-2 ${isTableSubmitting ? 'opacity-70 cursor-not-allowed' : ''}`}>
                      {isTableSubmitting ? t('saving') : t('save')}
                    </button>
                    <button type="button" onClick={closeTableModal} className="btn btn-secondary flex-1 py-2">{t('cancel')}</button>
                  </div>
                </form>
              </div>
            </div>
          )
        }

        {
          isQRModalOpen && selectedTableForQR && (
            <div className="fixed inset-0 modal-overlay flex items-center justify-center p-4" onClick={closeQRModal}>
              <div className="bg-white rounded-lg max-w-md w-full p-6 text-center modal-content" onClick={(e) => e.stopPropagation()}>
                <h3 className="text-xl font-bold mb-4">{t('qrTable')} {selectedTableForQR.table_number}</h3>
                <div className="flex justify-center items-center">
                  <div id={`qr-modal-${selectedTableForQR.id}`} className="qr-container" ref={(el) => (qrRefs.current[selectedTableForQR.id] = el)}></div>
                </div>
                <div className="flex gap-3 mt-6">
                  <button onClick={() => printQR(selectedTableForQR.id)} className="btn bg-emerald-600 hover:bg-emerald-700 text-white flex-1 py-2">{t('print')}</button>
                  <button onClick={closeQRModal} className="btn btn-secondary flex-1 py-2">{t('close')}</button>
                </div>
              </div>
            </div>
          )
        }

        {
          showOrdersModal && (
            <div className="fixed inset-0 modal-overlay flex items-center justify-center p-4" onClick={closeOrdersModal}>
              <div className="bg-white rounded-lg max-w-2xl w-full max-h-[80vh] overflow-y-auto p-6 modal-content" onClick={(e) => e.stopPropagation()}>
                <h3 className="text-xl font-bold mb-4">{t('ordersForTable')} #{selectedTableNum}</h3>
                {selectedTableOrders.length === 0 ? (
                  <p className="text-center text-red-600 font-normal py-8">No Orders Found</p>
                ) : (
                  <div className="space-y-6">
                    {(() => {
                      const isSameDay = (d1, d2) => (
                        d1.getFullYear() === d2.getFullYear() &&
                        d1.getMonth() === d2.getMonth() &&
                        d1.getDate() === d2.getDate()
                      );

                      const today = new Date();

                      // Filter to only orders created today (fall back to updated_at/session_checkout_at)
                      const todaysOrders = selectedTableOrders.filter(o => {
                        const ts = o.created_at || o.updated_at || o.session_checkout_at;
                        const d = ts ? new Date(ts) : new Date(0);
                        return isSameDay(d, today);
                      });

                      const grouped = {};
                      todaysOrders.forEach(order => {
                        const sid = order.session_id || 'no-session';
                        if (!grouped[sid]) grouped[sid] = [];
                        grouped[sid].push(order);
                      });

                      const sessionsArr = Object.entries(grouped).map(([sessionId, ordersInSession]) => {
                        const sessionTimestamp = ordersInSession.reduce((latest, o) => {
                          const t = new Date(o.created_at || 0).getTime();
                          return Math.max(latest, t);
                        }, 0);
                        return { sessionId, ordersInSession, sessionTimestamp };
                      });

                      // Sort sessions so newest (latest activity) is first
                      sessionsArr.sort((a, b) => b.sessionTimestamp - a.sessionTimestamp);

                      return sessionsArr.map(({ sessionId, ordersInSession }) => (
                        <div key={sessionId} className="border border-gray-200 rounded-lg overflow-hidden shadow-sm">
                          <div className="flex justify-between items-center bg-gray-100 p-3 border-b border-gray-200" style={{ background: '#f3f4f6' }}>
                            <div className="font-black text-gray-850 text-xs">
                              Session: {sessionId.startsWith('guest_') ? 'Active Guest Session' : sessionId}
                            </div>
                            <button
                              onClick={() => handlePrintBillForSession(sessionId, ordersInSession)}
                              className="bg-emerald-600 hover:bg-emerald-700 text-white font-bold text-xs px-3.5 py-1.5 rounded-lg shadow-md flex items-center gap-1.5 transition-all transform hover:scale-105 duration-200"
                              title="Print Consolidated Session Bill"
                            >
                              <i className="fas fa-print text-[10px]"></i> Print Session Bill
                            </button>
                          </div>
                          
                          <div className="p-4 space-y-4 bg-gray-50/50">
                            {ordersInSession.map((order) => (
                              <div key={order.id} className="border border-gray-100 rounded-lg p-3 bg-white shadow-xs">
                                <div className="flex justify-between items-start mb-2">
                                  <h4 className="font-bold text-gray-800 text-xs">
                                    {order.customer_name
                                      ? order.customer_name.charAt(0).toUpperCase() + order.customer_name.slice(1)
                                      : `Guest Table ${selectedTableNum || ''}`}
                                    {order.customer_phone && (
                                      <span className="ml-1.5 text-[9px] font-normal text-gray-400 font-mono">📞 {order.customer_phone}</span>
                                    )}
                                  </h4>
                                  <span className="text-[10px] text-gray-400 font-semibold">{new Date(order.created_at).toLocaleString()}</span>
                                </div>
                                <div className="mb-2 text-xs text-gray-700 font-normal">
                                  {order.items.map((item, idx) => (
                                    <div key={idx} className="mb-1">
                                      <div>
                                        {item.quantity}x {t(item.item_name)} -
                                        {item.discount_percentage > 0 ? (
                                          <>
                                            <span className="text-gray-400 line-through text-[9px] ml-1">
                                              {getCurrencySymbol()}{((currency === 'INR' ? item.price_inr : item.price_usd) * item.quantity).toFixed(2)}
                                            </span>
                                            <span className="font-bold ml-1 text-red-600">
                                              {getCurrencySymbol()}{((currency === 'INR' ? item.price_inr : item.price_usd) * (1 - item.discount_percentage / 100) * item.quantity).toFixed(2)}
                                            </span>
                                          </>
                                        ) : (
                                          <span className="ml-1">
                                            {getCurrencySymbol()}{((currency === 'INR' ? item.price_inr : item.price_usd) * item.quantity).toFixed(2)}
                                          </span>
                                        )}
                                      </div>
                                      {language !== 'en' && item.item_name && t(item.item_name) !== item.item_name && (
                                        <div className="text-[9px] text-gray-400 pl-4">{item.item_name}</div>
                                      )}
                                    </div>
                                  ))}
                                </div>
                                <div className="flex justify-between mb-2 text-xs text-gray-600 font-medium">
                                  <span>{t('total')}: ₹{parseFloat(order.total_amount_inr || 0).toFixed(2)}</span>
                                  <span>{t('payment')}: {order.payment_method}</span>
                                </div>
                                {order.is_offline_delivery && (
                                  <div className="mb-2 bg-indigo-50/50 p-2 rounded-lg text-[10px] text-indigo-900 border border-indigo-100">
                                    <p className="font-bold">Delivery Address:</p>
                                    <p>{order.delivery_address}, {order.delivery_city}, {order.delivery_state} - {order.delivery_pincode}</p>
                                    {order.shiprocket_order_id && (
                                      <div className="mt-1 flex justify-between items-center">
                                        <span>Shiprocket ID: <span className="font-bold">{order.shiprocket_order_id}</span></span>
                                        {order.shiprocket_shipment_id && (
                                          <a 
                                            href={`https://shiprocket.co/tracking/shipment/${order.shiprocket_shipment_id}`}
                                            target="_blank" 
                                            rel="noopener noreferrer" 
                                            className="text-indigo-600 font-bold hover:underline"
                                          >
                                            Track Delivery
                                          </a>
                                        )}
                                      </div>
                                    )}
                                  </div>
                                )}
                                <div className="flex items-center gap-2 pt-2 border-t border-gray-50">
                                  <span className="text-xs font-semibold text-gray-600">{t('status')}:</span>
                                  <select value={order.order_status} onChange={(e) => updateOrderStatus(order.id, e.target.value)} className="input-field px-2.5 py-0.5 text-xs bg-gray-50">
                                    <option value="pending">{t('pending')}</option>
                                    <option value="preparing">{t('preparing')}</option>
                                    <option value="ready">{t('ready')}</option>
                                    <option value="delivered">{t('delivered')}</option>
                                  </select>
                                </div>
                              </div>
                            ))}
                          </div>
                        </div>
                      ));
                    })()}
                  </div>
                )}
                <div className="flex gap-3 mt-6">
                  <button onClick={closeOrdersModal} className="btn btn-secondary flex-1 py-2">{t('close')}</button>
                </div>
              </div>
            </div>
          )
        }



        {activeTab === 'users' && (
          <div>
            <div className="bg-white rounded-xl shadow-sm border border-gray-100 overflow-hidden expand-in" style={{ animationDelay: '0.3s' }}>
              <div className="overflow-x-auto">
                <table className="w-full text-left">
                  <thead className="bg-gray-50 border-b border-gray-100">
                    <tr>
                      <th className="p-4 font-semibold text-gray-600">{t('name')}</th>
                      <th className="p-4 font-semibold text-gray-600">{t('email')}</th>
                      <th className="p-4 font-semibold text-gray-600">{t('phone')}</th>
                      <th className="p-4 font-semibold text-gray-600">{t('role')}</th>
                      <th className="p-4 font-semibold text-gray-600">{t('branch') || 'Branch'}</th>
                      <th className="p-4 font-semibold text-gray-600 text-right">{t('actions')}</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-gray-100">
                    {isUsersLoading ? (
                      <tr><td colSpan="6" className="p-8 text-center text-gray-500">{t('loadingUsers')}</td></tr>
                    ) : usersList.filter(u => u.role !== 'customer' && isUserInBranch(u, selectedBranch)).length === 0 ? (
                      <tr><td colSpan="6" className="p-8 text-center text-red-600 font-normal">{t('noUsersFound') || 'No Management Found'}</td></tr>
                    ) : (
                      usersList.filter(u => u.role !== 'customer' && isUserInBranch(u, selectedBranch)).map(user => (
                        <tr key={user.id} className="hover:bg-gray-50 transition">
                          <td className="p-4 font-medium text-gray-800">{user.full_name}</td>
                          <td className="p-4 text-gray-600">{user.email}</td>
                          <td className="p-4 text-gray-600">{user.phone || '-'}</td>
                          <td className="p-4 text-gray-600">
                            <span className="bg-emerald-100 text-emerald-800 text-xs px-2 py-1 rounded-full">
                              {user.display_role || user.role_name || user.role || t('custom')}
                            </span>
                          </td>
                          <td className="p-4 text-gray-600">
                            {(() => {
                              if (branches.length === 0) {
                                return <span className="bg-gray-100 text-gray-700 text-xs px-2 py-0.5 rounded font-medium border border-gray-200">{t('noBranch') || 'No branch'}</span>;
                              }
                              const roleStr = (user.role || '').toLowerCase();
                              if (roleStr === 'admin') {
                                return <span className="bg-gray-100 text-gray-700 text-xs px-2 py-0.5 rounded font-medium border border-gray-200">{t('noBranch') || 'No branch'}</span>;
                              }
                              const userRole = rolesList.find(r => r.id === user.role_id || String(r.id) === String(user.role_id));
                              if (!userRole) {
                                return <span className="bg-gray-100 text-gray-700 text-xs px-2 py-0.5 rounded font-medium border border-gray-200">{t('noBranch') || 'No branch'}</span>;
                              }
                              let allowed = userRole.allowed_branches;
                              if (typeof allowed === 'string') {
                                try { allowed = JSON.parse(allowed); } catch (e) { allowed = []; }
                              }
                              if (!Array.isArray(allowed) || allowed.length === 0) {
                                return <span className="bg-gray-100 text-gray-700 text-xs px-2 py-0.5 rounded font-medium border border-gray-200">{t('noBranch') || 'No branch'}</span>;
                              }
                              const names = allowed
                                .map(id => {
                                  const b = branches.find(branch => String(branch.id) === String(id));
                                  return b ? b.name : null;
                                })
                                .filter(Boolean);
                              return names.length > 0 ? (
                                <div className="flex flex-wrap gap-1">
                                  {names.map(name => (
                                    <span key={name} className="bg-gray-100 text-gray-700 text-xs px-2 py-0.5 rounded font-medium border border-gray-200">
                                      {name}
                                    </span>
                                  ))}
                                </div>
                              ) : (
                                <span className="bg-gray-100 text-gray-700 text-xs px-2 py-0.5 rounded font-medium border border-gray-200">{t('noBranch') || 'No branch'}</span>
                              );
                            })()}
                          </td>
                          <td className="p-4 text-right">
                            <button onClick={() => { setCurrentUser({ ...user, password: '' }); setIsUserModalOpen(true); }} className="text-emerald-600 hover:text-emerald-800 mr-3 transition" title={t('editUser')}>
                              <i className="fas fa-edit"></i>
                            </button>
                            {(() => {
                              const roleStr = (user.role || user.display_role || user.role_name || '').toLowerCase();
                              const isDeletable = roleStr !== 'admin' && Number(user.is_primary) !== 1;
                              return isDeletable ? (
                                <button onClick={() => deleteUser(user.id)} className="text-red-500 hover:text-red-700 transition" title={t('deleteUser')}>
                                  <i className="fas fa-trash-alt"></i>
                                </button>
                              ) : (
                                <button disabled className="text-gray-300 cursor-not-allowed transition" title={t('cannotDeleteAdmin') || "Cannot delete Admin user"}>
                                  <i className="fas fa-trash-alt"></i>
                                </button>
                              );
                            })()}
                          </td>
                        </tr>
                      ))
                    )}
                  </tbody>
                </table>
              </div>
            </div>
          </div>
        )
        }

        {activeTab === 'roles' && (
          <div>
            <div className="bg-white rounded-xl shadow-sm border border-gray-100 overflow-hidden expand-in" style={{ animationDelay: '0.3s' }}>
              <div className="overflow-x-auto">
                <table className="w-full text-left">
                  <thead className="bg-gray-50 border-b border-gray-100">
                    <tr>
                      <th className="p-4 font-semibold text-gray-600">{t('roleName')}</th>
                      <th className="p-4 font-semibold text-gray-600">{t('branch') || 'Branch'}</th>
                      <th className="p-4 font-semibold text-gray-600">{t('permissions')}</th>
                      <th className="p-4 font-semibold text-gray-600 text-right">{t('actions')}</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-gray-100">
                    {isRolesLoading ? (
                      <tr><td colSpan="4" className="p-8 text-center text-gray-500">{t('loadingRoles')}</td></tr>
                    ) : rolesList.filter(r => isRoleInBranch(r, selectedBranch)).length === 0 ? (
                      <tr><td colSpan="4" className="p-8 text-center text-red-600 font-normal">No Roles Found</td></tr>
                    ) : (
                      rolesList.filter(r => isRoleInBranch(r, selectedBranch)).map(role => (
                        <tr key={role.id} className="hover:bg-gray-50 transition">
                          <td className="p-4 font-medium text-gray-800">{role.name}</td>
                          <td className="p-4 text-gray-600">
                            {(() => {
                              if (branches.length === 0) {
                                return <span className="bg-gray-100 text-gray-700 text-xs px-2 py-0.5 rounded font-medium border border-gray-200">{t('noBranch') || 'No branch'}</span>;
                              }
                              let allowed = role.allowed_branches;
                              if (typeof allowed === 'string') {
                                try { allowed = JSON.parse(allowed); } catch (e) { allowed = []; }
                              }
                              if (!Array.isArray(allowed) || allowed.length === 0) {
                                return <span className="bg-gray-100 text-gray-700 text-xs px-2 py-0.5 rounded font-medium border border-gray-200">{t('noBranch') || 'No branch'}</span>;
                              }
                              const names = allowed
                                .map(id => {
                                  const b = branches.find(branch => String(branch.id) === String(id));
                                  return b ? b.name : null;
                                })
                                .filter(Boolean);
                              return names.length > 0 ? (
                                <div className="flex flex-wrap gap-1">
                                  {names.map(name => (
                                    <span key={name} className="bg-gray-100 text-gray-700 text-xs px-2 py-0.5 rounded font-medium border border-gray-200">
                                      {name}
                                    </span>
                                  ))}
                                </div>
                              ) : (
                                <span className="bg-gray-100 text-gray-700 text-xs px-2 py-0.5 rounded font-medium border border-gray-200">{t('noBranch') || 'No branch'}</span>
                              );
                            })()}
                          </td>
                          <td className="p-4 text-gray-600">
                            <div className="flex flex-wrap gap-2">
                              {AVAILABLE_PERMISSIONS.filter(p => role.permissions && role.permissions[p.id]).map(p => (
                                <span key={p.id} className="bg-emerald-50 text-emerald-700 text-xs px-2 py-1 rounded-full">
                                  {p.label}
                                </span>
                              ))}
                            </div>
                          </td>
                          <td className="p-4 text-right">
                            <button onClick={() => {
                              let allowed = role.allowed_branches;
                              if (typeof allowed === 'string') {
                                try { allowed = JSON.parse(allowed); } catch (e) { allowed = []; }
                              }
                              setCurrentRole({ ...role, allowed_branches: allowed || [] });
                              setIsRoleModalOpen(true);
                            }} className="text-emerald-600 hover:text-emerald-800 mr-3 transition" title={t('editRole')}>
                              <i className="fas fa-edit"></i>
                            </button>
                            <button onClick={() => deleteRole(role.id)} className="text-red-500 hover:text-red-700 transition" title={t('deleteRole')}>
                              <i className="fas fa-trash-alt"></i>
                            </button>
                          </td>
                        </tr>
                      ))
                    )}
                  </tbody>
                </table>
              </div>
            </div>
          </div>
        )
        }

        {activeTab === 'branches' && (
          <div className="expand-in" style={{ animationDelay: '0.3s' }}>
            <BranchesTab token={token} API_URL={API_URL} companyProfile={companyProfile} showToast={showToast} />
          </div>
        )
        }

        {activeTab === 'activity_log' && (
          <div className="expand-in" style={{ animationDelay: '0.3s' }}>
            <ActivityLogTab token={token} API_URL={API_URL} companyProfile={companyProfile} showToast={showToast} />
          </div>
        )
        }

        {activeTab === 'redeem' && companyProfile && companyProfile.id === 38 && (
          <div className="expand-in" style={{ animationDelay: '0.3s' }}>
            <RedeemSettingsTab token={token} API_URL={API_URL} companyProfile={companyProfile} showToast={showToast} />
          </div>
        )
        }
      </div >

      {
        isStaffQRModalOpen && (
          <div className="fixed inset-0 modal-overlay flex items-center justify-center p-4" onClick={() => setIsStaffQRModalOpen(false)}>
            <div className="bg-white rounded-2xl max-w-sm w-full p-8 text-center modal-content shadow-2xl" onClick={(e) => e.stopPropagation()}>
              <div className="w-16 h-16 bg-emerald-50 text-emerald-600 rounded-full flex items-center justify-center mx-auto mb-4">
                <i className="fas fa-qrcode text-2xl"></i>
              </div>
              <h3 className="text-2xl font-black text-gray-900 mb-2">{t('staffPortal') || 'Staff Portal'}</h3>
              <p className="text-gray-500 text-sm mb-6">{t('scanToAccessStaff')}</p>

              <div className="bg-white p-4 inline-block rounded-2xl shadow-sm mb-8 border border-gray-100">
                <img src={`https://api.qrserver.com/v1/create-qr-code/?size=250x250&data=${encodeURIComponent(`${BASE_URL}/staff`)}`} alt="Staff Portal QR" className="w-48 h-48" />
              </div>

              <div className="grid grid-cols-2 gap-3">
                <button
                  onClick={printStaffPortalQR}
                  className="flex items-center justify-center gap-2 bg-emerald-600 hover:bg-emerald-700 text-white py-3 rounded-xl font-bold transition-all shadow-lg shadow-emerald-100"
                >
                  <i className="fas fa-print"></i>
                  {t('print') || 'Print'}
                </button>
                <button
                  onClick={() => setIsStaffQRModalOpen(false)}
                  className="bg-gray-100 hover:bg-gray-200 text-gray-700 py-3 rounded-xl font-bold transition-all"
                >
                  {t('close')}
                </button>
              </div>
            </div>
          </div>
        )
      }



      {
        isRoleModalOpen && (
          <div className="fixed inset-0 modal-overlay flex items-center justify-center p-4">
            <div className="bg-white rounded-2xl shadow-2xl w-full max-w-lg max-h-[90vh] overflow-y-auto modal-content">
              <div className="p-6 border-b border-gray-100 flex justify-between items-center bg-gray-50">
                <h3 className="text-xl font-bold text-gray-800">{currentRole.id ? t('editRole') : t('createRole')}</h3>
                <button onClick={() => setIsRoleModalOpen(false)} className="text-gray-400 hover:text-gray-600 transition"><i className="fas fa-times text-xl"></i></button>
              </div>
              <form onSubmit={saveRole} className="p-6">
                <div className="mb-6"><label className="block text-sm font-medium text-gray-700 mb-1">{t('roleName')} <span className="text-red-500">*</span></label><input type="text" required className="input-field" value={currentRole.name} onChange={e => setCurrentRole({ ...currentRole, name: e.target.value })} placeholder={t('enterRoleName') || "Enter role name"} /></div>
                <div className="mb-6">
                  <div className="flex justify-between items-center mb-2">
                    <label className="block text-sm font-medium text-gray-700">{t('permissions')}</label>
                    <label className="flex items-center space-x-2 text-xs font-semibold text-emerald-600 hover:text-emerald-800 cursor-pointer select-none">
                      <input
                        type="checkbox"
                        className="w-4 h-4 text-emerald-600 rounded focus:ring-emerald-500 border-gray-300 cursor-pointer"
                        checked={AVAILABLE_PERMISSIONS.every(perm => currentRole.permissions?.[perm.id])}
                        onChange={e => {
                          const allChecked = e.target.checked;
                          const newPerms = {};
                          AVAILABLE_PERMISSIONS.forEach(perm => {
                            newPerms[perm.id] = allChecked;
                          });
                          setCurrentRole({ ...currentRole, permissions: newPerms });
                        }}
                      />
                      <span>{t('selectAll') || 'Select All'}</span>
                    </label>
                  </div>
                  <div className="grid grid-cols-2 gap-3">
                    {AVAILABLE_PERMISSIONS.map(perm => (
                      <label key={perm.id} className="flex items-center space-x-3 p-3 border rounded-lg hover:bg-gray-50 cursor-pointer transition">
                        <input
                          type="checkbox"
                          className="w-5 h-5 text-emerald-600 rounded focus:ring-emerald-500 border-gray-300"
                          checked={currentRole.permissions?.[perm.id] || false}
                          onChange={e => {
                            const newPerms = { ...currentRole.permissions, [perm.id]: e.target.checked };
                            setCurrentRole({ ...currentRole, permissions: newPerms });
                          }}
                        />
                        <span className="text-gray-700 font-medium">{perm.label}</span>
                      </label>
                    ))}
                  </div>
                </div>
                <div className="mb-6">
                  <label className="block text-sm font-medium text-gray-700 mb-1">{t('allowedBranches') || 'Allowed Branches'} <span className="text-xs text-gray-500 font-normal">({t('leaveEmptyForNoBranch') || 'Leave empty for no branch'})</span></label>
                  <div className="grid grid-cols-2 gap-3 max-h-40 overflow-y-auto p-1">
                    {branches.length === 0 ? (
                      <div className="col-span-2 text-center py-4 text-gray-400 font-bold border border-dashed border-gray-200 rounded-lg">
                        {t('noBranch') || 'No branch'}
                      </div>
                    ) : (
                      branches.map(branch => (
                        <label key={branch.id} className="flex items-center space-x-3 p-3 border rounded-lg hover:bg-gray-50 cursor-pointer transition">
                          <input
                            type="checkbox"
                            className="w-5 h-5 text-emerald-600 rounded focus:ring-emerald-500 border-gray-300"
                            checked={(() => {
                              let allowed = currentRole.allowed_branches || [];
                              if (typeof allowed === 'string') {
                                try { allowed = JSON.parse(allowed); } catch (e) { allowed = []; }
                              }
                              return Array.isArray(allowed) && allowed.map(String).includes(String(branch.id));
                            })()}
                            onChange={e => {
                              let current = currentRole.allowed_branches || [];
                              if (typeof current === 'string') {
                                try { current = JSON.parse(current); } catch (e) { current = []; }
                              }
                              if (!Array.isArray(current)) current = [];
                              let newBranches;
                              if (e.target.checked) {
                                newBranches = [...current.map(String), String(branch.id)].map(Number).filter(n => !isNaN(n));
                              } else {
                                newBranches = current.map(String).filter(id => id !== String(branch.id)).map(Number).filter(n => !isNaN(n));
                              }
                              setCurrentRole({ ...currentRole, allowed_branches: newBranches });
                            }}
                          />
                          <span className="text-gray-700 font-medium">{branch.name}</span>
                        </label>
                      ))
                    )}
                  </div>
                </div>
                <div className="flex justify-end gap-3 pt-4 border-t border-gray-100 mt-4"><button type="button" onClick={() => setIsRoleModalOpen(false)} className="px-6 py-2 rounded-lg border border-gray-300 text-gray-700 hover:bg-gray-50 font-medium transition btn-secondary">{t('cancel')}</button><button type="submit" className="px-6 py-2 rounded-lg bg-emerald-600 text-white hover:bg-emerald-700 font-bold shadow-lg transition transform hover:-translate-y-0.5 btn-primary">{currentRole.id ? t('updateRole') : t('createRole')}</button></div>
              </form>
            </div>
          </div>
        )
      }

      {
        isUserModalOpen && (
          <div className="fixed inset-0 modal-overlay flex items-center justify-center p-4">
            <div className="bg-white rounded-2xl shadow-2xl w-full max-w-lg overflow-hidden modal-content">
              <div className="p-6 border-b border-gray-100 flex justify-between items-center bg-gray-50"><h3 className="text-xl font-bold text-gray-800">{currentUser.id ? t('editUser') : t('addUser')}</h3><button onClick={() => setIsUserModalOpen(false)} className="text-gray-400 hover:text-gray-600 transition"><i className="fas fa-times text-xl"></i></button></div>
              <form onSubmit={saveUser} className="p-6">
                <div className="space-y-4">
                  <div><label className="block text-sm font-medium text-gray-700 mb-1">{t('fullName')} <span className="text-red-500">*</span></label><input type="text" required className="input-field" value={currentUser.full_name || ''} onChange={e => setCurrentUser({ ...currentUser, full_name: e.target.value })} placeholder={t('enterFullName') || "Enter Full Name"} /></div>
                  <div><label className="block text-sm font-medium text-gray-700 mb-1">{t('email')} <span className="text-red-500">*</span></label><input type="email" required className="input-field" value={currentUser.email || ''} onChange={e => setCurrentUser({ ...currentUser, email: e.target.value })} placeholder={t('enterEmail') || "Enter Email"} /></div>
                  <div><label className="block text-sm font-medium text-gray-700 mb-1">{t('phoneNumber')}</label><input type="tel" pattern="[0-9]{10}" title="Phone number must be exactly 10 digits" className="input-field" value={currentUser.phone || ''} onChange={e => setCurrentUser({ ...currentUser, phone: e.target.value })} placeholder={t('enterPhoneNumber') || "Enter Phone Number"} /></div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">{t('role')} <span className="text-red-500">*</span></label>
                    <select
                      required
                      className="input-field"
                      value={currentUser.role_id || ''}
                      onChange={e => {
                        if (e.target.value === 'ADD_NEW_ROLE') {
                          setIsUserModalOpen(false);
                          setCurrentRole({ name: '', permissions: {}, allowed_branches: branches.length === 1 ? [branches[0].id] : [] });
                          setIsRoleModalOpen(true);
                        } else {
                          setCurrentUser({ ...currentUser, role_id: e.target.value ? parseInt(e.target.value) : null });
                        }
                      }}
                    >
                      <option value="">{t('selectRole')}</option>
                      {rolesList.map(role => (
                        <option key={role.id} value={role.id}>{role.name}</option>
                      ))}
                      <option value="ADD_NEW_ROLE" className="font-bold text-emerald-600 bg-emerald-50">+ {t('addRoleType') || 'Role Type Add'}</option>
                    </select>
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      {currentUser.id ? t('newPassword') : <>{t('password')} <span className="text-red-500">*</span></>}
                    </label>
                    <div className="relative">
                      <input
                        type={visiblePins[`user-password`] ? "text" : "password"}
                        required={!currentUser.id}
                        className="input-field pr-10"
                        value={currentUser.password || ''}
                        onChange={e => setCurrentUser({ ...currentUser, password: e.target.value })}
                        placeholder={currentUser.id ? (t('leaveBlankToKeepCurrent') || 'Leave Blank to Keep Current') : (t('passwordPlaceholder') || "Enter Password")}
                      />
                      <button
                        type="button"
                        onClick={() => setVisiblePins(prev => ({ ...prev, [`user-password`]: !prev[`user-password`] }))}
                        className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600 transition-colors"
                      >
                        <i className={`fas ${visiblePins[`user-password`] ? 'fa-eye-slash' : 'fa-eye'}`}></i>
                      </button>
                    </div>
                  </div>
                </div>
                <div className="flex justify-end gap-3 pt-6 mt-6 border-t border-gray-100"><button type="button" onClick={() => setIsUserModalOpen(false)} className="px-6 py-2 rounded-lg border border-gray-300 text-gray-700 hover:bg-gray-50 font-medium transition btn-secondary">{t('cancel')}</button><button type="submit" className="px-6 py-2 rounded-lg bg-emerald-600 text-white hover:bg-emerald-700 font-bold shadow-lg transition transform hover:-translate-y-0.5 btn-primary">{t('saveUser')}</button></div>
              </form>
            </div>
          </div>
        )
      }


      {
        confirmModal.show && (
          <div className="fixed inset-0 modal-overlay flex items-center justify-center p-4 z-50">
            <div className="bg-white rounded-2xl shadow-xl max-w-sm w-full p-6 modal-content text-center">
              {confirmModal.title && (
                <div className="w-16 h-16 bg-indigo-50 rounded-full flex items-center justify-center mx-auto mb-4">
                  <i className="fas fa-crown text-2xl text-indigo-500"></i>
                </div>
              )}
              <h3 className="text-xl font-bold mb-2 text-gray-900">{confirmModal.title || t('confirmAction')}</h3>
              <p className="mb-6 text-gray-500 text-sm leading-relaxed">{confirmModal.message}</p>
              <div className="flex gap-3">
                <button onClick={() => setConfirmModal({ show: false })} className={confirmModal.cancelClass || "btn btn-secondary flex-1 py-2"}>{t('no')}</button>
                <button onClick={confirmModal.onConfirm} className={confirmModal.confirmClass || "btn btn-danger flex-1 py-2"}>{confirmModal.confirmText || t('yes')}</button>
              </div>
            </div>
          </div>
        )
      }

      {
        toast.show && (
          <div className={`toast ${toast.type} show`}>
            {toast.type === 'success' && <i className="fas fa-check-circle text-green-500 mr-3"></i>}
            {toast.type === 'error' && <i className="fas fa-exclamation-circle text-red-500 mr-3"></i>}
            {toast.message}
          </div>
        )
      }



      <SubscriptionModal isOpen={isSubscriptionOpen} onClose={() => setIsSubscriptionOpen(false)} />
      <PlanRenewalModal
        isOpen={isRenewalModalOpen}
        onClose={() => {
          setIsRenewalModalOpen(false);
          setRenewalModalOptions({});
        }}
        companyProfile={companyProfile}
        token={token}
        onSuccess={() => {
          loadCompanyProfile();
        }}
        {...renewalModalOptions}
      />
      <BuyBranchesModal
        isOpen={isBuyBranchesOpen}
        onClose={() => setIsBuyBranchesOpen(false)}
        companyProfile={companyProfile}
        token={token}
        onSuccess={() => {
          loadCompanyProfile();
          window.dispatchEvent(new CustomEvent('refreshBranches'));
        }}
      />
      {
        isManageCategoriesModalOpen && (
          <div className="fixed inset-0 modal-overlay flex items-center justify-center p-4" onClick={() => setIsManageCategoriesModalOpen(false)}>
            <div className="bg-white rounded-lg max-w-md w-full p-6 modal-content" onClick={(e) => e.stopPropagation()}>
              <div className="flex justify-between items-center mb-4">
                <h3 className="text-xl font-bold">{t('manageCategories')}</h3>
                <button onClick={() => setIsManageCategoriesModalOpen(false)} className="text-gray-500 hover:text-gray-700"><i className="fas fa-times"></i></button>
              </div>

              {/* Add New Category Input in Modal */}
              <div className="flex gap-2 mb-4">
                <input
                  type="text"
                  placeholder={t('categoryNamePlaceholder') || "New Category Name"}
                  value={newCategoryName}
                  onChange={(e) => setNewCategoryName(e.target.value)}
                  className="input-field flex-1"
                  onKeyDown={(e) => {
                    if (e.key === 'Enter') {
                      addNewCategory();
                    }
                  }}
                />
                <button onClick={addNewCategory} className="btn bg-emerald-600 hover:bg-emerald-700 text-white px-4 rounded">
                  <i className="fas fa-plus"></i>
                </button>
              </div>

              <div className="flex justify-between items-center bg-emerald-50 p-4 rounded-xl mb-4 border border-emerald-100 shadow-sm">
                <div>
                  <h4 className="font-bold text-emerald-800">{t('globalSelfService')}</h4>
                  <p className="text-xs text-emerald-600">{t('globalSelfServiceDesc')}</p>
                </div>
                <label className="flex items-center cursor-pointer select-none">
                  <span className="mr-3 text-sm font-semibold text-emerald-700">
                    {categories.length > 0 && categories.every(c => c.is_self_service) ? 'All Enabled' : 'Enable All'}
                  </span>
                  <input
                    type="checkbox"
                    checked={categories.length > 0 && categories.every(c => c.is_self_service)}
                    onChange={(e) => toggleAllCategoriesSelfService(e.target.checked)}
                    className="w-6 h-6 text-emerald-600 rounded focus:ring-emerald-500 border-gray-300"
                  />
                </label>
              </div>

              <div className="max-h-[60vh] overflow-y-auto space-y-2">
                {categories.map(cat => (
                  <div key={cat.id || cat.name} className="flex justify-between items-center p-3 bg-gray-50 rounded border hover:bg-gray-100 transition-colors">
                    {editingCategory === cat.id ? (
                      <div className="flex items-center flex-1 gap-2">
                        <input
                          type="text"
                          value={editingCategoryName}
                          onChange={(e) => setEditingCategoryName(e.target.value)}
                          className="flex-1 input-field py-1 px-2 text-sm"
                          autoFocus
                          onKeyDown={(e) => {
                            if (e.key === 'Enter') {
                              // Save logic duplicated for enter key
                              if (editingCategoryName && editingCategoryName.trim() !== '' && editingCategoryName !== cat.name) {
                                (async () => {
                                  try {
                                    const res = await fetch(`${API_URL}/api/categories/${cat.id}`, {
                                      method: 'PUT',
                                      headers: { 'Content-Type': 'application/json', 'Authorization': `Bearer ${token}` },
                                      body: JSON.stringify({ name: editingCategoryName })
                                    });
                                    if ((await res.json()).success) {
                                      setCategories(prev => prev.map(c => c.id === cat.id ? { ...c, name: editingCategoryName } : c));
                                      showToast('Renamed successfully');
                                      setEditingCategory(null);
                                    }
                                  } catch (e) { showToast('Rename failed', 'error'); }
                                })();
                              } else {
                                setEditingCategory(null);
                              }
                            } else if (e.key === 'Escape') {
                              setEditingCategory(null);
                            }
                          }}
                        />
                        <button
                          onClick={() => {
                            if (editingCategoryName && editingCategoryName.trim() !== '' && editingCategoryName !== cat.name) {
                              (async () => {
                                try {
                                  const res = await fetch(`${API_URL}/api/categories/${cat.id}`, {
                                    method: 'PUT',
                                    headers: { 'Content-Type': 'application/json', 'Authorization': `Bearer ${token}` },
                                    body: JSON.stringify({ name: editingCategoryName })
                                  });
                                  if ((await res.json()).success) {
                                    setCategories(prev => prev.map(c => c.id === cat.id ? { ...c, name: editingCategoryName } : c));
                                    showToast('Renamed successfully');
                                    setEditingCategory(null);
                                  }
                                } catch (e) { showToast('Rename failed', 'error'); }
                              })();
                            } else {
                              setEditingCategory(null);
                            }
                          }}
                          className="text-green-600 hover:text-green-800 p-1" title="Save"
                        >
                          <i className="fas fa-check"></i>
                        </button>
                        <button
                          onClick={() => setEditingCategory(null)}
                          className="text-gray-500 hover:text-gray-700 p-1" title="Cancel"
                        >
                          <i className="fas fa-times"></i>
                        </button>
                      </div>
                    ) : (
                      <>
                        <span className="font-medium text-gray-800">{getLocalizedText(cat, 'name', language)}</span>
                        <div className="flex items-center gap-3">
                          <label className="flex items-center cursor-pointer select-none">
                            <input
                              type="checkbox"
                              checked={!!cat.is_self_service}
                              onChange={() => toggleCategorySelfService(cat)}
                              className="mr-2 h-4 w-4 text-emerald-600 focus:ring-emerald-500 border-gray-300 rounded"
                            />
                            <span className={`text-sm font-medium ${cat.is_self_service ? 'text-emerald-600' : 'text-gray-500'}`}>
                              {cat.is_self_service ? 'Self Serve' : 'Served'}
                            </span>
                          </label>
                          <div className="h-4 w-px bg-gray-300 mx-1"></div>
                          <button
                            onClick={() => {
                              setEditingCategory(cat.id);
                              setEditingCategoryName(cat.name);
                            }}
                            className="text-emerald-600 hover:text-emerald-800 p-1" title="Edit Name"
                          >
                            <i className="fas fa-edit"></i>
                          </button>
                          <button
                            onClick={() => {
                              if (window.confirm(`Delete category "${cat.name}"? Items in it will lose their category.`)) {
                                (async () => {
                                  try {
                                    const res = await fetch(`${API_URL}/api/categories/${cat.id}`, {
                                      method: 'DELETE',
                                      headers: { 'Authorization': `Bearer ${token}` }
                                    });
                                    if ((await res.json()).success) {
                                      setCategories(prev => prev.filter(c => c.id !== cat.id));
                                      showToast('Deleted successfully');
                                    }
                                  } catch (e) { showToast('Delete failed', 'error'); }
                                })();
                              }
                            }}
                            className="text-red-500 hover:text-red-700 p-1" title="Delete Category"
                          >
                            <i className="fas fa-trash-alt"></i>
                          </button>
                        </div>
                      </>
                    )}
                  </div>
                ))}
                {categories.length === 0 && <p className="text-center text-red-600 font-normal">No categories found.</p>}
              </div>
              <div className="mt-4 pt-4 border-t text-sm text-gray-500">
                <p>Toggle "Self Serve" to notify customers to pick up orders from this category.</p>
              </div>
            </div>
          </div>
        )}
      {showUnlockModal && (
        <div className="fixed inset-0 modal-overlay flex items-center justify-center p-4">
          <div className="bg-white rounded-2xl max-w-sm w-full p-8 modal-content shadow-2xl">
            <div className="text-center mb-6">
              <div className="w-20 h-20 bg-emerald-50 rounded-full flex items-center justify-center mx-auto mb-4">
                <i className="fas fa-unlock-alt text-3xl text-emerald-600"></i>
              </div>
              <h3 className="text-2xl font-black text-gray-800">{t('unlockSettings') || 'Unlock Settings'}</h3>
              <p className="text-gray-500 mt-2">{t('enterPasswordToProceed') || 'Please enter your admin password to continue'}</p>
            </div>

            <form onSubmit={async (e) => {
              e.preventDefault();
              setIsUnlocking(true);
              try {
                const res = await fetch(`${API_URL}/api/auth/verify-password`, {
                  method: 'POST',
                  headers: { 'Content-Type': 'application/json', 'Authorization': `Bearer ${token}` },
                  body: JSON.stringify({ password: unlockPassword })
                });
                const json = await res.json();
                if (json.success) {
                  setIsPaymentSettingsUnlocked(true);
                  setShowUnlockModal(false);
                  setUnlockPassword('');
                  showToast('Settings unlocked!', 'success');
                } else {
                  showToast(json.message || 'Invalid password', 'error');
                }
              } catch (err) {
                showToast('Verification failed', 'error');
              } finally {
                setIsUnlocking(false);
              }
            }}>
              <div className="mb-6">
                <input
                  type="password"
                  placeholder={t('password')}
                  value={unlockPassword}
                  onChange={(e) => setUnlockPassword(e.target.value)}
                  required
                  className="w-full px-4 py-3 rounded-xl border-2 border-gray-100 focus:border-emerald-500 focus:ring-4 focus:ring-emerald-500/10 outline-none transition-all"
                  autoFocus
                />
                <button
                  type="button"
                  onClick={async () => {
                    if (!authUser?.email) {
                      showToast('Admin email not found', 'error');
                      return;
                    }
                    showToast('Sending reset link...', 'info');
                    try {
                      const res = await fetch(`${API_URL}/api/auth/forgot-password`, {
                        method: 'POST',
                        headers: { 'Content-Type': 'application/json' },
                        body: JSON.stringify({ email: authUser.email })
                      });
                      const json = await res.json();
                      showToast(json.message, json.success ? 'success' : 'error');
                    } catch (err) {
                      showToast('Failed to send reset email', 'error');
                    }
                  }}
                  className="text-emerald-600 text-xs font-bold mt-2 hover:underline"
                >
                  {t('forgotPassword')}?
                </button>
              </div>

              <div className="flex gap-3">
                <button
                  type="button"
                  onClick={() => {
                    setShowUnlockModal(false);
                    setUnlockPassword('');
                  }}
                  className="flex-1 px-6 py-3 rounded-xl bg-gray-100 text-gray-600 font-bold hover:bg-gray-200 transition"
                >
                  {t('cancel')}
                </button>
                <button
                  type="submit"
                  disabled={isUnlocking}
                  className="flex-1 px-6 py-3 rounded-xl bg-emerald-600 text-white font-bold hover:bg-emerald-700 transition shadow-lg shadow-emerald-200 disabled:opacity-50"
                >
                  {isUnlocking ? '...' : t('unlock')}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
}

export default AdminPage;
